import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ChevronRight, ChevronLeft, Check, Lock, Play, 
  TrendingUp, Users, Target, Settings, DollarSign,
  Smartphone, CheckCircle, AlertTriangle, Zap, BookOpen,
  ArrowRight, Calculator, Lightbulb, Star, Sparkles, Menu, X
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ============ FINANCIAL OPTIMIZER (IA) ============
const FinancialOptimizerGenerator = ({ userInputs }) => {
  const [formData, setFormData] = useState({
    monthly_ca: userInputs?.monthlyCA || '',
    food_cost_pct: userInputs?.foodCost || '',
    staff_cost_pct: userInputs?.staffCost || '',
    fixed_charges: '',
    avg_ticket: '',
    monthly_covers: '',
    restaurant_type: userInputs?.restaurantType || 'bistrot',
    main_pain_point: '',
  });
  const [loading, setLoading] = useState(false);
  const [plan, setPlan] = useState(null);
  const [error, setError] = useState('');

  const handleGenerate = async () => {
    if (!formData.monthly_ca) {
      setError('Renseigne au minimum ton CA mensuel.');
      return;
    }
    setError('');
    setLoading(true);
    setPlan(null);
    try {
      const res = await fetch(`${API_URL}/api/ai/generate-financial-plan`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          monthly_ca: parseFloat(formData.monthly_ca) || 0,
          food_cost_pct: parseFloat(formData.food_cost_pct) || 0,
          staff_cost_pct: parseFloat(formData.staff_cost_pct) || 0,
          fixed_charges: parseFloat(formData.fixed_charges) || 0,
          avg_ticket: parseFloat(formData.avg_ticket) || 0,
          monthly_covers: parseInt(formData.monthly_covers) || 0,
          restaurant_type: formData.restaurant_type,
          main_pain_point: formData.main_pain_point,
        }),
      });
      if (!res.ok) {
        const errBody = await res.json().catch(() => ({}));
        throw new Error(errBody.detail || `HTTP ${res.status}`);
      }
      const data = await res.json();
      setPlan(data.plan);
      // scroll to result
      setTimeout(() => {
        const el = document.querySelector('[data-testid="fo-result"]');
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    } catch (err) {
      setError(`Génération échouée : ${err.message}`);
    }
    setLoading(false);
  };

  const statusColor = (status) => ({
    rouge: 'bg-red-50 border-red-200 text-red-700',
    orange: 'bg-orange-50 border-orange-200 text-orange-700',
    vert: 'bg-green-50 border-green-200 text-green-700',
  }[status] || 'bg-gray-50 border-gray-200 text-gray-700');

  return (
    <div className="space-y-4" data-testid="financial-optimizer">
      {/* Header */}
      <div className="bg-gradient-to-br from-emerald-600 to-teal-600 rounded-2xl p-5 text-white">
        <div className="flex items-start gap-3 mb-2">
          <Calculator className="w-6 h-6 flex-shrink-0" />
          <div className="flex-1">
            <h3 className="text-xl font-black leading-tight">Optimiseur Financier IA</h3>
            <p className="text-white/85 text-sm mt-1">Diagnostic + plan d'action chiffré en 15 secondes</p>
          </div>
        </div>
        <p className="text-white/90 text-xs leading-relaxed">
          Renseigne tes chiffres clés. L'IA te génère un diagnostic complet + 6-8 actions chiffrées avec gain mensuel estimé.
        </p>
      </div>

      {/* Form */}
      <div className="bg-white rounded-2xl border border-[#e0dbd4] p-5 space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <label className="block col-span-2">
            <span className="text-xs font-bold text-[#1a1a1a] block mb-1.5">CA mensuel (€) *</span>
            <input
              type="number"
              value={formData.monthly_ca}
              onChange={(e) => { setFormData({...formData, monthly_ca: e.target.value}); setError(''); }}
              placeholder="35000"
              className="w-full px-3 py-2.5 bg-[#f5f3f0] border border-[#e0dbd4] rounded-xl text-sm font-bold outline-none focus:border-emerald-600"
              data-testid="fo-ca"
            />
          </label>
          <label className="block">
            <span className="text-xs font-bold text-[#1a1a1a] block mb-1.5">Food cost (%)</span>
            <input type="number" value={formData.food_cost_pct} onChange={(e) => setFormData({...formData, food_cost_pct: e.target.value})} placeholder="30" className="w-full px-3 py-2.5 bg-[#f5f3f0] border border-[#e0dbd4] rounded-xl text-sm outline-none focus:border-emerald-600" data-testid="fo-fc" />
          </label>
          <label className="block">
            <span className="text-xs font-bold text-[#1a1a1a] block mb-1.5">Masse salariale (%)</span>
            <input type="number" value={formData.staff_cost_pct} onChange={(e) => setFormData({...formData, staff_cost_pct: e.target.value})} placeholder="35" className="w-full px-3 py-2.5 bg-[#f5f3f0] border border-[#e0dbd4] rounded-xl text-sm outline-none focus:border-emerald-600" data-testid="fo-ms" />
          </label>
          <label className="block">
            <span className="text-xs font-bold text-[#1a1a1a] block mb-1.5">Charges fixes/mois (€)</span>
            <input type="number" value={formData.fixed_charges} onChange={(e) => setFormData({...formData, fixed_charges: e.target.value})} placeholder="12000" className="w-full px-3 py-2.5 bg-[#f5f3f0] border border-[#e0dbd4] rounded-xl text-sm outline-none focus:border-emerald-600" data-testid="fo-fixed" />
          </label>
          <label className="block">
            <span className="text-xs font-bold text-[#1a1a1a] block mb-1.5">Ticket moyen (€)</span>
            <input type="number" step="0.5" value={formData.avg_ticket} onChange={(e) => setFormData({...formData, avg_ticket: e.target.value})} placeholder="18" className="w-full px-3 py-2.5 bg-[#f5f3f0] border border-[#e0dbd4] rounded-xl text-sm outline-none focus:border-emerald-600" data-testid="fo-avg" />
          </label>
          <label className="block">
            <span className="text-xs font-bold text-[#1a1a1a] block mb-1.5">Couverts/mois</span>
            <input type="number" value={formData.monthly_covers} onChange={(e) => setFormData({...formData, monthly_covers: e.target.value})} placeholder="1944" className="w-full px-3 py-2.5 bg-[#f5f3f0] border border-[#e0dbd4] rounded-xl text-sm outline-none focus:border-emerald-600" data-testid="fo-covers" />
          </label>
          <label className="block">
            <span className="text-xs font-bold text-[#1a1a1a] block mb-1.5">Type de resto</span>
            <select value={formData.restaurant_type} onChange={(e) => setFormData({...formData, restaurant_type: e.target.value})} className="w-full px-3 py-2.5 bg-[#f5f3f0] border border-[#e0dbd4] rounded-xl text-sm font-bold outline-none focus:border-emerald-600" data-testid="fo-type">
              <option value="brunch">Brunch</option>
              <option value="bistrot">Bistrot</option>
              <option value="gastro">Gastro</option>
              <option value="fast-casual">Fast Casual</option>
              <option value="coffee">Coffee</option>
              <option value="italien">Italien</option>
              <option value="burger">Burger</option>
              <option value="healthy">Healthy</option>
              <option value="asiatique">Asiatique</option>
              <option value="pizzeria">Pizzeria</option>
              <option value="tapas">Tapas</option>
              <option value="libanais">Libanais</option>
            </select>
          </label>
        </div>

        <label className="block">
          <span className="text-xs font-bold text-[#1a1a1a] block mb-1.5">📍 Ton point de douleur principal</span>
          <textarea
            value={formData.main_pain_point}
            onChange={(e) => setFormData({...formData, main_pain_point: e.target.value})}
            rows={2}
            placeholder='Ex : "Mes marges ne montent pas malgré un CA correct"'
            className="w-full px-3 py-2.5 bg-[#f5f3f0] border border-[#e0dbd4] rounded-xl text-sm outline-none focus:border-emerald-600"
            data-testid="fo-pain"
          />
        </label>

        {error && <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-sm text-red-700 font-bold" data-testid="fo-error">{error}</div>}

        <button
          onClick={handleGenerate}
          disabled={loading}
          className="w-full py-4 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 disabled:opacity-60 text-white font-black text-base rounded-xl flex items-center justify-center gap-2 transition shadow-lg"
          data-testid="fo-generate-btn"
        >
          {loading ? (
            <><span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" /> Analyse en cours…</>
          ) : (
            <><Calculator className="w-5 h-5" /> Générer mon plan financier</>
          )}
        </button>
      </div>

      {/* Result */}
      {plan && (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-3" data-testid="fo-result">
          {/* Diagnostic banner */}
          {plan.diagnostic && (
            <div className={`rounded-2xl p-5 border-2 ${
              plan.diagnostic.status === 'rouge' ? 'bg-red-50 border-red-300' :
              plan.diagnostic.status === 'orange' ? 'bg-orange-50 border-orange-300' :
              'bg-green-50 border-green-300'
            }`}>
              <p className={`text-[10px] font-black uppercase tracking-widest mb-1.5 ${
                plan.diagnostic.status === 'rouge' ? 'text-red-700' :
                plan.diagnostic.status === 'orange' ? 'text-orange-700' :
                'text-green-700'
              }`}>🩺 Diagnostic — Zone {plan.diagnostic.status}</p>
              <p className="text-[#1a1a1a] text-xl font-black leading-tight mb-2">{plan.diagnostic.headline}</p>
              <p className="text-[#1a1a1a]/85 text-sm leading-relaxed">{plan.diagnostic.summary}</p>
              {plan.diagnostic.monthly_loss_estimate > 0 && (
                <div className="mt-3 pt-3 border-t border-current/20">
                  <p className="text-xs text-[#1a1a1a]/60 font-bold uppercase">Perte estimée /mois</p>
                  <p className="text-2xl font-black tabular-nums text-red-600">−{Math.round(plan.diagnostic.monthly_loss_estimate).toLocaleString('fr-FR')}€</p>
                </div>
              )}
            </div>
          )}

          {/* Key metrics */}
          {plan.key_metrics && plan.key_metrics.length > 0 && (
            <div className="bg-white rounded-2xl p-4 border border-[#e0dbd4]">
              <p className="text-[#1a1a1a]/60 text-[10px] font-black uppercase tracking-widest mb-3">📊 Tes indicateurs clés</p>
              <div className="space-y-2.5">
                {plan.key_metrics.map((m, i) => (
                  <div key={i} className={`rounded-xl p-3 border ${statusColor(m.status)}`}>
                    <div className="flex items-center justify-between gap-2 mb-1.5">
                      <p className="font-bold text-sm">{m.label}</p>
                      <span className="text-xs font-black tabular-nums">{m.current}</span>
                    </div>
                    <p className="text-xs opacity-80 mb-1.5">🎯 Cible : <strong>{m.target}</strong></p>
                    <p className="text-xs text-[#1a1a1a]/80 leading-snug">{m.comment}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Actions */}
          {plan.actions && plan.actions.length > 0 && (
            <div className="bg-white rounded-2xl p-4 border border-[#e0dbd4]">
              <div className="flex items-center justify-between mb-3">
                <p className="text-[#1a1a1a]/60 text-[10px] font-black uppercase tracking-widest">🎯 Plan d'action chiffré</p>
                {plan.monthly_gain_total > 0 && (
                  <div className="bg-emerald-100 px-2.5 py-1 rounded-lg">
                    <p className="text-emerald-800 text-xs font-black">+{Math.round(plan.monthly_gain_total).toLocaleString('fr-FR')}€/mois</p>
                  </div>
                )}
              </div>
              <div className="space-y-2.5">
                {plan.actions.map((a, i) => (
                  <div key={i} className="bg-[#fafaf9] rounded-xl p-3.5 border border-[#e0dbd4]">
                    <div className="flex items-start gap-2.5 mb-2">
                      <div className="w-7 h-7 rounded-full bg-emerald-600 text-white text-xs font-black flex items-center justify-center flex-shrink-0">{a.priority || i+1}</div>
                      <div className="flex-1 min-w-0">
                        <p className="text-[#1a1a1a] font-black text-sm leading-tight">{a.title}</p>
                        <div className="flex flex-wrap gap-1.5 mt-1">
                          {a.monthly_gain_eur > 0 && <span className="px-1.5 py-0.5 bg-emerald-100 text-emerald-800 text-[10px] font-bold rounded">+{Math.round(a.monthly_gain_eur).toLocaleString('fr-FR')}€/mois</span>}
                          {a.effort && <span className={`px-1.5 py-0.5 text-[10px] font-bold rounded ${a.effort === 'facile' ? 'bg-green-100 text-green-800' : a.effort === 'moyen' ? 'bg-amber-100 text-amber-800' : 'bg-red-100 text-red-800'}`}>{a.effort}</span>}
                          {a.delay_days && <span className="px-1.5 py-0.5 bg-blue-100 text-blue-800 text-[10px] font-bold rounded">{a.delay_days}j</span>}
                          {a.category && <span className="px-1.5 py-0.5 bg-[#1a1a1a]/10 text-[#1a1a1a]/70 text-[10px] font-bold rounded uppercase">{a.category}</span>}
                        </div>
                      </div>
                    </div>
                    <p className="text-[#1a1a1a]/80 text-xs leading-relaxed pl-9">{a.description}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Roadmap 30 jours */}
          {plan.roadmap_30_days && (
            <div className="bg-white rounded-2xl p-4 border border-[#e0dbd4]">
              <p className="text-[#1a1a1a]/60 text-[10px] font-black uppercase tracking-widest mb-3">📅 Roadmap 30 jours</p>
              <div className="space-y-2">
                {Object.entries(plan.roadmap_30_days).map(([wk, items]) => {
                  const labels = { week_0: 'Semaine 0 — Stop hémorragie', week_1: 'Semaine 1', week_2: 'Semaine 2', week_3: 'Semaine 3', week_4: 'Semaine 4 — Bilan' };
                  const colors = { week_0: 'border-red-400 bg-red-50', week_1: 'border-orange-400 bg-orange-50', week_2: 'border-amber-400 bg-amber-50', week_3: 'border-blue-400 bg-blue-50', week_4: 'border-emerald-400 bg-emerald-50' };
                  return (
                    <div key={wk} className={`rounded-xl p-3 border-l-4 ${colors[wk]}`}>
                      <p className="text-[#1a1a1a] font-black text-xs uppercase tracking-wider mb-1.5">{labels[wk] || wk}</p>
                      <ul className="space-y-1">
                        {(Array.isArray(items) ? items : []).map((it, i) => (
                          <li key={i} className="text-[#1a1a1a]/85 text-xs leading-snug">✓ {it}</li>
                        ))}
                      </ul>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Punchline */}
          {plan.final_punchline && (
            <div className="bg-gradient-to-br from-[#1a1a1a] to-[#2d1810] rounded-2xl p-5 text-white">
              <p className="text-orange-400 text-[10px] font-black uppercase tracking-widest mb-2">💪 Le mot de Sabril</p>
              <p className="text-base font-bold leading-snug">"{plan.final_punchline}"</p>
            </div>
          )}
        </motion.div>
      )}
    </div>
  );
};

// ============ MENU TRENDS GENERATOR (IA) ============
const MenuTrendsGenerator = () => {
  const RESTO_TYPES = [
    { id: 'brunch', label: 'Brunch', emoji: '🥐' },
    { id: 'bistrot', label: 'Bistrot', emoji: '🍷' },
    { id: 'gastro', label: 'Gastro', emoji: '⭐' },
    { id: 'fast-casual', label: 'Fast Casual', emoji: '🥗' },
    { id: 'coffee', label: 'Coffee', emoji: '☕' },
    { id: 'italien', label: 'Italien', emoji: '🇮🇹' },
    { id: 'burger', label: 'Burger', emoji: '🍔' },
    { id: 'healthy', label: 'Healthy', emoji: '🥬' },
    { id: 'asiatique', label: 'Asiatique', emoji: '🥢' },
    { id: 'pizzeria', label: 'Pizzeria', emoji: '🍕' },
    { id: 'tapas', label: 'Tapas', emoji: '🫒' },
    { id: 'libanais', label: 'Libanais', emoji: '🧆' },
  ];
  const FILTERS = [
    { id: 'all', label: 'Tout', emoji: '✨' },
    { id: 'drinks', label: 'Boissons', emoji: '🥤' },
    { id: 'dishes', label: 'Plats', emoji: '🍽' },
    { id: 'desserts', label: 'Desserts', emoji: '🍰' },
    { id: 'concepts', label: 'Concepts', emoji: '💡' },
    { id: 'social_trends', label: 'Réseaux', emoji: '📱' },
  ];
  const CAT_LABELS = {
    drinks: { label: 'Boissons', emoji: '🥤', color: 'bg-pink-50 text-pink-700 border-pink-200' },
    dishes: { label: 'Plats', emoji: '🍽', color: 'bg-orange-50 text-orange-700 border-orange-200' },
    desserts: { label: 'Desserts', emoji: '🍰', color: 'bg-amber-50 text-amber-700 border-amber-200' },
    concepts: { label: 'Concepts', emoji: '💡', color: 'bg-purple-50 text-purple-700 border-purple-200' },
    social_trends: { label: 'Réseaux sociaux', emoji: '📱', color: 'bg-blue-50 text-blue-700 border-blue-200' },
  };

  const [activeType, setActiveType] = useState(null);
  const [filter, setFilter] = useState('all');
  const [trendsByType, setTrendsByType] = useState({});
  const [loadingType, setLoadingType] = useState(null);
  const [error, setError] = useState('');

  const loadTrends = async (typeId) => {
    setActiveType(typeId);
    if (trendsByType[typeId]) return; // cached
    setLoadingType(typeId);
    setError('');
    try {
      const res = await fetch(`${API_URL}/api/ai/generate-menu-trends`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ restaurant_type: typeId }),
      });
      if (!res.ok) {
        const errBody = await res.json().catch(() => ({}));
        throw new Error(errBody.detail || `HTTP ${res.status}`);
      }
      const data = await res.json();
      setTrendsByType(prev => ({ ...prev, [typeId]: data.trends }));
    } catch (err) {
      setError(`Génération échouée : ${err.message}`);
    }
    setLoadingType(null);
  };

  const trends = activeType ? trendsByType[activeType] : null;
  const categoriesToShow = filter === 'all' ? ['drinks', 'dishes', 'desserts', 'concepts', 'social_trends'] : [filter];

  return (
    <div className="space-y-4" data-testid="menu-trends-generator">
      {/* Header card */}
      <div className="bg-gradient-to-br from-orange-500 to-amber-500 rounded-2xl p-5 text-white">
        <div className="flex items-start gap-3 mb-2">
          <TrendingUp className="w-6 h-6 flex-shrink-0" />
          <div className="flex-1">
            <h3 className="text-xl font-black leading-tight">Tendances du Secteur</h3>
            <p className="text-white/85 text-sm mt-1">Recettes, boissons & concepts populaires 2026</p>
          </div>
        </div>
        <p className="text-white/90 text-xs leading-relaxed">
          Choisis ton type de resto pour découvrir les tendances actuelles. Food cost et prix conseillés inclus.
        </p>
      </div>

      {/* Restaurant type selector */}
      <div>
        <p className="text-[#1a1a1a]/60 text-xs font-bold uppercase tracking-wider mb-2">Type de restaurant</p>
        <div className="flex gap-2 overflow-x-auto pb-2 -mx-1 px-1 snap-x">
          {RESTO_TYPES.map(t => (
            <button
              key={t.id}
              onClick={() => loadTrends(t.id)}
              data-testid={`menu-trend-type-${t.id}`}
              className={`flex-shrink-0 px-4 py-2.5 rounded-xl text-sm font-bold transition snap-start ${
                activeType === t.id
                  ? 'bg-green-600 text-white shadow-md'
                  : 'bg-white border border-[#e0dbd4] text-[#1a1a1a] hover:bg-[#f0ede8]'
              }`}
            >
              <span className="mr-1.5">{t.emoji}</span>{t.label}
            </button>
          ))}
        </div>
      </div>

      {loadingType && (
        <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-center gap-3">
          <span className="w-5 h-5 border-2 border-green-600 border-t-transparent rounded-full animate-spin" />
          <p className="text-green-800 text-sm font-bold">L'IA génère les tendances 2026 pour ce type…</p>
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-red-700 text-sm font-bold" data-testid="menu-trend-error">
          {error}
        </div>
      )}

      {/* Filters */}
      {trends && (
        <div className="flex gap-2 overflow-x-auto pb-2 -mx-1 px-1 snap-x">
          {FILTERS.map(f => (
            <button
              key={f.id}
              onClick={() => setFilter(f.id)}
              className={`flex-shrink-0 px-3 py-2 rounded-full text-xs font-bold transition snap-start ${
                filter === f.id
                  ? 'bg-[#1a1a1a] text-white'
                  : 'bg-[#f0ede8] text-[#1a1a1a]/70 hover:bg-[#e0dbd4]'
              }`}
            >
              <span className="mr-1">{f.emoji}</span>{f.label}
            </button>
          ))}
        </div>
      )}

      {/* Results */}
      {trends && categoriesToShow.map(cat => {
        const items = trends[cat] || [];
        if (!items.length) return null;
        const meta = CAT_LABELS[cat];
        return (
          <div key={cat} className="space-y-2">
            <div className="flex items-center gap-2">
              <div className={`w-9 h-9 rounded-full flex items-center justify-center text-base border ${meta.color}`}>{meta.emoji}</div>
              <p className="text-[#1a1a1a] font-black text-base">{meta.label} <span className="text-[#1a1a1a]/40 text-sm font-bold">({items.length})</span></p>
            </div>
            {items.map((item, i) => (
              <div key={i} className="bg-white rounded-2xl p-4 border border-[#e0dbd4]">
                <div className="flex items-start justify-between gap-2 mb-1.5">
                  <p className="text-[#1a1a1a] font-black text-base leading-tight flex-1 min-w-0">{item.name}</p>
                  {(cat === 'drinks' || cat === 'dishes' || cat === 'desserts') && (
                    <span className="px-2 py-0.5 bg-green-100 text-green-700 text-[10px] font-bold rounded-md flex-shrink-0">Recette</span>
                  )}
                  {cat === 'concepts' && <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-[10px] font-bold rounded-md flex-shrink-0">Concept</span>}
                  {cat === 'social_trends' && <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-[10px] font-bold rounded-md flex-shrink-0">Format</span>}
                </div>
                <p className="text-[#1a1a1a]/75 text-sm leading-snug mb-2.5">{item.description}</p>
                <div className="flex flex-wrap items-center gap-3 mb-2 text-xs">
                  {typeof item.popularity === 'number' && (
                    <span className="inline-flex items-center gap-1 text-amber-700"><Star className="w-3 h-3" />{item.popularity}%</span>
                  )}
                  {typeof item.price_eur === 'number' && item.price_eur > 0 && (
                    <span className="inline-flex items-center gap-1 text-green-700 font-bold">€ {item.price_eur}€</span>
                  )}
                  {typeof item.food_cost_pct === 'number' && item.food_cost_pct > 0 && (
                    <span className="inline-flex items-center gap-1 text-blue-700 font-bold">% FC {item.food_cost_pct}%</span>
                  )}
                </div>
                {item.hashtags && item.hashtags.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {item.hashtags.map((tag, j) => (
                      <span key={j} className="px-2 py-0.5 bg-[#f5f3f0] text-[#1a1a1a]/60 text-[10px] rounded font-bold">#{tag}</span>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        );
      })}

      {!activeType && (
        <div className="bg-[#f0ede8] rounded-xl p-4 text-center">
          <p className="text-[#1a1a1a]/60 text-sm">👆 Sélectionne ton type de resto pour démarrer.</p>
        </div>
      )}
    </div>
  );
};

// ============ VIRAL SCRIPT GENERATOR (IA) ============
const ViralScriptGenerator = ({ restaurantType, restaurantName }) => {
  const [formData, setFormData] = useState({
    objective: 'Engagement / vues',
    platform: 'Instagram Reel',
    audience: 'foodies locaux 25-45 ans',
    angle: 'Avant / Après',
    duration: 30,
    tone: 'Authentique / chaleureux',
    subject: '',
  });
  const [loading, setLoading] = useState(false);
  const [script, setScript] = useState(null);
  const [error, setError] = useState('');

  const handleGenerate = async () => {
    if (!formData.subject.trim()) {
      setError('Décris le sujet précis de ta vidéo.');
      return;
    }
    setError('');
    setLoading(true);
    setScript(null);
    try {
      const res = await fetch(`${API_URL}/api/ai/generate-viral-script`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...formData, restaurant_type: restaurantType, restaurant_name: restaurantName }),
      });
      if (!res.ok) {
        const errBody = await res.json().catch(() => ({}));
        throw new Error(errBody.detail || `HTTP ${res.status}`);
      }
      const data = await res.json();
      setScript(data.script);
    } catch (err) {
      setError(`Génération échouée : ${err.message}`);
    }
    setLoading(false);
  };

  const copyScript = () => {
    if (!script) return;
    const text = `🎬 HOOK : ${script.hook}\n\n📽 SCÈNES :\n${(script.scenes || []).map(s => `[${s.time}] Visuel : ${s.visual}\n  Texte : ${s.text_overlay}`).join('\n\n')}\n\n🎙 VOIX OFF : ${script.voice_over}\n\n📣 CTA : ${script.cta}\n\n🎵 SON : ${script.sound_suggestion}\n\n#️⃣ HASHTAGS : ${(script.hashtags || []).map(h => `#${h}`).join(' ')}\n\n💡 PRO TIP : ${script.pro_tip}`;
    navigator.clipboard.writeText(text);
  };

  return (
    <div className="space-y-4" data-testid="viral-generator">
      <div className="bg-gradient-to-br from-purple-100 via-pink-50 to-orange-50 rounded-2xl p-5 border border-purple-200">
        <div className="flex items-center gap-2 mb-1">
          <Sparkles className="w-5 h-5 text-purple-600" />
          <h3 className="text-purple-700 font-black text-lg">Générateur de scripts viraux</h3>
        </div>
        <p className="text-[#1a1a1a]/80 text-sm">
          L'IA construit un script complet : <strong>hook scotchant</strong>, scènes, texte à incruster, CTA, hashtags pertinents et son tendance suggéré.
        </p>
      </div>

      <div className="bg-white rounded-2xl border border-[#e0dbd4] p-5 space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <label className="block">
            <span className="text-xs font-bold text-[#1a1a1a] mb-1.5 flex items-center gap-1.5">🎯 Objectif</span>
            <select value={formData.objective} onChange={(e) => setFormData({...formData, objective: e.target.value})} className="w-full px-3 py-2.5 bg-[#f5f3f0] border border-[#e0dbd4] rounded-xl text-sm font-bold outline-none focus:border-purple-500" data-testid="vg-objective">
              <option>Engagement / vues</option>
              <option>Visites en restaurant</option>
              <option>Notoriété locale</option>
              <option>Conversion Uber Eats / Click & Collect</option>
              <option>Recrutement équipe</option>
            </select>
          </label>
          <label className="block">
            <span className="text-xs font-bold text-[#1a1a1a] mb-1.5 flex items-center gap-1.5">📱 Plateforme & format</span>
            <select value={formData.platform} onChange={(e) => setFormData({...formData, platform: e.target.value})} className="w-full px-3 py-2.5 bg-[#f5f3f0] border border-[#e0dbd4] rounded-xl text-sm font-bold outline-none focus:border-purple-500" data-testid="vg-platform">
              <option>Instagram Reel</option>
              <option>TikTok</option>
              <option>Instagram Story</option>
              <option>YouTube Shorts</option>
              <option>Post photo + carrousel</option>
            </select>
          </label>
          <label className="block">
            <span className="text-xs font-bold text-[#1a1a1a] mb-1.5 flex items-center gap-1.5">👥 Audience cible</span>
            <input type="text" value={formData.audience} onChange={(e) => setFormData({...formData, audience: e.target.value})} placeholder="foodies locaux 25-45 ans" className="w-full px-3 py-2.5 bg-[#f5f3f0] border border-[#e0dbd4] rounded-xl text-sm outline-none focus:border-purple-500" data-testid="vg-audience" />
          </label>
          <label className="block">
            <span className="text-xs font-bold text-[#1a1a1a] mb-1.5 flex items-center gap-1.5">🎬 Angle / mécanique</span>
            <select value={formData.angle} onChange={(e) => setFormData({...formData, angle: e.target.value})} className="w-full px-3 py-2.5 bg-[#f5f3f0] border border-[#e0dbd4] rounded-xl text-sm font-bold outline-none focus:border-purple-500" data-testid="vg-angle">
              <option>Avant / Après</option>
              <option>Behind the scenes</option>
              <option>Erreur fréquente → fix</option>
              <option>POV client</option>
              <option>Story du chef</option>
              <option>Recette express</option>
              <option>Réaction / dégustation</option>
            </select>
          </label>
          <label className="block">
            <span className="text-xs font-bold text-[#1a1a1a] mb-1.5 flex items-center gap-1.5">⏱ Durée (s)</span>
            <input type="number" min={15} max={90} value={formData.duration} onChange={(e) => setFormData({...formData, duration: parseInt(e.target.value) || 30})} className="w-full px-3 py-2.5 bg-[#f5f3f0] border border-[#e0dbd4] rounded-xl text-sm font-bold outline-none focus:border-purple-500" data-testid="vg-duration" />
          </label>
          <label className="block">
            <span className="text-xs font-bold text-[#1a1a1a] mb-1.5 flex items-center gap-1.5">🎙 Ton</span>
            <select value={formData.tone} onChange={(e) => setFormData({...formData, tone: e.target.value})} className="w-full px-3 py-2.5 bg-[#f5f3f0] border border-[#e0dbd4] rounded-xl text-sm font-bold outline-none focus:border-purple-500" data-testid="vg-tone">
              <option>Authentique / chaleureux</option>
              <option>Énergique / punchy</option>
              <option>Premium / posé</option>
              <option>Humour / décalé</option>
              <option>Émotion / storytelling</option>
            </select>
          </label>
        </div>

        <label className="block">
          <span className="text-xs font-bold text-[#1a1a1a] mb-1.5 flex items-center gap-1.5">📝 Sujet précis de la vidéo</span>
          <textarea
            value={formData.subject}
            onChange={(e) => { setFormData({...formData, subject: e.target.value}); setError(''); }}
            rows={2}
            placeholder='Ex : "Notre nouveau brunch healthy du dimanche : avocado toast, açaï bowl, pancakes maison"'
            className="w-full px-3 py-2.5 bg-[#f5f3f0] border border-[#e0dbd4] rounded-xl text-sm outline-none focus:border-purple-500"
            data-testid="vg-subject"
          />
        </label>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-sm text-red-700 font-bold" data-testid="vg-error">{error}</div>
        )}

        <button
          onClick={handleGenerate}
          disabled={loading}
          className="w-full py-4 bg-gradient-to-r from-purple-500 via-pink-500 to-orange-500 hover:from-purple-600 hover:to-orange-600 disabled:opacity-60 text-white font-black text-base rounded-xl flex items-center justify-center gap-2 transition shadow-lg"
          data-testid="vg-generate-btn"
        >
          {loading ? (
            <><span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" /> Génération en cours…</>
          ) : (
            <><Sparkles className="w-5 h-5" /> Générer le script viral</>
          )}
        </button>
      </div>

      {/* Result */}
      {script && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-3"
          data-testid="vg-result"
        >
          <div className="bg-gradient-to-br from-[#1a1a1a] to-[#2d1810] text-white rounded-2xl p-5 border-2 border-orange-500/30">
            <p className="text-orange-400 text-[10px] font-black uppercase tracking-widest mb-2">🎬 HOOK</p>
            <p className="text-lg font-black leading-tight">{script.hook}</p>
          </div>

          {script.scenes && script.scenes.length > 0 && (
            <div className="bg-white rounded-2xl p-5 border border-[#e0dbd4]">
              <p className="text-[#1a1a1a]/60 text-[10px] font-black uppercase tracking-widest mb-3">📽 SCÈNES</p>
              <div className="space-y-3">
                {script.scenes.map((scene, i) => (
                  <div key={i} className="border-l-2 border-purple-400 pl-3">
                    <p className="text-purple-700 text-xs font-bold">[{scene.time}]</p>
                    <p className="text-[#1a1a1a] text-sm mt-0.5"><strong>Visuel :</strong> {scene.visual}</p>
                    <p className="text-[#1a1a1a]/70 text-sm mt-0.5"><strong>Texte :</strong> "{scene.text_overlay}"</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {script.voice_over && (
            <div className="bg-blue-50 rounded-2xl p-4 border border-blue-200">
              <p className="text-blue-700 text-[10px] font-black uppercase tracking-widest mb-1.5">🎙 VOIX OFF</p>
              <p className="text-[#1a1a1a] text-sm italic leading-relaxed">"{script.voice_over}"</p>
            </div>
          )}

          {script.cta && (
            <div className="bg-orange-50 rounded-2xl p-4 border border-orange-200">
              <p className="text-orange-700 text-[10px] font-black uppercase tracking-widest mb-1.5">📣 CTA</p>
              <p className="text-[#1a1a1a] text-sm font-bold">{script.cta}</p>
            </div>
          )}

          {script.sound_suggestion && (
            <div className="bg-pink-50 rounded-2xl p-4 border border-pink-200">
              <p className="text-pink-700 text-[10px] font-black uppercase tracking-widest mb-1.5">🎵 SON SUGGÉRÉ</p>
              <p className="text-[#1a1a1a] text-sm">{script.sound_suggestion}</p>
            </div>
          )}

          {script.hashtags && script.hashtags.length > 0 && (
            <div className="bg-[#f5f3f0] rounded-2xl p-4 border border-[#e0dbd4]">
              <p className="text-[#1a1a1a]/60 text-[10px] font-black uppercase tracking-widest mb-2">#️⃣ HASHTAGS</p>
              <div className="flex flex-wrap gap-1.5">
                {script.hashtags.map((tag, i) => (
                  <span key={i} className="px-2 py-1 bg-white border border-[#e0dbd4] rounded-md text-xs font-bold text-[#1a1a1a]">#{tag}</span>
                ))}
              </div>
            </div>
          )}

          {script.pro_tip && (
            <div className="bg-amber-50 rounded-2xl p-4 border border-amber-200">
              <p className="text-amber-700 text-[10px] font-black uppercase tracking-widest mb-1.5">💡 PRO TIP DE SABRIL</p>
              <p className="text-[#1a1a1a] text-sm font-bold leading-snug">{script.pro_tip}</p>
            </div>
          )}

          <button onClick={copyScript} className="w-full py-3 bg-[#1a1a1a] hover:bg-black text-white font-bold rounded-xl text-sm" data-testid="vg-copy-btn">
            📋 Copier le script complet
          </button>
        </motion.div>
      )}
    </div>
  );
};

// Theme
const theme = {
  bg: '#0F0F0F',
  card: '#1A1A1A',
  orange: '#F05A1A',
  green: '#10B981',
  blue: '#3B82F6',
  purple: '#8B5CF6',
  yellow: '#F59E0B',
  red: '#EF4444',
};

const EbookPage = () => {
  const { accessCode } = useParams();
  const navigate = useNavigate();
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [showCodeForm, setShowCodeForm] = useState(false);
  const [codeInput, setCodeInput] = useState('');
  const [codeError, setCodeError] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [downloadUrl, setDownloadUrl] = useState('');
  const [currentScreen, setCurrentScreen] = useState(0);
  const [exportMode, setExportMode] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [visitedScreens, setVisitedScreens] = useState({});
  const [completedActions, setCompletedActions] = useState({});
  const [userInputs, setUserInputs] = useState({
    restaurantName: '',
    restaurantType: '',
    city: '',
    monthlyCA: '',
    teamSize: '',
    mainObjective: '',
    whyClientComes: '',
    breakEven: '',
    foodCost: '',
    staffCost: '',
    primeCost: '',
    returnToZero: '',
    basketIncrease: '',
    monthlyGain: '',
    ideaForIncrease: ''
  });
  
  // Verify access code
  useEffect(() => {
    const verifyAccess = async () => {
      if (!accessCode) {
        // No code in URL → show form
        setShowCodeForm(true);
        setIsLoading(false);
        return;
      }
      try {
        const res = await fetch(`${API_URL}/api/ebook/verify-code`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ access_code: accessCode })
        });
        if (res.ok) {
          const data = await res.json();
          if (data.valid) {
            if (data.download_url) {
              // Store the URL and show the ebook-ready screen with download button
              const target = data.download_url.startsWith('http')
                ? data.download_url
                : `${window.location.origin}${data.download_url}`;
              setDownloadUrl(target);
              setIsAuthorized(true);
              setIsLoading(false);
              return;
            }
            // No download_url (edge case) → fallback to code form
            setShowCodeForm(true);
            setCodeError('Impossible de générer le lien PDF. Contacte le support.');
          } else {
            setShowCodeForm(true);
            setCodeError('Code invalide ou expiré.');
          }
        } else {
          // 404 = code not found
          setShowCodeForm(true);
          setCodeError('Code invalide ou expiré.');
        }
      } catch (err) {
        setShowCodeForm(true);
        setCodeError('Impossible de vérifier le code. Réessaie.');
      }
      setIsLoading(false);
    };
    verifyAccess();
  }, [accessCode, navigate]);

  // Submit code from the form
  const handleSubmitCode = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    const code = (codeInput || '').trim();
    if (!code) {
      setCodeError('Entre ton code d\'accès.');
      return;
    }
    setCodeError('');
    setVerifying(true);
    try {
      const res = await fetch(`${API_URL}/api/ebook/verify-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ access_code: code })
      });
      if (res.ok) {
        const data = await res.json();
        if (data.valid && data.download_url) {
          // Store the URL and show the ebook-ready screen with download button
          const target = data.download_url.startsWith('http')
            ? data.download_url
            : `${window.location.origin}${data.download_url}`;
          setDownloadUrl(target);
          setIsAuthorized(true);
          setVerifying(false);
          return;
        }
        setCodeError('Code valide mais lien PDF indisponible. Contacte le support.');
      } else {
        // 404 = code not found
        setCodeError('Code invalide. Vérifie ton email de confirmation.');
      }
    } catch (err) {
      setCodeError('Erreur réseau. Réessaie.');
    }
    setVerifying(false);
  };
  
  // Toggle action completion
  const toggleAction = (screenId, actionId) => {
    setCompletedActions(prev => ({
      ...prev,
      [`${screenId}-${actionId}`]: !prev[`${screenId}-${actionId}`]
    }));
  };

  // Track visited screens (for "✓" in sidebar)
  useEffect(() => {
    if (isAuthorized && screens && screens[currentScreen]) {
      const id = screens[currentScreen].id;
      setVisitedScreens(prev => prev[id] ? prev : { ...prev, [id]: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentScreen, isAuthorized]);

  // Persist sidebar progress to localStorage so the user comes back where they were
  useEffect(() => {
    if (!accessCode || !isAuthorized) return;
    try {
      const saved = localStorage.getItem(`ebook-progress-${accessCode}`);
      if (saved) {
        const { screen, visited } = JSON.parse(saved);
        if (typeof screen === 'number' && screen >= 0) setCurrentScreen(screen);
        if (visited && typeof visited === 'object') setVisitedScreens(visited);
      }
    } catch (e) { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthorized, accessCode]);

  useEffect(() => {
    if (!accessCode || !isAuthorized) return;
    try {
      localStorage.setItem(`ebook-progress-${accessCode}`, JSON.stringify({ screen: currentScreen, visited: visitedScreens }));
    } catch (e) { /* ignore */ }
  }, [currentScreen, visitedScreens, accessCode, isAuthorized]);

  const goToScreen = (idx) => {
    setCurrentScreen(idx);
    setSidebarOpen(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Export full plan as printable PDF
  const handleExportFullPlan = () => {
    setExportMode(true);
    // Wait for all screens to render then trigger native print
    setTimeout(() => {
      window.print();
      // Reset shortly after print dialog closes
      setTimeout(() => setExportMode(false), 800);
    }, 500);
  };
  
  // Calculate progress
  const totalActions = 19;
  const completedCount = Object.values(completedActions).filter(Boolean).length;
  const progress = Math.round((completedCount / totalActions) * 100);
  
  // Screens content
  const screens = [
    // SCREEN 0 - ACCROCHE
    {
      id: 'accroche',
      title: 'BIENVENUE',
      content: (
        <div className="space-y-8">
          <div>
            <p className="text-orange-600 text-xs font-bold uppercase tracking-widest mb-4">Plan d'exécution personnalisé · 30 jours</p>
            <motion.h1 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="font-['Playfair_Display',serif] text-4xl md:text-5xl font-black text-[#1a1a1a] leading-[1.05] tracking-tight"
            >
              Ce n'est pas un ebook.
              <br/>
              <span className="text-orange-500">C'est ton plan d'exécution.</span>
            </motion.h1>
          </div>
          
          <motion.p 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="text-lg text-[#1a1a1a]/75 leading-relaxed"
          >
            Tu vas remplir chaque étape avec <span className="font-bold text-[#1a1a1a]">TES chiffres</span>, <span className="font-bold text-[#1a1a1a]">TON secteur</span>, <span className="font-bold text-[#1a1a1a]">TA réalité</span>. À la fin, tu obtiens <span className="text-orange-500 font-bold">un plan 30 jours sur-mesure</span> — actions prioritaires, jour par jour — construit avec ma méthode (celle qui fait tourner Maison Avocado).
          </motion.p>
          
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="bg-white rounded-2xl p-6 border border-[#e0dbd4]"
          >
            <p className="text-sm font-bold text-[#1a1a1a] uppercase tracking-wider mb-4">Comment ça marche :</p>
            <div className="space-y-3">
              {[
                { n: '1', t: 'Tu remplis les diagnostics (food cost, CA, seuil, équipe...)' },
                { n: '2', t: 'Tu apprends chaque brique : finance, menu, équipe, marketing' },
                { n: '3', t: 'À la fin : ton plan 30 jours généré selon TES réponses' },
              ].map((s) => (
                <div key={s.n} className="flex items-start gap-3">
                  <span className="w-6 h-6 rounded-full bg-orange-500 text-white text-xs font-black flex items-center justify-center flex-shrink-0 mt-0.5">{s.n}</span>
                  <span className="text-sm text-[#1a1a1a]/80">{s.t}</span>
                </div>
              ))}
            </div>
          </motion.div>
          
          <motion.button
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.8 }}
            onClick={() => setCurrentScreen(1)}
            className="w-full py-4 bg-orange-500 hover:bg-orange-600 text-white font-bold rounded-xl flex items-center justify-center gap-2 transition-all"
          >
            Construire mon plan <ChevronRight className="w-5 h-5" />
          </motion.button>
        </div>
      )
    },
    
    // SCREEN 1 - PROFIL Q1 : Nom du resto
    {
      id: 'profil-name',
      title: 'TON RESTO',
      content: (
        <div className="space-y-7">
          <div>
            <p className="text-orange-600 text-xs font-bold uppercase tracking-widest mb-3">Étape 1 sur 5</p>
            <h2 className="font-['Playfair_Display',serif] text-3xl md:text-4xl font-black text-[#1a1a1a] leading-[1.05]">
              Comment s'appelle <span className="text-orange-500">ton resto ?</span>
            </h2>
            <p className="text-[#1a1a1a]/60 text-sm mt-2">On va construire ton plan ensemble, étape par étape.</p>
          </div>

          <div>
            <input
              type="text"
              value={userInputs.restaurantName}
              onChange={(e) => setUserInputs({...userInputs, restaurantName: e.target.value})}
              placeholder="Ex : Maison Avocado"
              autoFocus
              className="w-full px-5 py-4 rounded-2xl bg-white border-2 border-[#e0dbd4] text-[#1a1a1a] text-lg font-bold outline-none focus:border-orange-500 transition shadow-sm"
            />
          </div>

          {userInputs.restaurantName && userInputs.restaurantName.length > 1 && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-2xl p-5 border border-orange-200"
            >
              <div className="flex items-center gap-2 mb-2">
                <Sparkles className="w-4 h-4 text-orange-600" />
                <span className="text-orange-700 font-bold text-xs uppercase tracking-wider">Personnalisation activée</span>
              </div>
              <p className="text-[#1a1a1a] text-sm">
                Salut <span className="font-bold text-orange-600">{userInputs.restaurantName}</span> ! Ton plan 30 jours sera construit selon tes réponses. Suivant →
              </p>
            </motion.div>
          )}
        </div>
      )
    },

    // SCREEN 2 - PROFIL Q2 : Type + ville
    {
      id: 'profil-type',
      title: 'TYPE & VILLE',
      content: (
        <div className="space-y-7">
          <div>
            <p className="text-orange-600 text-xs font-bold uppercase tracking-widest mb-3">Étape 2 sur 5</p>
            <h2 className="font-['Playfair_Display',serif] text-3xl md:text-4xl font-black text-[#1a1a1a] leading-[1.05]">
              Quel type ? <span className="text-orange-500">Quelle ville ?</span>
            </h2>
            <p className="text-[#1a1a1a]/60 text-sm mt-2">Pour adapter les exemples à ton terrain.</p>
          </div>

          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-[#1a1a1a]/70 mb-2">Type de resto</label>
            <div className="grid grid-cols-2 gap-2">
              {['Traditionnel', 'Pizzeria', 'Fast-food', 'Bistrot', 'Brasserie', 'Asiatique', 'Healthy / bowl', 'Autre'].map((t) => (
                <button
                  key={t}
                  onClick={() => setUserInputs({...userInputs, restaurantType: t})}
                  className={`px-3 py-3 rounded-xl text-sm font-bold transition ${
                    userInputs.restaurantType === t
                      ? 'bg-orange-500 text-white border-2 border-orange-500 shadow-md'
                      : 'bg-white text-[#1a1a1a] border-2 border-[#e0dbd4] hover:border-orange-300'
                  }`}
                >
                  {t}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-[#1a1a1a]/70 mb-2">Ville</label>
            <input
              type="text"
              value={userInputs.city}
              onChange={(e) => setUserInputs({...userInputs, city: e.target.value})}
              placeholder="Ex : Lille"
              className="w-full px-5 py-4 rounded-2xl bg-white border-2 border-[#e0dbd4] text-[#1a1a1a] text-lg font-bold outline-none focus:border-orange-500 transition"
            />
          </div>

          {userInputs.restaurantType && userInputs.city && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-5 border border-green-200"
            >
              <div className="flex items-center gap-2 mb-2">
                <Target className="w-4 h-4 text-green-700" />
                <span className="text-green-700 font-bold text-xs uppercase tracking-wider">Cible identifiée</span>
              </div>
              <p className="text-[#1a1a1a] text-sm">
                <span className="font-bold">{userInputs.restaurantName}</span> · {userInputs.restaurantType} à {userInputs.city}.<br/>
                Les exemples du plan seront adaptés à ce profil.
              </p>
            </motion.div>
          )}
        </div>
      )
    },

    // SCREEN 3 - PROFIL Q3 : CA + équipe (avec révélation chiffres)
    {
      id: 'profil-numbers',
      title: 'TES CHIFFRES',
      content: (() => {
        const ca = parseFloat(userInputs.monthlyCA) || 0;
        const team = parseInt(userInputs.teamSize) || 0;
        const annualCA = ca * 12;
        const opportunityMonthly = Math.round(ca * 0.20);
        const savingsMonthly = Math.round(ca * 0.10);
        const totalImpact = opportunityMonthly + savingsMonthly;
        return (
          <div className="space-y-7">
            <div>
              <p className="text-orange-600 text-xs font-bold uppercase tracking-widest mb-3">Étape 3 sur 5</p>
              <h2 className="font-['Playfair_Display',serif] text-3xl md:text-4xl font-black text-[#1a1a1a] leading-[1.05]">
                Tes <span className="text-orange-500">chiffres clés.</span>
              </h2>
              <p className="text-[#1a1a1a]/60 text-sm mt-2">Pour calculer ton potentiel réel.</p>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-[#1a1a1a]/70 mb-2">CA mensuel</label>
                <div className="relative">
                  <input
                    type="number"
                    value={userInputs.monthlyCA}
                    onChange={(e) => setUserInputs({...userInputs, monthlyCA: e.target.value})}
                    placeholder="50 000"
                    className="w-full px-5 py-4 pr-12 rounded-2xl bg-white border-2 border-[#e0dbd4] text-[#1a1a1a] text-2xl font-black outline-none focus:border-orange-500 transition"
                  />
                  <span className="absolute right-5 top-1/2 -translate-y-1/2 text-[#1a1a1a]/40 text-xl font-bold">€</span>
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-[#1a1a1a]/70 mb-2">Combien dans ton équipe ?</label>
                <input
                  type="number"
                  value={userInputs.teamSize}
                  onChange={(e) => setUserInputs({...userInputs, teamSize: e.target.value})}
                  placeholder="5"
                  className="w-full px-5 py-4 rounded-2xl bg-white border-2 border-[#e0dbd4] text-[#1a1a1a] text-xl font-bold outline-none focus:border-orange-500 transition"
                />
              </div>
            </div>

            {ca > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-gradient-to-br from-orange-500 to-amber-500 rounded-3xl p-6 text-white shadow-lg"
              >
                <p className="text-white/90 text-xs font-bold uppercase tracking-wider mb-2">📊 Ton potentiel sur 30 jours</p>
                <div className="grid grid-cols-2 gap-3 mb-3">
                  <div className="bg-white/15 rounded-xl p-3">
                    <p className="text-white/80 text-[10px] uppercase font-bold">+20% CA</p>
                    <p className="text-white text-xl font-black tabular-nums">+{opportunityMonthly.toLocaleString('fr-FR')}€</p>
                  </div>
                  <div className="bg-white/15 rounded-xl p-3">
                    <p className="text-white/80 text-[10px] uppercase font-bold">-10% coûts</p>
                    <p className="text-white text-xl font-black tabular-nums">+{savingsMonthly.toLocaleString('fr-FR')}€</p>
                  </div>
                </div>
                <div className="bg-white rounded-xl p-3 text-center">
                  <p className="text-orange-700 text-[10px] uppercase font-black tracking-wider">Impact mensuel</p>
                  <p className="text-orange-600 text-3xl font-black tabular-nums">+{totalImpact.toLocaleString('fr-FR')}€</p>
                  <p className="text-[#1a1a1a]/60 text-[10px]">soit {(totalImpact * 12).toLocaleString('fr-FR')}€ sur 12 mois</p>
                </div>
              </motion.div>
            )}
          </div>
        );
      })()
    },

    // SCREEN 4 - PROFIL Q4 : Objectif principal
    {
      id: 'profil-objective',
      title: 'TON OBJECTIF',
      content: (
        <div className="space-y-7">
          <div>
            <p className="text-orange-600 text-xs font-bold uppercase tracking-widest mb-3">Étape 4 sur 5</p>
            <h2 className="font-['Playfair_Display',serif] text-3xl md:text-4xl font-black text-[#1a1a1a] leading-[1.05]">
              Ton objectif <span className="text-orange-500">n°1 ?</span>
            </h2>
            <p className="text-[#1a1a1a]/60 text-sm mt-2">Le plan se construit autour de cette priorité.</p>
          </div>

          <div className="space-y-3">
            {[
              { v: 'ca', icon: '📈', l: 'Augmenter mon CA', sub: 'Menu engineering, upsell, Uber Eats optimisé' },
              { v: 'cout', icon: '💰', l: 'Réduire mes coûts', sub: 'Food cost, fournisseurs, gaspillage' },
              { v: 'equipe', icon: '👥', l: 'Structurer mon équipe', sub: 'Onboarding, primes par paliers, briefing' },
              { v: 'temps', icon: '⚡', l: 'Libérer du temps pour moi', sub: 'Délégation, QR codes, automatisations IA' },
            ].map((o) => (
              <motion.button
                key={o.v}
                whileTap={{ scale: 0.98 }}
                onClick={() => setUserInputs({...userInputs, mainObjective: o.v})}
                className={`w-full flex items-start gap-3 px-4 py-4 rounded-2xl text-left transition ${
                  userInputs.mainObjective === o.v
                    ? 'bg-orange-500 text-white border-2 border-orange-500 shadow-lg'
                    : 'bg-white text-[#1a1a1a] border-2 border-[#e0dbd4] hover:border-orange-300'
                }`}
              >
                <span className="text-2xl flex-shrink-0">{o.icon}</span>
                <div className="flex-1 min-w-0">
                  <p className={`font-bold text-base ${userInputs.mainObjective === o.v ? 'text-white' : 'text-[#1a1a1a]'}`}>{o.l}</p>
                  <p className={`text-xs mt-0.5 ${userInputs.mainObjective === o.v ? 'text-white/80' : 'text-[#1a1a1a]/55'}`}>{o.sub}</p>
                </div>
                {userInputs.mainObjective === o.v && <Check className="w-5 h-5 text-white flex-shrink-0" />}
              </motion.button>
            ))}
          </div>
        </div>
      )
    },

    // SCREEN 5 - CONSTRUCTION DU PLAN — cinematic loader
    {
      id: 'construction',
      title: 'CONSTRUCTION',
      content: (() => {
        const obj = userInputs.mainObjective;
        const objLabel = obj === 'ca' ? 'augmenter ton CA'
          : obj === 'cout' ? 'réduire tes coûts'
          : obj === 'equipe' ? 'structurer ton équipe'
          : obj === 'temps' ? 'libérer ton temps'
          : 'optimiser ton resto';
        const buildSteps = [
          { icon: '🎯', label: `Objectif principal : ${objLabel}` },
          { icon: '📊', label: `Profil ${userInputs.restaurantType || 'resto'} ${userInputs.city ? '· ' + userInputs.city : ''}` },
          { icon: '📈', label: `Plan calibré sur ${userInputs.monthlyCA ? parseInt(userInputs.monthlyCA).toLocaleString('fr-FR') + '€' : 'ton CA'}/mois` },
          { icon: '🗓️', label: 'Génération des 4 semaines (S1 à S4)' },
          { icon: '✅', label: 'Plan personnalisé prêt' },
        ];
        return (
          <div className="space-y-6">
            <div>
              <p className="text-orange-600 text-xs font-bold uppercase tracking-widest mb-3">Étape 5 sur 5</p>
              <h2 className="font-['Playfair_Display',serif] text-3xl md:text-4xl font-black text-[#1a1a1a] leading-[1.05]">
                Je construis <span className="text-orange-500">ton plan...</span>
              </h2>
              <p className="text-[#1a1a1a]/60 text-sm mt-2">Tu verras le résultat à la dernière étape.</p>
            </div>

            <div className="space-y-3">
              {buildSteps.map((s, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.4, duration: 0.4 }}
                  className="flex items-center gap-3 bg-white rounded-2xl p-4 border border-[#e0dbd4]"
                >
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: i * 0.4 + 0.2, type: 'spring' }}
                    className="w-10 h-10 rounded-full bg-orange-500/10 flex items-center justify-center text-xl flex-shrink-0"
                  >
                    {s.icon}
                  </motion.div>
                  <span className="text-[#1a1a1a] font-bold text-sm">{s.label}</span>
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: i * 0.4 + 0.5 }}
                    className="ml-auto"
                  >
                    <Check className="w-5 h-5 text-green-600" />
                  </motion.div>
                </motion.div>
              ))}
            </div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 2.5 }}
              className="bg-gradient-to-br from-orange-500 to-amber-500 rounded-2xl p-5 text-white text-center"
            >
              <p className="text-white/90 text-xs uppercase tracking-widest font-bold mb-1">📚 Prochaines étapes</p>
              <p className="text-white text-base font-bold leading-snug">
                Tu vas apprendre <span className="font-black">le système</span><br/>
                puis recevoir <span className="font-black">ton plan 30 jours</span>.
              </p>
              <p className="text-white/80 text-xs mt-2">Continue avec « Suivant »</p>
            </motion.div>
          </div>
        );
      })()
    },

    // SCREEN 6 - DIAGNOSTIC RISQUE — Es-tu en danger ?
    {
      id: 'risk-diag',
      title: 'ES-TU EN DANGER ?',
      content: (() => {
        const r = userInputs.riskAnswers || {};
        const allAnswered = ['cash', 'charges', 'tendance', 'dependance', 'sommeil'].every(k => r[k] !== undefined);
        let score = 0;
        if (r.cash === 'no') score += 30;
        else if (r.cash === 'partial') score += 15;
        if (r.charges === 'late') score += 25;
        else if (r.charges === 'tight') score += 12;
        if (r.tendance === 'down') score += 20;
        else if (r.tendance === 'flat') score += 8;
        if (r.dependance === 'yes') score += 15;
        if (r.sommeil === 'bad') score += 10;
        const level = score >= 50 ? 'red' : score >= 25 ? 'orange' : 'green';
        const levelData = {
          red: { label: 'ZONE ROUGE — survie', color: '#dc2626', bg: 'from-red-50 to-rose-50', border: 'border-red-300', text: 'text-red-700', msg: 'Ta priorité absolue : stopper l\'hémorragie cette semaine. La prochaine étape (Stop Hémorragie) est faite POUR TOI.' },
          orange: { label: 'ZONE ORANGE — fragile', color: '#f59e0b', bg: 'from-orange-50 to-amber-50', border: 'border-orange-300', text: 'text-orange-700', msg: 'Pas critique mais attention. Applique d\'abord les actions Stop Hémorragie (S0), puis le système classique.' },
          green: { label: 'ZONE VERTE — solide', color: '#16a34a', bg: 'from-green-50 to-emerald-50', border: 'border-green-300', text: 'text-green-700', msg: 'Tu es dans une bonne situation. Passe direct au système d\'optimisation (S1 → S4).' },
        };
        const ld = levelData[level];
        return (
          <div className="space-y-6">
            <div>
              <p className="text-red-600 text-xs font-bold uppercase tracking-widest mb-3">⚠️ Diagnostic préalable</p>
              <h2 className="font-['Playfair_Display',serif] text-3xl md:text-4xl font-black text-[#1a1a1a] leading-[1.05]">
                Es-tu <span className="text-red-600">en danger ?</span>
              </h2>
              <p className="text-[#1a1a1a]/60 text-sm mt-2">5 questions vite répondues. Honnête uniquement.</p>
            </div>

            {[
              { q: 'cash', label: '1. As-tu 3 mois de charges en cash dispo ?', opts: [{v:'yes',l:'Oui'},{v:'partial',l:'1-2 mois'},{v:'no',l:'Moins d\'1 mois'}] },
              { q: 'charges', label: '2. Tu paies tes charges (URSSAF, fournisseurs) à l\'heure ?', opts: [{v:'on_time',l:'Oui, à l\'heure'},{v:'tight',l:'Juste à temps'},{v:'late',l:'En retard / négocié'}] },
              { q: 'tendance', label: '3. Ton CA des 3 derniers mois ?', opts: [{v:'up',l:'En hausse'},{v:'flat',l:'Stable'},{v:'down',l:'En baisse'}] },
              { q: 'dependance', label: '4. Tu dépends d\'1 seule source de CA (salle uniquement) ?', opts: [{v:'no',l:'Non, multi-canaux'},{v:'partial',l:'Surtout 1'},{v:'yes',l:'Oui, 100% salle'}] },
              { q: 'sommeil', label: '5. Tu dors bien la nuit ?', opts: [{v:'good',l:'Oui'},{v:'mid',l:'Bof'},{v:'bad',l:'Insomnies / stress'}] },
            ].map((qq) => (
              <div key={qq.q}>
                <p className="text-[#1a1a1a] font-bold text-sm mb-2">{qq.label}</p>
                <div className="flex gap-2 flex-wrap">
                  {qq.opts.map((o) => (
                    <button
                      key={o.v}
                      onClick={() => setUserInputs({...userInputs, riskAnswers: {...r, [qq.q]: o.v}})}
                      className={`px-3 py-2 rounded-xl text-xs font-bold transition flex-1 ${
                        r[qq.q] === o.v
                          ? 'bg-orange-500 text-white border-2 border-orange-500'
                          : 'bg-white text-[#1a1a1a] border-2 border-[#e0dbd4] hover:border-orange-300'
                      }`}
                    >
                      {o.l}
                    </button>
                  ))}
                </div>
              </div>
            ))}

            {allAnswered && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className={`bg-gradient-to-br ${ld.bg} rounded-2xl p-5 border-2 ${ld.border}`}
              >
                <p className={`${ld.text} text-xs font-black uppercase tracking-wider mb-2`}>Ton score : {score}/100</p>
                <p className="text-2xl font-black text-[#1a1a1a]">{ld.label}</p>
                <p className="text-[#1a1a1a]/75 text-sm mt-3 leading-relaxed">{ld.msg}</p>
              </motion.div>
            )}
          </div>
        );
      })()
    },

    // SCREEN 7 - STOP HÉMORRAGIE — Semaine 0
    {
      id: 'stop-hemorragie',
      title: 'STOP HÉMORRAGIE',
      content: (
        <div className="space-y-6">
          <div>
            <p className="text-red-600 text-xs font-bold uppercase tracking-widest mb-3">🚨 Semaine 0 · Avant tout le reste</p>
            <h2 className="font-['Playfair_Display',serif] text-3xl md:text-4xl font-black text-[#1a1a1a] leading-[1.05]">
              7 actions à faire <span className="text-red-600">avant lundi.</span>
            </h2>
            <p className="text-[#1a1a1a]/60 text-sm mt-2">Du cash immédiat. De l'oxygène. Du temps.</p>
          </div>

          <div className="bg-red-50 rounded-2xl p-4 border border-red-200">
            <p className="text-red-700 text-sm font-bold leading-snug">
              ⚠️ Si tu es en zone rouge, tu commences ici. Le reste du livre vient après.
            </p>
          </div>

          <div className="space-y-3">
            {[
              { n: '01', t: 'Liste TOUTES tes charges fixes', d: 'Loyer, salaires, URSSAF, abonnements, assurances. Identifie les 3 plus grosses.', impact: 'Tu vois où l\'argent fuit' },
              { n: '02', t: 'Appelle TON proprio — négocie 1 mois de loyer', d: 'Script : "Bonjour, je traverse une période difficile. Je voudrais étaler le loyer du mois X sur 3 mois. Acceptez-vous ?"', impact: '−2000 à −4000€ ce mois' },
              { n: '03', t: 'Renégocie tes fournisseurs sur les délais', d: 'Demande passage à 45j au lieu de 15j. Un fournisseur = un appel = du cash de tréso.', impact: '+1 mois de cash' },
              { n: '04', t: 'Stop tous les abonnements inutiles', d: 'Logiciels que tu n\'utilises pas, livraisons d\'eau, options de communication. Liste + résiliation par téléphone.', impact: '200-500€/mois' },
              { n: '05', t: 'Retire de la carte les 3 plats qui ne se vendent pas', d: 'Ils stockent. Ils périment. Ils gaspillent. Cette semaine.', impact: 'Stop le gaspi' },
              { n: '06', t: 'Augmente 5 plats de +1 à +2€', d: 'Tes best-sellers. Personne ne va arrêter de venir pour 1€. Mais ça change ton mois.', impact: '+800 à +1500€/mois' },
              { n: '07', t: 'Active un canal de vente immédiat', d: 'Click & Collect avec code -10% OU Uber Eats avec offre. CA AVANT le rush.', impact: 'CA payé d\'avance' },
            ].map((s, i) => (
              <motion.div
                key={s.n}
                initial={{ opacity: 0, x: -10 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
                className="bg-white rounded-2xl p-4 border border-[#e0dbd4]"
              >
                <div className="flex items-start gap-3">
                  <span className="font-['Playfair_Display',serif] font-black text-3xl text-red-600 leading-none flex-shrink-0">{s.n}</span>
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-sm text-[#1a1a1a]">{s.t}</p>
                    <p className="text-xs text-[#1a1a1a]/60 mt-1 leading-snug">{s.d}</p>
                    <div className="mt-2 inline-flex items-center gap-1.5 bg-green-50 border border-green-200 rounded-lg px-2 py-1">
                      <TrendingUp className="w-3 h-3 text-green-700" />
                      <span className="text-green-700 text-[11px] font-bold">{s.impact}</span>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          <div className="bg-gradient-to-br from-orange-500 to-red-500 rounded-2xl p-5 text-white">
            <p className="text-white/90 text-xs font-bold uppercase tracking-widest mb-2">🎯 Objectif Semaine 0</p>
            <p className="text-2xl font-black leading-tight">+3 000 à +6 000€ de cash<br/>débloqués cette semaine.</p>
            <p className="text-white/80 text-xs mt-2">Sans changer ton menu. Sans plus de clients. Juste des décisions.</p>
          </div>
        </div>
      )
    },

    // SCREEN 8 - TEMPLATES TÉLÉCHARGEABLES
    {
      id: 'templates',
      title: 'TES TEMPLATES',
      content: (() => {
        const handleCopy = async (text, key) => {
          try {
            await navigator.clipboard.writeText(text);
            toggleAction('template', key);
          } catch (e) { console.error(e); }
        };
        const tplKpi = `Semaine | CA | Couverts | Panier moyen | Food cost % | Masse salariale % | Avis Google (note) | Avis Google (nb) | Cash banque
S1      |    |          |              |             |                    |                    |                  |
S2      |    |          |              |             |                    |                    |                  |
S3      |    |          |              |             |                    |                    |                  |
S4      |    |          |              |             |                    |                    |                  |`;
        const tplFournisseur = `Bonjour [Nom du fournisseur],

Je suis [Ton nom], gérant du restaurant [Nom du resto].

Je vous écris pour vous proposer une révision de nos conditions commerciales :

1. Allongement du délai de paiement de 15 à 45 jours
2. Application d'une remise volume de [X]% sur nos commandes mensuelles (>[montant]€)
3. Franco de port à partir de [montant]€

Cette demande s'inscrit dans une volonté de pérenniser notre collaboration sur le long terme.

Je reste disponible pour en échanger par téléphone ou en visio cette semaine.

Cordialement,
[Ton nom] — [Restaurant]
[Téléphone] — [Email]`;
        const tplBriefing = `BRIEFING ÉQUIPE — 10 MIN AVANT CHAQUE SERVICE
====================================================

⏱ 2 min · PLATS DU JOUR
- Le chef présente. L'équipe goûte si possible.

⏱ 1 min · RUPTURES & 86
- Ce qui est en rupture ou retiré du service.

⏱ 3 min · OBJECTIF DU SERVICE
- Ex : pousser la formule du soir
- Ex : 10 entrées vendues
- Ex : +2€ de panier moyen
[Choisis 1 OBJECTIF par service. Annonce-le clairement.]

⏱ 2 min · UN POSITIF DE LA VEILLE
- Ce qui a bien marché.
- Cite 1 nom (renforce la motivation).

⏱ 2 min · QUI FAIT QUOI
- Runner / bar / terrasse / rangs.
- Zéro ambiguïté.

📌 Règle : non négociable. Tous les jours. Avant le service.`;
        return (
          <div className="space-y-6">
            <div>
              <p className="text-orange-600 text-xs font-bold uppercase tracking-widest mb-3">📥 Tes templates prêts à l'emploi</p>
              <h2 className="font-['Playfair_Display',serif] text-3xl md:text-4xl font-black text-[#1a1a1a] leading-[1.05]">
                3 outils. <span className="text-orange-500">Copier. Coller. Utiliser.</span>
              </h2>
              <p className="text-[#1a1a1a]/60 text-sm mt-2">Les fichiers prêts que j'utilise chez Maison Avocado.</p>
            </div>

            {/* Template 1 — KPI tracker */}
            <div className="bg-white rounded-2xl p-5 border border-[#e0dbd4]">
              <div className="flex items-start gap-3 mb-3">
                <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center flex-shrink-0">
                  <Calculator className="w-5 h-5 text-blue-700" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-base text-[#1a1a1a]">Tableau KPIs hebdo</p>
                  <p className="text-xs text-[#1a1a1a]/60">9 colonnes. 1 ligne par semaine. 10 min de saisie le lundi.</p>
                </div>
              </div>
              <div className="bg-[#f0ede8] rounded-lg p-3 mb-3 overflow-x-auto">
                <pre className="text-[10px] text-[#1a1a1a]/80 font-mono whitespace-pre">{tplKpi}</pre>
              </div>
              <button
                onClick={() => handleCopy(tplKpi, 'kpi')}
                className={`w-full py-3 rounded-xl font-bold text-sm transition ${
                  completedActions['template-kpi']
                    ? 'bg-green-500 text-white'
                    : 'bg-orange-500 text-white hover:bg-orange-600'
                }`}
              >
                {completedActions['template-kpi'] ? '✓ Copié — colle dans Google Sheets' : '📋 Copier le template KPIs'}
              </button>
            </div>

            {/* Template 2 — Lettre fournisseur */}
            <div className="bg-white rounded-2xl p-5 border border-[#e0dbd4]">
              <div className="flex items-start gap-3 mb-3">
                <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center flex-shrink-0">
                  <BookOpen className="w-5 h-5 text-purple-700" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-base text-[#1a1a1a]">Lettre négociation fournisseur</p>
                  <p className="text-xs text-[#1a1a1a]/60">Mail prêt à envoyer. Délai 45j + remise + franco de port.</p>
                </div>
              </div>
              <div className="bg-[#f0ede8] rounded-lg p-3 mb-3 max-h-32 overflow-y-auto">
                <pre className="text-[10px] text-[#1a1a1a]/80 font-mono whitespace-pre-wrap">{tplFournisseur}</pre>
              </div>
              <button
                onClick={() => handleCopy(tplFournisseur, 'fournisseur')}
                className={`w-full py-3 rounded-xl font-bold text-sm transition ${
                  completedActions['template-fournisseur']
                    ? 'bg-green-500 text-white'
                    : 'bg-orange-500 text-white hover:bg-orange-600'
                }`}
              >
                {completedActions['template-fournisseur'] ? '✓ Copié — colle dans ton mail' : '📋 Copier la lettre'}
              </button>
            </div>

            {/* Template 3 — Briefing 10 min */}
            <div className="bg-white rounded-2xl p-5 border border-[#e0dbd4]">
              <div className="flex items-start gap-3 mb-3">
                <div className="w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center flex-shrink-0">
                  <Users className="w-5 h-5 text-green-700" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-base text-[#1a1a1a]">Script briefing 10 min</p>
                  <p className="text-xs text-[#1a1a1a]/60">Imprime, affiche en cuisine. Lis-le chaque jour.</p>
                </div>
              </div>
              <div className="bg-[#f0ede8] rounded-lg p-3 mb-3 max-h-32 overflow-y-auto">
                <pre className="text-[10px] text-[#1a1a1a]/80 font-mono whitespace-pre-wrap">{tplBriefing}</pre>
              </div>
              <button
                onClick={() => handleCopy(tplBriefing, 'briefing')}
                className={`w-full py-3 rounded-xl font-bold text-sm transition ${
                  completedActions['template-briefing']
                    ? 'bg-green-500 text-white'
                    : 'bg-orange-500 text-white hover:bg-orange-600'
                }`}
              >
                {completedActions['template-briefing'] ? '✓ Copié — imprime-le' : '📋 Copier le script'}
              </button>
            </div>

            <div className="bg-orange-50 rounded-xl p-4 border border-orange-200">
              <p className="text-orange-700 text-sm font-bold mb-1">💡 Pro tip</p>
              <p className="text-[#1a1a1a]/80 text-xs leading-relaxed">
                Crée un dossier "PRSNL — Mon resto" sur ton ordi. Mets-y les 3 fichiers. Reviens-y chaque lundi.
              </p>
            </div>
          </div>
        );
      })()
    },

    // SCREEN — GLOSSAIRE / VOCABULAIRE DE BASE (pour ceux qui partent de zéro)
    {
      id: 'glossaire',
      title: 'LE VOCABULAIRE',
      content: (
        <div className="space-y-6">
          <div>
            <p className="text-orange-600 text-xs font-bold uppercase tracking-widest mb-3">📖 Avant de continuer</p>
            <h2 className="font-['Playfair_Display',serif] text-3xl md:text-4xl font-black text-[#1a1a1a] leading-[1.05]">
              Les <span className="text-orange-500">8 mots</span><br/>que tout patron<br/>doit connaître.
            </h2>
            <p className="text-[#1a1a1a]/60 text-sm mt-3 leading-relaxed">
              Pas besoin d'être expert-comptable. Mais ces mots, tu DOIS savoir ce qu'ils veulent dire. Sinon, tu navigues à l'aveugle.<br/>
              <span className="text-[#1a1a1a]/50 italic">Garde cette page en favori. Reviens-y quand tu veux.</span>
            </p>
          </div>

          <div className="bg-blue-50 rounded-2xl p-4 border border-blue-200">
            <p className="text-blue-700 text-sm font-bold leading-snug">
              💡 90% des restaurateurs en galère ne connaissent pas ces termes. Quand tu les maîtrises, tu rentres dans les 10% qui pilotent vraiment.
            </p>
          </div>

          {[
            {
              term: 'CA (Chiffre d\'affaires)',
              short: 'L\'argent que tu encaisses',
              long: 'Le total des ventes (TTC ou HT) sur une période donnée. C\'est ton « combien tu rentres ». Ne pas confondre avec ce que tu gagnes.',
              ex: 'Tu vends pour 30 000€ ce mois → CA = 30 000€.',
              why: 'C\'est la base. Tout se calcule en % du CA.',
              color: 'orange',
            },
            {
              term: 'Food Cost',
              short: 'Combien tu dépenses en matières',
              long: 'Le coût des matières premières (cuisine + bar) en pourcentage du CA. Plus c\'est bas, plus tu gardes de marge.',
              ex: 'Tu vends un burger 14€, il te coûte 4,20€ en ingrédients → food cost = 30%.',
              why: 'Cible : 28-32% en restauration classique. Si tu dépasses 35%, tu perds de l\'argent sur chaque plat.',
              color: 'amber',
            },
            {
              term: 'Masse Salariale (MS)',
              short: 'Combien tu paies tes équipes',
              long: 'Salaires + charges patronales en pourcentage du CA. Si tu es non salarié, ajoute aussi ta « rémunération théorique ».',
              ex: 'Tu paies 10 500€ de salaires/charges, ton CA est 30 000€ → MS = 35%.',
              why: 'Cible : 28-35%. Au-dessus, ton équipe te coûte trop par rapport à ton CA.',
              color: 'blue',
            },
            {
              term: 'Prime Cost',
              short: 'Food Cost + Masse Salariale',
              long: 'Tes 2 plus gros postes additionnés. C\'est LE chiffre n°1 à surveiller.',
              ex: '30% (FC) + 35% (MS) = Prime Cost de 65%.',
              why: 'Cible : MAX 65%. Au-dessus, il ne te reste pas assez pour le loyer, les charges, et toi.',
              color: 'red',
            },
            {
              term: 'Charges fixes',
              short: 'Ce que tu paies tous les mois',
              long: 'Les dépenses qui tombent même si tu fais 0€ de CA : loyer, salaires fixes, abonnements, assurances, URSSAF gérant.',
              ex: 'Loyer 4 000€ + salaires fixes 8 000€ + abonnements 500€ = 12 500€/mois.',
              why: 'Tu dois les couvrir AVANT de penser à autre chose.',
              color: 'gray',
            },
            {
              term: 'Charges variables',
              short: 'Ce qui dépend de ton CA',
              long: 'Achats matières, intérim, livraison, % plateformes (Uber Eats, Deliveroo). Plus tu vends, plus elles montent.',
              ex: 'Pour 30 000€ de CA : 9 000€ achats + 1 500€ Uber Eats = 10 500€.',
              why: 'Tu dois les optimiser plat par plat (pas en moyenne).',
              color: 'purple',
            },
            {
              term: 'Break-Even (Seuil de rentabilité)',
              short: 'Le minimum pour ne pas perdre',
              long: 'Le CA mensuel minimum pour couvrir TOUTES tes dépenses. En dessous, tu perds de l\'argent. Au-dessus, tu gagnes.',
              ex: 'Charges fixes 12 500€ + variables prévues 10 500€ = Break-Even 23 000€.',
              why: 'Si tu ne connais pas ce chiffre, tu ne sais pas si tu travailles à perte ou non.',
              color: 'indigo',
            },
            {
              term: 'EBE (Excédent Brut d\'Exploitation)',
              short: 'Ce que ton resto dégage VRAIMENT',
              long: 'CA − Achats − Salaires − Loyer − Charges externes. C\'est la richesse créée par ton activité avant impôts et amortissements.',
              ex: 'CA 30 000€ − (achats 9 000 + salaires 10 500 + loyer 4 000 + autres 3 000) = EBE de 3 500€.',
              why: 'Cible : EBE positif et > 8% du CA. C\'est l\'indicateur n°1 de la santé de ton resto.',
              color: 'green',
            },
            {
              term: 'Trésorerie ≠ Bénéfice',
              short: 'Le cash ≠ ce que tu gagnes',
              long: 'La trésorerie = ce qui est SUR TON COMPTE maintenant. Le bénéfice = ce que tu gagnes en théorie sur le papier.',
              ex: 'Tu peux faire 5 000€ de bénéf et avoir -2 000€ en banque (TVA + URSSAF tombées la même semaine).',
              why: 'Garde 3 mois de charges fixes en cash, toujours. La tréso, c\'est l\'oxygène.',
              color: 'cyan',
            },
          ].map((item) => {
            const colorMap = {
              orange: { bg: 'bg-orange-50', border: 'border-orange-200', text: 'text-orange-700', dot: 'bg-orange-500' },
              amber: { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700', dot: 'bg-amber-500' },
              blue: { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-700', dot: 'bg-blue-500' },
              red: { bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-700', dot: 'bg-red-500' },
              gray: { bg: 'bg-[#f0ede8]', border: 'border-[#d4cfc7]', text: 'text-[#1a1a1a]', dot: 'bg-[#666]' },
              purple: { bg: 'bg-purple-50', border: 'border-purple-200', text: 'text-purple-700', dot: 'bg-purple-500' },
              indigo: { bg: 'bg-indigo-50', border: 'border-indigo-200', text: 'text-indigo-700', dot: 'bg-indigo-500' },
              green: { bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-700', dot: 'bg-green-500' },
              cyan: { bg: 'bg-cyan-50', border: 'border-cyan-200', text: 'text-cyan-700', dot: 'bg-cyan-500' },
            };
            const c = colorMap[item.color];
            return (
              <div key={item.term} className={`${c.bg} rounded-2xl p-5 border ${c.border}`}>
                <div className="flex items-start gap-3 mb-3">
                  <div className={`w-2.5 h-2.5 rounded-full ${c.dot} flex-shrink-0 mt-1.5`} />
                  <div className="flex-1 min-w-0">
                    <p className="text-[#1a1a1a] font-black text-base leading-tight">{item.term}</p>
                    <p className={`${c.text} text-xs font-bold uppercase tracking-wider mt-0.5`}>{item.short}</p>
                  </div>
                </div>
                <p className="text-[#1a1a1a]/85 text-sm leading-relaxed mb-2.5">{item.long}</p>
                <div className="bg-white/70 rounded-lg p-3 border border-white mb-2.5">
                  <p className="text-[#1a1a1a]/50 text-xs font-bold uppercase tracking-wider mb-1">📊 Exemple</p>
                  <p className="text-[#1a1a1a]/85 text-sm leading-snug">{item.ex}</p>
                </div>
                <div className="flex items-start gap-1.5">
                  <span className="text-base flex-shrink-0">⚡</span>
                  <p className="text-[#1a1a1a] text-xs font-bold leading-snug">{item.why}</p>
                </div>
              </div>
            );
          })}

          <div className="bg-gradient-to-br from-orange-500 to-amber-500 rounded-2xl p-5 text-white">
            <p className="text-white/90 text-xs font-bold uppercase tracking-widest mb-2">🎯 À retenir</p>
            <p className="text-xl font-black leading-tight">Tu connais ces 8 mots ?<br/>Tu es déjà devant 90% des restaurateurs.</p>
            <p className="text-white/85 text-sm mt-2 leading-relaxed">Maintenant on peut passer aux choses sérieuses : le système qui transforme ton resto en machine à cash.</p>
          </div>
        </div>
      )
    },

    // SCREEN 9 - AUTO-DIAGNOSTIC
    {
      id: 'diagnostic',
      title: 'AUTO-DIAGNOSTIC',
      content: (
        <div className="space-y-6">
          <h2 className="text-3xl font-black text-[#1a1a1a]">🧠 Auto-diagnostic rapide</h2>
          <p className="text-[#1a1a1a]/70">Coche ce qui te correspond :</p>
          
          <div className="space-y-3">
            {[
              { id: 'no-strategy', label: "Je n'ai pas de stratégie claire pour attirer des clients" },
              { id: 'team-no-sell', label: "Mon équipe ne vend pas activement" },
              { id: 'no-basket', label: "Je ne connais pas mon panier moyen exact" },
              { id: 'depend-traffic', label: "Mon restaurant dépend du passage" },
              { id: 'indispensable', label: "Je suis indispensable au quotidien" }
            ].map((item) => (
              <button
                key={item.id}
                onClick={() => toggleAction('diag', item.id)}
                className={`w-full p-4 rounded-xl text-left flex items-center gap-3 transition-all ${
                  completedActions[`diag-${item.id}`]
                    ? 'bg-orange-500/20 border-2 border-orange-500'
                    : 'bg-white border border-[#e0dbd4] border-2 border-transparent hover:border-[#d4cfc7]'
                }`}
              >
                <div className={`w-6 h-6 rounded-lg flex items-center justify-center ${
                  completedActions[`diag-${item.id}`] ? 'bg-orange-500' : 'bg-[#e0dbd4]'
                }`}>
                  {completedActions[`diag-${item.id}`] && <Check className="w-4 h-4 text-[#1a1a1a]" />}
                </div>
                <span className="text-[#1a1a1a]/90">{item.label}</span>
              </button>
            ))}
          </div>
          
          {/* Result */}
          <div className="bg-gradient-to-r from-red-500/20 to-orange-500/20 rounded-xl p-4 border border-red-500/30">
            <p className="text-sm text-[#1a1a1a]/70 mb-2">🔥 Résultat :</p>
            <div className="flex gap-4 text-sm">
              <span className="text-green-600">0-1 = OK</span>
              <span className="text-yellow-600">2-3 = Fragile</span>
              <span className="text-red-600">4-5 = ⚠️ Danger</span>
            </div>
          </div>
        </div>
      )
    },
    
    // SCREEN 2 - REALITE
    {
      id: 'realite',
      title: 'RÉALITÉ',
      content: (
        <div className="space-y-8">
          <h2 className="text-3xl font-black text-[#1a1a1a]">
            Ton restaurant ne manque pas d'efforts.<br/>
            <span className="text-red-500">Il manque de structure.</span>
          </h2>
          
          <div className="bg-white border border-[#e0dbd4] rounded-2xl p-6 border border-[#e0dbd4]">
            <p className="text-[#1a1a1a]/70 mb-4">👉 Passe à l'action maintenant :</p>
            <p className="text-lg text-[#1a1a1a] mb-4">Écris en 1 phrase :</p>
            <p className="text-orange-500 font-bold mb-4">"Pourquoi un client vient chez moi ?"</p>
            
            <textarea
              value={userInputs.whyClientComes}
              onChange={(e) => setUserInputs(prev => ({ ...prev, whyClientComes: e.target.value }))}
              placeholder="Ta réponse..."
              className="w-full p-4 rounded-xl bg-[#e0dbd4] text-[#1a1a1a] border-2 border-[#e0dbd4] focus:border-orange-500 outline-none resize-none h-24"
            />
            
            {!userInputs.whyClientComes && (
              <p className="text-red-600 text-sm mt-2">⚠️ Si tu hésites → gros problème</p>
            )}
          </div>
        </div>
      )
    },
    
    // SCREEN 3 - LE SYSTEME (4 PILIERS)
    {
      id: 'systeme',
      title: 'LE SYSTÈME',
      content: (
        <div className="space-y-6">
          <h2 className="text-3xl font-black text-[#1a1a1a]">⚙️ Le Système (4 Piliers)</h2>
          
          <div className="grid grid-cols-2 gap-4">
            {[
              { icon: '🔵', name: 'ATTIRER', color: 'blue', desc: 'Générer du flux' },
              { icon: '🟢', name: 'CONVERTIR', color: 'green', desc: 'Plus de CA/client' },
              { icon: '🟡', name: 'FIDÉLISER', color: 'yellow', desc: 'Faire revenir' },
              { icon: '🔴', name: 'STRUCTURER', color: 'red', desc: 'Ne plus être indispensable' }
            ].map((pilier, i) => (
              <motion.button
                key={i}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: i * 0.1 }}
                onClick={() => {
                  const targetId = ['attirer', 'convertir', 'fideliser', 'structurer'][i];
                  const idx = screens.findIndex(s => s.id === targetId);
                  if (idx !== -1) setCurrentScreen(idx);
                }}
                className={`p-6 rounded-2xl border-2 hover:scale-[1.02] transition-all text-left`}
                style={{ 
                  backgroundColor: pilier.color === 'blue' ? 'rgba(59,130,246,0.1)' : 
                                   pilier.color === 'green' ? 'rgba(16,185,129,0.1)' :
                                   pilier.color === 'yellow' ? 'rgba(245,158,11,0.1)' :
                                   'rgba(239,68,68,0.1)',
                  borderColor: pilier.color === 'blue' ? 'rgba(59,130,246,0.3)' : 
                               pilier.color === 'green' ? 'rgba(16,185,129,0.3)' :
                               pilier.color === 'yellow' ? 'rgba(245,158,11,0.3)' :
                               'rgba(239,68,68,0.3)'
                }}
              >
                <span className="text-3xl">{pilier.icon}</span>
                <h3 className="text-[#1a1a1a] font-bold mt-2">{pilier.name}</h3>
                <p className="text-[#1a1a1a]/60 text-sm">{pilier.desc}</p>
              </motion.button>
            ))}
          </div>
        </div>
      )
    },
    
    // SCREEN 4 - ATTIRER
    {
      id: 'attirer',
      title: 'ATTIRER',
      content: (
        <div className="space-y-6">
          <div className="flex items-center gap-3">
            <span className="text-4xl">🔵</span>
            <h2 className="text-3xl font-black text-[#1a1a1a]">ATTIRER</h2>
          </div>
          <p className="text-blue-600">👉 Objectif : générer du flux</p>
          
          <div className="bg-blue-500/10 rounded-2xl p-6 border border-blue-500/30">
            <p className="text-[#1a1a1a] font-bold mb-4">🎬 Action immédiate :</p>
            <p className="text-[#1a1a1a]/80 mb-4">Filme ça aujourd'hui :</p>
            
            <div className="space-y-2 mb-4">
              {['Cuisine en action', 'Dressage d\'un plat', 'Client satisfait'].map((item, i) => (
                <button
                  key={i}
                  onClick={() => toggleAction('attirer', `film-${i}`)}
                  className={`w-full p-3 rounded-xl text-left flex items-center gap-3 ${
                    completedActions[`attirer-film-${i}`]
                      ? 'bg-blue-500/30 border border-blue-500'
                      : 'bg-white border border-[#e0dbd4] border border-transparent'
                  }`}
                >
                  <CheckCircle className={`w-5 h-5 ${
                    completedActions[`attirer-film-${i}`] ? 'text-blue-600' : 'text-[#1a1a1a]/30'
                  }`} />
                  <span className="text-[#1a1a1a]/90">{item}</span>
                </button>
              ))}
            </div>
            
            <div className="bg-white border border-[#e0dbd4] rounded-xl p-4">
              <p className="text-[#1a1a1a]/70 text-sm">👉 Poste avec cette légende :</p>
              <p className="text-[#1a1a1a] font-bold mt-2">"Voilà pourquoi nos clients reviennent"</p>
            </div>
          </div>
        </div>
      )
    },
    
    // SCREEN 5 - CONVERTIR
    {
      id: 'convertir',
      title: 'CONVERTIR',
      content: (
        <div className="space-y-6">
          <div className="flex items-center gap-3">
            <span className="text-4xl">🟢</span>
            <h2 className="text-3xl font-black text-[#1a1a1a]">CONVERTIR</h2>
          </div>
          <p className="text-green-600">👉 Objectif : faire plus de CA par client</p>
          
          <div className="bg-green-500/10 rounded-2xl p-6 border border-green-500/30">
            <p className="text-[#1a1a1a] font-bold mb-4">📝 Exercice à faire CE SOIR :</p>
            <p className="text-[#1a1a1a]/80 mb-4">Donne ce script à ton équipe :</p>
            
            <div className="bg-[#f0ede8] rounded-xl p-4 border-l-4 border-green-500">
              <p className="text-green-700 font-bold italic">
                "Je vous conseille [produit], c'est ce que nos clients prennent le plus."
              </p>
            </div>
            
            <button
              onClick={() => toggleAction('convertir', 'script')}
              className={`w-full mt-4 p-4 rounded-xl flex items-center justify-center gap-2 font-bold transition-all ${
                completedActions['convertir-script']
                  ? 'bg-green-500 text-white'
                  : 'bg-[#e0dbd4] text-[#1a1a1a] hover:bg-[#d4cfc7]'
              }`}
            >
              <Check className="w-5 h-5" />
              {completedActions['convertir-script'] ? 'Script donné à l\'équipe ✓' : 'Marquer comme fait'}
            </button>
          </div>
        </div>
      )
    },
    
    // SCREEN 6 - FIDELISER
    {
      id: 'fideliser',
      title: 'FIDÉLISER',
      content: (
        <div className="space-y-6">
          <div className="flex items-center gap-3">
            <span className="text-4xl">🟡</span>
            <h2 className="text-3xl font-black text-[#1a1a1a]">FIDÉLISER</h2>
          </div>
          <p className="text-yellow-600">👉 Objectif : faire revenir</p>
          
          <div className="bg-yellow-500/10 rounded-2xl p-6 border border-yellow-500/30">
            <p className="text-[#1a1a1a] font-bold mb-4">🎯 Action immédiate :</p>
            
            <div className="space-y-4">
              <div className="bg-white border border-[#e0dbd4] rounded-xl p-4">
                <p className="text-yellow-700 font-bold">1. Demande le prénom du client</p>
              </div>
              <div className="bg-white border border-[#e0dbd4] rounded-xl p-4">
                <p className="text-yellow-700 font-bold">2. Réutilise-le</p>
                <p className="text-[#1a1a1a]/70 mt-2 italic">"Merci [prénom], à très vite !"</p>
              </div>
            </div>
            
            <button
              onClick={() => toggleAction('fideliser', 'prenom')}
              className={`w-full mt-4 p-4 rounded-xl flex items-center justify-center gap-2 font-bold transition-all ${
                completedActions['fideliser-prenom']
                  ? 'bg-yellow-500 text-black'
                  : 'bg-[#e0dbd4] text-[#1a1a1a] hover:bg-[#d4cfc7]'
              }`}
            >
              <Check className="w-5 h-5" />
              {completedActions['fideliser-prenom'] ? 'Technique appliquée ✓' : 'Je vais le faire'}
            </button>
          </div>
        </div>
      )
    },
    
    // SCREEN 7 - STRUCTURER
    {
      id: 'structurer',
      title: 'STRUCTURER',
      content: (
        <div className="space-y-6">
          <div className="flex items-center gap-3">
            <span className="text-4xl">🔴</span>
            <h2 className="text-3xl font-black text-[#1a1a1a]">STRUCTURER</h2>
          </div>
          <p className="text-red-600">👉 Objectif : ne plus être indispensable</p>
          
          <div className="bg-red-500/10 rounded-2xl p-6 border border-red-500/30">
            <p className="text-[#1a1a1a] font-bold mb-4">📊 Routine simple :</p>
            <p className="text-[#1a1a1a]/80 mb-4">Chaque jour, note :</p>
            
            <div className="space-y-3">
              {[
                { label: 'CA du jour', icon: '💰' },
                { label: 'Problème principal', icon: '⚠️' },
                { label: '1 amélioration', icon: '✨' }
              ].map((item, i) => (
                <button
                  key={i}
                  onClick={() => toggleAction('structurer', `routine-${i}`)}
                  className={`w-full p-4 rounded-xl text-left flex items-center gap-3 ${
                    completedActions[`structurer-routine-${i}`]
                      ? 'bg-red-500/30 border border-red-500'
                      : 'bg-white border border-[#e0dbd4] border border-transparent'
                  }`}
                >
                  <span className="text-2xl">{item.icon}</span>
                  <span className="text-[#1a1a1a]/90">{item.label}</span>
                  {completedActions[`structurer-routine-${i}`] && (
                    <Check className="w-5 h-5 text-red-600 ml-auto" />
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>
      )
    },
    
    // SCREEN — BREAK-EVEN
    {
      id: 'breakeven',
      title: 'BREAK-EVEN',
      content: (
        <div className="space-y-6">
          {/* Bloc pédagogique — pour ceux qui partent de zéro */}
          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-5 border-2 border-blue-200">
            <p className="text-blue-700 text-xs font-black uppercase tracking-widest mb-2">📖 C'est quoi le Break-Even ?</p>
            <p className="text-[#1a1a1a] text-base font-bold mb-3 leading-snug">
              Le <span className="text-blue-700">Break-Even</span> (ou « seuil de rentabilité » en français) c'est le CA mensuel <strong>minimum</strong> que tu dois faire pour <strong>NE PAS perdre d'argent</strong>.
            </p>
            <p className="text-[#1a1a1a]/75 text-sm leading-relaxed">
              💡 Tu fais ce chiffre = tu paies tout (loyer, salaires, achats, charges) sans rien gagner ni perdre.<br/>
              💡 Tu fais moins = tu <strong className="text-red-600">perds de l'argent</strong>.<br/>
              💡 Tu fais plus = la différence c'est <strong className="text-green-700">ton bénéfice</strong>.
            </p>
            <div className="mt-3 pt-3 border-t border-blue-200">
              <p className="text-blue-700 text-xs font-bold uppercase tracking-wider mb-1">⚡ Pourquoi c'est crucial ?</p>
              <p className="text-[#1a1a1a]/80 text-sm leading-relaxed">
                Sans ce chiffre tu navigues à l'aveugle. C'est la première ligne dans le tableau de bord d'un patron qui pilote. Ne pas le connaître, c'est comme conduire sans compteur.
              </p>
            </div>
          </div>

          <h2 className="text-3xl font-black text-[#1a1a1a]">💰 Calcule TON Break-Even</h2>
          <p className="text-[#1a1a1a]/70">Le chiffre que tu dois faire pour ne pas perdre d'argent</p>
          
          <div className="bg-gradient-to-r from-purple-500/10 to-blue-500/10 rounded-2xl p-6 border border-purple-500/30">
            <p className="text-[#1a1a1a] font-bold mb-4">🔥 Formule simple :</p>
            <div className="bg-[#f0ede8] rounded-xl p-4 text-center">
              <p className="text-purple-700 font-mono text-lg">
                Break-Even = Charges fixes + Charges variables
              </p>
            </div>
            
            <div className="mt-4 bg-white rounded-xl p-4 border border-[#e0dbd4]">
              <p className="text-[#1a1a1a]/70 text-xs font-bold uppercase tracking-wider mb-2">📌 Pour info</p>
              <p className="text-[#1a1a1a]/80 text-sm leading-snug">
                <strong>Charges fixes</strong> = ce que tu paies CHAQUE MOIS quoi qu'il arrive (loyer, salaires fixes, abonnements, assurances, URSSAF gérant).<br/>
                <strong>Charges variables</strong> = ce qui dépend de ton CA (achats matières, intérim, livraison, % plateformes).
              </p>
            </div>

            <div className="mt-6 space-y-4">
              <p className="text-[#1a1a1a]/70">📊 Exemple :</p>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="bg-white border border-[#e0dbd4] rounded-lg p-3">
                  <p className="text-[#1a1a1a]/50">Charges fixes</p>
                  <p className="text-[#1a1a1a] font-bold">15 000€</p>
                </div>
                <div className="bg-white border border-[#e0dbd4] rounded-lg p-3">
                  <p className="text-[#1a1a1a]/50">Charges variables</p>
                  <p className="text-[#1a1a1a] font-bold">10 000€</p>
                </div>
              </div>
              <div className="bg-purple-500/20 rounded-xl p-4 text-center">
                <p className="text-purple-700">👉 BE = <span className="text-2xl font-black text-[#1a1a1a]">25 000€</span></p>
              </div>
            </div>
            
            <div className="mt-6">
              <p className="text-[#1a1a1a] font-bold mb-2">🧠 Ton Break-Even :</p>
              <input
                type="text"
                value={userInputs.breakEven}
                onChange={(e) => setUserInputs(prev => ({ ...prev, breakEven: e.target.value }))}
                placeholder="Ex: 20000"
                className="w-full p-4 rounded-xl bg-[#e0dbd4] text-[#1a1a1a] border-2 border-[#e0dbd4] focus:border-purple-500 outline-none text-xl font-bold text-center"
              />
              {!userInputs.breakEven && (
                <p className="text-orange-600 text-sm mt-2 text-center">💡 Pas grave si tu ne sais pas — c'est exactement pour ça qu'on est là.</p>
              )}
            </div>
          </div>
        </div>
      )
    },
    
    // SCREEN — PRIME COST
    {
      id: 'primecost',
      title: 'PRIME COST',
      content: (
        <div className="space-y-6">
          {/* Bloc pédagogique */}
          <div className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-2xl p-5 border-2 border-orange-200">
            <p className="text-orange-700 text-xs font-black uppercase tracking-widest mb-2">📖 C'est quoi le Prime Cost ?</p>
            <p className="text-[#1a1a1a] text-base font-bold mb-3 leading-snug">
              Le <span className="text-orange-700">Prime Cost</span> c'est <strong>la somme de tes 2 plus gros postes de dépenses</strong> :
            </p>
            <ul className="text-[#1a1a1a]/85 text-sm space-y-1.5 leading-relaxed mb-3">
              <li>🍅 <strong>Food Cost</strong> = ce que tu dépenses en matières premières (achats cuisine + bar) en % de ton CA</li>
              <li>👨‍🍳 <strong>Masse Salariale</strong> = ce que tu dépenses en salaires + charges en % de ton CA</li>
            </ul>
            <div className="bg-white rounded-lg p-3 border border-orange-200 mb-3">
              <p className="text-[#1a1a1a] font-mono text-sm text-center">
                Prime Cost = Food Cost % + Masse Salariale %
              </p>
            </div>
            <div className="pt-3 border-t border-orange-200">
              <p className="text-orange-700 text-xs font-bold uppercase tracking-wider mb-1">⚡ Pourquoi c'est LE chiffre n°1 à surveiller ?</p>
              <p className="text-[#1a1a1a]/80 text-sm leading-relaxed">
                Parce que ces 2 postes représentent <strong>60-70% de tes dépenses</strong>. Si ton Prime Cost dépasse 65%, tu bosses pour rien : il ne reste plus rien pour le loyer, les charges et toi.
              </p>
            </div>
          </div>

          <h2 className="text-3xl font-black text-[#1a1a1a]">🔥 Calcule TON Prime Cost</h2>
          <p className="text-[#1a1a1a]/70">Le nerf de la guerre</p>
          
          <div className="bg-gradient-to-r from-orange-500/10 to-red-500/10 rounded-2xl p-6 border border-orange-500/30">
            <div className="bg-green-500/20 rounded-xl p-4 text-center mb-6">
              <p className="text-green-700">🎯 Objectif : <span className="text-2xl font-black text-[#1a1a1a]">55% à 65% MAX</span></p>
            </div>

            <div className="bg-white rounded-xl p-4 border border-[#e0dbd4] mb-4">
              <p className="text-[#1a1a1a]/70 text-xs font-bold uppercase tracking-wider mb-2">🧮 Comment calculer en 2 min</p>
              <p className="text-[#1a1a1a]/80 text-sm leading-relaxed">
                <strong>Food Cost %</strong> = (Achats matières du mois ÷ CA du mois) × 100<br/>
                Ex : 9 000€ d'achats / 30 000€ de CA × 100 = <strong>30%</strong><br/><br/>
                <strong>Masse Salariale %</strong> = (Salaires + charges du mois ÷ CA du mois) × 100<br/>
                Ex : 10 500€ de salaires / 30 000€ × 100 = <strong>35%</strong>
              </p>
            </div>
            
            <p className="text-[#1a1a1a] font-bold mb-4">🧠 Calcule le tien :</p>
            
            <div className="space-y-3">
              <div>
                <label className="text-[#1a1a1a]/70 text-sm">Food Cost (%)</label>
                <input
                  type="number"
                  value={userInputs.foodCost}
                  onChange={(e) => setUserInputs(prev => ({ ...prev, foodCost: e.target.value }))}
                  placeholder="Ex: 30"
                  className="w-full p-3 rounded-xl bg-[#e0dbd4] text-[#1a1a1a] border border-[#e0dbd4] focus:border-orange-500 outline-none"
                />
              </div>
              <div>
                <label className="text-[#1a1a1a]/70 text-sm">Masse Salariale (%)</label>
                <input
                  type="number"
                  value={userInputs.staffCost}
                  onChange={(e) => setUserInputs(prev => ({ ...prev, staffCost: e.target.value }))}
                  placeholder="Ex: 35"
                  className="w-full p-3 rounded-xl bg-[#e0dbd4] text-[#1a1a1a] border border-[#e0dbd4] focus:border-orange-500 outline-none"
                />
              </div>
            </div>
            
            {userInputs.foodCost && userInputs.staffCost && (
              <div className={`mt-4 p-4 rounded-xl text-center ${
                parseInt(userInputs.foodCost) + parseInt(userInputs.staffCost) <= 65 
                  ? 'bg-green-500/20 border border-green-500' 
                  : 'bg-red-500/20 border border-red-500'
              }`}>
                <p className="text-[#1a1a1a]/70 text-sm">Ton Prime Cost</p>
                <p className={`text-3xl font-black ${
                  parseInt(userInputs.foodCost) + parseInt(userInputs.staffCost) <= 65 
                    ? 'text-green-600' 
                    : 'text-red-600'
                }`}>
                  {parseInt(userInputs.foodCost) + parseInt(userInputs.staffCost)}%
                </p>
                {parseInt(userInputs.foodCost) + parseInt(userInputs.staffCost) > 65 && (
                  <p className="text-red-600 text-sm mt-2">⚠️ Tu travailles pour rien !</p>
                )}
              </div>
            )}
          </div>
        </div>
      )
    },
    
    // SCREEN 10 - CALCULATEUR INTERACTIF
    {
      id: 'calculateur',
      title: 'CALCULATEUR',
      content: (
        <div className="space-y-6">
          <h2 className="text-3xl font-black text-[#1a1a1a]">💸 Cas Concret</h2>
          
          <div className="bg-gradient-to-r from-green-500/10 to-emerald-500/10 rounded-2xl p-6 border border-green-500/30">
            <p className="text-[#1a1a1a]/70 mb-4">👉 Imagine :</p>
            
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div className="bg-white border border-[#e0dbd4] rounded-xl p-4 text-center">
                <p className="text-[#1a1a1a]/50 text-sm">Clients/jour</p>
                <p className="text-3xl font-black text-[#1a1a1a]">100</p>
              </div>
              <div className="bg-white border border-[#e0dbd4] rounded-xl p-4 text-center">
                <p className="text-[#1a1a1a]/50 text-sm">Panier moyen</p>
                <p className="text-3xl font-black text-[#1a1a1a]">10€</p>
              </div>
            </div>
            
            <div className="bg-[#e0dbd4] rounded-xl p-4 text-center mb-6">
              <p className="text-[#1a1a1a]/50 text-sm">CA actuel</p>
              <p className="text-4xl font-black text-[#1a1a1a]">1000€<span className="text-lg text-[#1a1a1a]/50">/jour</span></p>
            </div>
            
            <div className="bg-green-500/20 rounded-xl p-4 text-center">
              <p className="text-green-700 font-bold mb-2">👉 Avec +2€ de panier moyen :</p>
              <p className="text-5xl font-black text-green-600">1200€<span className="text-lg text-green-700/70">/jour</span></p>
              <div className="mt-4 grid grid-cols-2 gap-4">
                <div className="bg-[#f0ede8] rounded-lg p-3">
                  <p className="text-green-700 text-sm">Par jour</p>
                  <p className="text-xl font-bold text-[#1a1a1a]">+200€</p>
                </div>
                <div className="bg-[#f0ede8] rounded-lg p-3">
                  <p className="text-green-700 text-sm">Par mois</p>
                  <p className="text-xl font-bold text-[#1a1a1a]">+6000€</p>
                </div>
              </div>
            </div>
            
            <div className="mt-6">
              <p className="text-[#1a1a1a] font-bold mb-2">👉 Qu'est-ce que tu peux ajouter pour faire +2€ ?</p>
              <textarea
                value={userInputs.ideaForIncrease}
                onChange={(e) => setUserInputs(prev => ({ ...prev, ideaForIncrease: e.target.value }))}
                placeholder="Écris 1 idée..."
                className="w-full p-4 rounded-xl bg-[#e0dbd4] text-[#1a1a1a] border border-[#e0dbd4] focus:border-green-500 outline-none resize-none h-20"
              />
            </div>
          </div>
        </div>
      )
    },
    
    // SCREEN 11 - PLAN 30 JOURS
    {
      id: 'plan30',
      title: 'PLAN 30 JOURS',
      content: (
        <div className="space-y-6">
          <h2 className="text-3xl font-black text-[#1a1a1a]">⚡ Plan 30 Jours</h2>
          
          <div className="space-y-4">
            {[
              { week: 1, title: 'STRUCTURE', tasks: ['Clarifie ton offre', 'Note tes chiffres', 'Mets une routine'], color: 'red' },
              { week: 2, title: 'TRAFIC', tasks: ['1 vidéo / jour', '1 offre simple'], color: 'blue' },
              { week: 3, title: 'CASH', tasks: ['Upsell systématique', 'Optimisation menu'], color: 'green' },
              { week: 4, title: 'OPTIMISATION', tasks: ['Analyse', 'Ajuste', 'Stabilise'], color: 'purple' }
            ].map((week) => (
              <div 
                key={week.week}
                className="rounded-2xl p-4 border"
                style={{ 
                  backgroundColor: week.color === 'red' ? 'rgba(239,68,68,0.1)' :
                                   week.color === 'blue' ? 'rgba(59,130,246,0.1)' :
                                   week.color === 'green' ? 'rgba(16,185,129,0.1)' :
                                   'rgba(139,92,246,0.1)',
                  borderColor: week.color === 'red' ? 'rgba(239,68,68,0.3)' :
                               week.color === 'blue' ? 'rgba(59,130,246,0.3)' :
                               week.color === 'green' ? 'rgba(16,185,129,0.3)' :
                               'rgba(139,92,246,0.3)'
                }}
              >
                <div className="flex items-center gap-3 mb-3">
                  <span className="text-[#1a1a1a]/50 text-sm">Semaine {week.week}</span>
                  <span className="text-[#1a1a1a] font-bold">{week.title}</span>
                </div>
                <div className="space-y-2">
                  {week.tasks.map((task, i) => (
                    <div key={i} className="flex items-center gap-2 text-[#1a1a1a]/80 text-sm">
                      <div className="w-1.5 h-1.5 rounded-full bg-white border border-[#e0dbd4]0" />
                      {task}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )
    },
    
    // SCREEN 12 - RECRUTEMENT
    {
      id: 'recrutement',
      title: 'RECRUTEMENT',
      content: (
        <div className="space-y-6">
          <h2 className="text-3xl font-black text-[#1a1a1a]">👥 Recruter les bons profils</h2>
          <p className="text-[#1a1a1a]/70">La clé d'une équipe autonome</p>
          
          <div className="bg-blue-500/10 rounded-2xl p-6 border border-blue-500/30">
            <p className="text-blue-700 font-bold mb-4">📋 Questions à poser en entretien :</p>
            
            <div className="space-y-3">
              {[
                { q: "Pourquoi la restauration ?", why: "Teste la passion" },
                { q: "Raconte-moi un problème que tu as résolu seul", why: "Teste l'autonomie" },
                { q: "Comment tu gères un client difficile ?", why: "Teste le relationnel" },
                { q: "Qu'est-ce qui te motive à venir bosser ?", why: "Teste l'engagement" }
              ].map((item, i) => (
                <div key={i} className="bg-white border border-[#e0dbd4] rounded-xl p-3">
                  <p className="text-[#1a1a1a] font-bold text-sm">"{item.q}"</p>
                  <p className="text-blue-700 text-xs mt-1">→ {item.why}</p>
                </div>
              ))}
            </div>
          </div>
          
          <div className="bg-red-500/10 rounded-xl p-4 border border-red-500/30">
            <p className="text-red-700 text-sm font-bold mb-2">🚨 Red flags à éviter :</p>
            <div className="space-y-1 text-xs text-[#1a1a1a]/70">
              <p>• Parle mal de ses anciens employeurs</p>
              <p>• Ne pose aucune question sur le resto</p>
              <p>• Arrive en retard à l'entretien</p>
              <p>• Ne connaît pas ton établissement</p>
            </div>
          </div>
          
          <div className="bg-green-500/10 rounded-xl p-4 border border-green-500/30">
            <p className="text-green-700 text-sm font-bold mb-2">✅ Template annonce efficace :</p>
            <div className="bg-[#f0ede8] rounded-lg p-3 text-xs text-[#1a1a1a]/80 font-mono">
              <p>🍽️ [NOM DU RESTO] recrute !</p>
              <p className="mt-2">On cherche : [POSTE]</p>
              <p>Ce qu'on offre : [AVANTAGES]</p>
              <p>Ce qu'on attend : [QUALITÉS]</p>
              <p className="mt-2">📩 Envoie ton CV + 3 lignes sur pourquoi toi</p>
            </div>
          </div>
        </div>
      )
    },
    
    // SCREEN 13 - PRIMES & MOTIVATION (Avocado system)
    {
      id: 'primes',
      title: 'PRIMES AVOCADO',
      content: (
        <div className="space-y-6">
          <div>
            <p className="text-orange-700 text-xs font-bold uppercase tracking-wider mb-2">Ma méthode Maison Avocado</p>
            <h2 className="text-3xl font-black text-[#1a1a1a] leading-tight">
              Un système de primes <span className="text-orange-500">collectives.</span>
            </h2>
            <p className="text-[#1a1a1a]/60 text-sm mt-2">Chez Maison Avocado, j'applique ce système. Ça marche. Je te le donne.</p>
          </div>

          <div className="bg-gradient-to-br from-yellow-50 to-orange-50 rounded-2xl p-5 border border-yellow-200">
            <div className="flex items-center gap-2 mb-3">
              <Star className="w-5 h-5 text-yellow-600 fill-yellow-500" />
              <h3 className="text-yellow-700 font-bold">Prime Avis Google</h3>
            </div>
            <p className="text-[#1a1a1a]/80 text-sm mb-3">Objectif : 35 avis Google 5 étoiles dans le mois.</p>
            <div className="bg-white rounded-xl p-3 border border-yellow-200">
              <p className="text-yellow-700 font-black text-lg">50€ par personne</p>
              <p className="text-[#1a1a1a]/60 text-xs">Si objectif atteint, prime collective partagée.</p>
            </div>
          </div>

          <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-5 border border-green-200">
            <div className="flex items-center gap-2 mb-3">
              <TrendingUp className="w-5 h-5 text-green-600" />
              <h3 className="text-green-700 font-bold">Primes CA par paliers</h3>
            </div>
            <p className="text-[#1a1a1a]/80 text-sm mb-3">Primes par personne, à atteindre collectivement sur le CA mensuel :</p>
            <div className="space-y-2">
              <div className="flex items-center justify-between bg-white rounded-xl p-3 border border-green-100">
                <div>
                  <p className="text-[#1a1a1a] font-bold text-sm">CA 55 000€</p>
                  <p className="text-[#1a1a1a]/50 text-xs">Palier 1</p>
                </div>
                <span className="text-green-700 font-black">150€ / pers</span>
              </div>
              <div className="flex items-center justify-between bg-white rounded-xl p-3 border border-green-200">
                <div>
                  <p className="text-[#1a1a1a] font-bold text-sm">CA 65 000€</p>
                  <p className="text-[#1a1a1a]/50 text-xs">Palier 2</p>
                </div>
                <span className="text-green-700 font-black">200€ / pers</span>
              </div>
              <div className="flex items-center justify-between bg-white rounded-xl p-3 border-2 border-green-400">
                <div>
                  <p className="text-[#1a1a1a] font-bold text-sm">CA 70 000€+</p>
                  <p className="text-green-700 text-xs font-bold">🔥 Palier max</p>
                </div>
                <span className="text-green-700 font-black text-lg">250€ / pers</span>
              </div>
            </div>
            <p className="text-[#1a1a1a]/60 text-xs mt-3">👉 Sous le palier 1 : 0€ de prime. L'équipe sait exactement ce qu'elle joue chaque mois.</p>
          </div>

          <div className="bg-white rounded-xl p-4 border border-[#e0dbd4]">
            <p className="text-[#1a1a1a] font-bold text-sm mb-2">💡 Pourquoi ce système marche :</p>
            <div className="space-y-1 text-xs text-[#1a1a1a]/70">
              <p>• <span className="font-bold">Collectif</span> : l'équipe s'auto-motive. Plus de passagers.</p>
              <p>• <span className="font-bold">Par paliers</span> : pas de "tout ou rien" flou. Chaque niveau est clair.</p>
              <p>• <span className="font-bold">Visible</span> : le CA du jour est affiché. Ils voient la prime se rapprocher.</p>
              <p>• <span className="font-bold">Rentable</span> : +15-25K de CA vs 750-1250€ de prime = ROI massif.</p>
            </div>
          </div>

          <button
            onClick={() => toggleAction('primes', 'system')}
            className={`w-full p-4 rounded-xl flex items-center justify-center gap-2 font-bold transition-all ${
              completedActions['primes-system']
                ? 'bg-green-500 text-white'
                : 'bg-[#e0dbd4] text-[#1a1a1a] hover:bg-[#d4cfc7]'
            }`}
          >
            <Check className="w-5 h-5" />
            {completedActions['primes-system'] ? 'Système de primes créé ✓' : 'Je mets en place les paliers'}
          </button>
        </div>
      )
    },
    
    // SCREEN 14 - FICHES TECHNIQUES
    {
      id: 'fiches',
      title: 'FICHES TECHNIQUES',
      content: (
        <div className="space-y-6">
          <h2 className="text-3xl font-black text-[#1a1a1a]">📋 Fiches techniques</h2>
          <p className="text-[#1a1a1a]/70">Standardiser pour scaler</p>
          
          <div className="bg-orange-500/10 rounded-2xl p-6 border border-orange-500/30">
            <p className="text-orange-700 font-bold mb-4">📄 Template fiche technique :</p>
            
            <div className="bg-[#f0ede8] rounded-xl p-4 space-y-3 text-sm">
              <div className="flex justify-between border-b border-[#e0dbd4] pb-2">
                <span className="text-[#1a1a1a]/50">Nom du plat</span>
                <span className="text-[#1a1a1a] font-bold">[NOM]</span>
              </div>
              <div className="flex justify-between border-b border-[#e0dbd4] pb-2">
                <span className="text-[#1a1a1a]/50">Nombre de portions</span>
                <span className="text-[#1a1a1a] font-bold">[X] portions</span>
              </div>
              <div className="flex justify-between border-b border-[#e0dbd4] pb-2">
                <span className="text-[#1a1a1a]/50">Coût matière</span>
                <span className="text-[#1a1a1a] font-bold">[X]€/portion</span>
              </div>
              <div className="flex justify-between border-b border-[#e0dbd4] pb-2">
                <span className="text-[#1a1a1a]/50">Prix de vente</span>
                <span className="text-[#1a1a1a] font-bold">[X]€</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[#1a1a1a]/50">Marge</span>
                <span className="text-green-600 font-bold">[X]%</span>
              </div>
            </div>
          </div>
          
          <div className="bg-blue-500/10 rounded-xl p-4 border border-blue-500/30">
            <p className="text-blue-700 font-bold mb-3">🧪 Exemple de dosage standard :</p>
            <div className="grid grid-cols-2 gap-2 text-xs">
              {[
                { item: 'Sauce (louche)', dose: '30ml' },
                { item: 'Vinaigrette', dose: '15ml' },
                { item: 'Fromage râpé', dose: '20g' },
                { item: 'Beurre (noisette)', dose: '10g' },
                { item: 'Crème (plat)', dose: '50ml' },
                { item: 'Huile (cuisson)', dose: '15ml' }
              ].map((d, i) => (
                <div key={i} className="bg-white border border-[#e0dbd4] rounded-lg p-2 flex justify-between">
                  <span className="text-[#1a1a1a]/70">{d.item}</span>
                  <span className="text-blue-700 font-bold">{d.dose}</span>
                </div>
              ))}
            </div>
          </div>
          
          <div className="bg-red-500/10 rounded-xl p-4 border border-red-500/30">
            <p className="text-red-700 text-sm font-bold mb-2">⚠️ Pourquoi c'est crucial :</p>
            <div className="space-y-1 text-xs text-[#1a1a1a]/70">
              <p>• Sans dosage = coûts variables imprévisibles</p>
              <p>• Sans fiche = qualité inconsistante</p>
              <p>• Sans standard = impossible de former</p>
            </div>
          </div>
          
          <button
            onClick={() => toggleAction('fiches', 'created')}
            className={`w-full p-4 rounded-xl flex items-center justify-center gap-2 font-bold transition-all ${
              completedActions['fiches-created']
                ? 'bg-green-500 text-white'
                : 'bg-[#e0dbd4] text-[#1a1a1a] hover:bg-[#d4cfc7]'
            }`}
          >
            <Check className="w-5 h-5" />
            {completedActions['fiches-created'] ? 'Fiches créées ✓' : 'Je vais créer mes fiches'}
          </button>
        </div>
      )
    },
    
    // SCREEN 15 - RÉSEAUX SOCIAUX — ALGORITHME
    {
      id: 'reseaux',
      title: 'ALGO INSTA 2026',
      content: (
        <div className="space-y-6">
          <div>
            <p className="text-pink-600 text-xs font-bold uppercase tracking-wider mb-2">Réseaux sociaux · 1/3</p>
            <h2 className="text-3xl font-black text-[#1a1a1a] leading-tight">
              Comprends l'algo. <span className="text-orange-500">Arrête de poster dans le vide.</span>
            </h2>
            <p className="text-[#1a1a1a]/60 text-sm mt-2">Les règles qui décident si ta vidéo fait 200 ou 20 000 vues.</p>
          </div>

          <div className="bg-white border border-[#e0dbd4] rounded-2xl p-5 border border-[#e0dbd4]">
            <p className="text-pink-700 text-sm font-bold mb-3">🧠 Ce que l'algo regarde (dans l'ordre)</p>
            <div className="space-y-3">
              <div className="bg-[#f0ede8] rounded-lg p-3">
                <div className="flex items-center gap-2 mb-1">
                  <span className="w-6 h-6 rounded bg-pink-500/30 flex items-center justify-center text-pink-700 text-xs font-black">1</span>
                  <p className="text-[#1a1a1a] font-bold text-sm">Watch time (temps de visionnage)</p>
                </div>
                <p className="text-[#1a1a1a]/60 text-xs">Si 60%+ des gens regardent jusqu'au bout → boost massif. Sous 40% → enterrée.</p>
              </div>
              <div className="bg-[#f0ede8] rounded-lg p-3">
                <div className="flex items-center gap-2 mb-1">
                  <span className="w-6 h-6 rounded bg-pink-500/30 flex items-center justify-center text-pink-700 text-xs font-black">2</span>
                  <p className="text-[#1a1a1a] font-bold text-sm">Partages &gt; Commentaires &gt; Likes</p>
                </div>
                <p className="text-[#1a1a1a]/60 text-xs">1 partage = 10 likes en poids. Un like coûte rien, un partage = vrai intérêt.</p>
              </div>
              <div className="bg-[#f0ede8] rounded-lg p-3">
                <div className="flex items-center gap-2 mb-1">
                  <span className="w-6 h-6 rounded bg-pink-500/30 flex items-center justify-center text-pink-700 text-xs font-black">3</span>
                  <p className="text-[#1a1a1a] font-bold text-sm">Sauvegardes (saved)</p>
                </div>
                <p className="text-[#1a1a1a]/60 text-xs">Une sauvegarde = "je veux revenir" → signal fort pour l'algo. Pousse ton contenu à être sauvegardable.</p>
              </div>
              <div className="bg-[#f0ede8] rounded-lg p-3">
                <div className="flex items-center gap-2 mb-1">
                  <span className="w-6 h-6 rounded bg-pink-500/30 flex items-center justify-center text-pink-700 text-xs font-black">4</span>
                  <p className="text-[#1a1a1a] font-bold text-sm">Géolocalisation</p>
                </div>
                <p className="text-[#1a1a1a]/60 text-xs">Tag ta ville + hashtag local. L'algo priorise les vues à &lt;30km du resto = vraies réservations.</p>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-pink-500/10 to-purple-500/10 rounded-2xl p-5 border border-pink-500/30">
            <p className="text-pink-700 text-xs font-bold uppercase tracking-wider mb-3">Les 4 règles 2026</p>
            <div className="space-y-2 text-sm text-[#1a1a1a]/85">
              <p>✅ <span className="font-bold">Reels &gt; photos</span> — Insta pousse 5x plus les reels</p>
              <p>✅ <span className="font-bold">Durée 7-15s</span> — meilleur taux de complétion</p>
              <p>✅ <span className="font-bold">3-5 hashtags locaux pertinents</span> — pas 30 génériques</p>
              <p>✅ <span className="font-bold">1 reel/jour minimum</span> — la régularité bat la perfection</p>
            </div>
          </div>

          <div className="bg-red-500/10 rounded-xl p-4 border border-red-500/30">
            <p className="text-red-700 text-sm font-bold mb-1">🚨 Ce qui tue ton compte :</p>
            <div className="space-y-1 text-xs text-[#1a1a1a]/80">
              <p>• Poster des photos statiques (moins de 10% de portée des reels)</p>
              <p>• Spam hashtags (#food #restaurant #paris × 30 → shadowban)</p>
              <p>• Supprimer une vidéo qui marche mal (l'algo t'enregistre ça)</p>
              <p>• Ne pas répondre aux commentaires dans l'heure</p>
            </div>
          </div>
        </div>
      )
    },

    // SCREEN 15b - FORMAT VIDÉO : HOOK / REHOOK / STORY / CTA
    {
      id: 'format-video',
      title: 'FORMAT VIDÉO',
      content: (
        <div className="space-y-6">
          <div>
            <p className="text-pink-600 text-xs font-bold uppercase tracking-wider mb-2">Réseaux sociaux · 2/3</p>
            <h2 className="text-3xl font-black text-[#1a1a1a] leading-tight">
              Le format qui <span className="text-orange-500">remplit ton resto.</span>
            </h2>
            <p className="text-[#1a1a1a]/60 text-sm mt-2">4 blocs. 15 secondes. Toutes tes vidéos suivent cette structure.</p>
          </div>

          <div className="space-y-3">
            {/* HOOK */}
            <div className="bg-white border border-[#e0dbd4] rounded-2xl p-5 border border-[#e0dbd4]">
              <div className="flex items-center gap-2 mb-2">
                <span className="px-2 py-0.5 rounded bg-red-500/30 text-red-700 text-xs font-black">0 — 3s</span>
                <p className="text-[#1a1a1a] font-bold text-sm">HOOK</p>
                <span className="text-[#1a1a1a]/40 text-xs">· Stop au scroll</span>
              </div>
              <p className="text-[#1a1a1a]/70 text-sm mb-3">Tu as 1 seconde pour capter. Question, promesse, choc visuel.</p>
              <div className="space-y-2">
                <div className="bg-[#f0ede8] rounded-lg p-3 text-xs text-orange-700 font-mono">
                  "Ne commande jamais ce plat dans un resto 🚨"
                </div>
                <div className="bg-[#f0ede8] rounded-lg p-3 text-xs text-orange-700 font-mono">
                  "Le plat le plus vendu de mon resto depuis 3 ans"
                </div>
                <div className="bg-[#f0ede8] rounded-lg p-3 text-xs text-orange-700 font-mono">
                  "Tu sais pourquoi ta pizza colle ? Regarde..."
                </div>
              </div>
            </div>

            {/* REHOOK */}
            <div className="bg-white border border-[#e0dbd4] rounded-2xl p-5 border border-[#e0dbd4]">
              <div className="flex items-center gap-2 mb-2">
                <span className="px-2 py-0.5 rounded bg-orange-500/30 text-orange-700 text-xs font-black">3 — 7s</span>
                <p className="text-[#1a1a1a] font-bold text-sm">REHOOK</p>
                <span className="text-[#1a1a1a]/40 text-xs">· Re-capter</span>
              </div>
              <p className="text-[#1a1a1a]/70 text-sm mb-3">Changement de plan, mouvement, révélation partielle. L'œil a besoin d'une relance.</p>
              <div className="bg-[#f0ede8] rounded-lg p-3 text-xs text-[#1a1a1a]/70">
                👉 Exemple : plan large sur la cuisine → gros plan sur la main du chef → rebondissement.<br/>
                👉 Texte à l'écran qui apparaît pour casser la monotonie.
              </div>
            </div>

            {/* STORYTELLING */}
            <div className="bg-white border border-[#e0dbd4] rounded-2xl p-5 border border-[#e0dbd4]">
              <div className="flex items-center gap-2 mb-2">
                <span className="px-2 py-0.5 rounded bg-yellow-500/30 text-yellow-700 text-xs font-black">7 — 13s</span>
                <p className="text-[#1a1a1a] font-bold text-sm">STORYTELLING</p>
                <span className="text-[#1a1a1a]/40 text-xs">· Émotion</span>
              </div>
              <p className="text-[#1a1a1a]/70 text-sm mb-3">Problème → solution. Anecdote. Coulisses. Fais-les ressentir quelque chose.</p>
              <div className="space-y-2">
                <div className="bg-[#f0ede8] rounded-lg p-3 text-xs text-[#1a1a1a]/80">
                  <p className="text-yellow-700 font-bold mb-1">Angle Problème → Solution :</p>
                  "Avant on jetait 5kg de pain par semaine. Aujourd'hui, on en fait notre pain perdu signature. Regarde..."
                </div>
                <div className="bg-[#f0ede8] rounded-lg p-3 text-xs text-[#1a1a1a]/80">
                  <p className="text-yellow-700 font-bold mb-1">Angle Coulisses :</p>
                  "5h du matin, seul en cuisine. Voilà pourquoi nos pâtes sont fraîches chaque midi."
                </div>
                <div className="bg-[#f0ede8] rounded-lg p-3 text-xs text-[#1a1a1a]/80">
                  <p className="text-yellow-700 font-bold mb-1">Angle Réaction client :</p>
                  "Je lui ai fait goûter le dessert sans rien dire. Sa réaction ⬇️"
                </div>
              </div>
            </div>

            {/* CTA */}
            <div className="bg-green-500/10 rounded-2xl p-5 border border-green-500/30">
              <div className="flex items-center gap-2 mb-2">
                <span className="px-2 py-0.5 rounded bg-green-500/30 text-green-700 text-xs font-black">13 — 15s</span>
                <p className="text-[#1a1a1a] font-bold text-sm">CTA</p>
                <span className="text-[#1a1a1a]/40 text-xs">· Call-to-action</span>
              </div>
              <p className="text-[#1a1a1a]/70 text-sm mb-3">Sans CTA, pas de réservation. Dis-leur EXACTEMENT quoi faire.</p>
              <div className="space-y-2">
                <div className="bg-[#f0ede8] rounded-lg p-3 text-xs text-green-700 font-mono">
                  "👉 Réserve ta table : lien en bio"
                </div>
                <div className="bg-[#f0ede8] rounded-lg p-3 text-xs text-green-700 font-mono">
                  "📍 On est à [ville] — viens ce week-end"
                </div>
                <div className="bg-[#f0ede8] rounded-lg p-3 text-xs text-green-700 font-mono">
                  "💬 Dis-moi en commentaire si tu veux la recette"
                </div>
                <div className="bg-[#f0ede8] rounded-lg p-3 text-xs text-green-700 font-mono">
                  "🔖 Sauvegarde pour ton prochain date-night"
                </div>
              </div>
              <p className="text-[#1a1a1a]/60 text-xs mt-3">💡 Combine 2 CTA : "Sauvegarde + réserve en bio" = 2x plus de conversions.</p>
            </div>
          </div>

          <div className="bg-orange-500/10 rounded-xl p-4 border border-orange-500/30">
            <p className="text-orange-700 text-sm font-bold mb-1">🎯 Action :</p>
            <p className="text-[#1a1a1a]/80 text-sm">Écris 7 scripts (un par jour) avec ces 4 blocs. Tu tournes 7 vidéos en 1h. Tu postes 1/jour.</p>
          </div>

          <button
            onClick={() => toggleAction('video', 'scripts')}
            className={`w-full p-4 rounded-xl flex items-center justify-center gap-2 font-bold transition-all ${
              completedActions['video-scripts']
                ? 'bg-green-500 text-white'
                : 'bg-[#e0dbd4] text-[#1a1a1a] hover:bg-[#d4cfc7]'
            }`}
          >
            <Check className="w-5 h-5" />
            {completedActions['video-scripts'] ? '7 scripts écrits ✓' : 'J\'écris mes 7 scripts cette semaine'}
          </button>
        </div>
      )
    },

    // SCREEN — GÉNÉRATEUR VIRAL IA
    {
      id: 'social-generator',
      title: 'GÉNÉRATEUR VIRAL',
      content: (
        <div className="space-y-5">
          <div>
            <p className="text-purple-600 text-xs font-bold uppercase tracking-wider mb-2">Réseaux sociaux · Outil IA</p>
            <h2 className="font-['Playfair_Display',serif] text-3xl md:text-4xl font-black text-[#1a1a1a] leading-[1.05]">
              Ton <span className="text-purple-600">générateur</span><br/>de scripts viraux.
            </h2>
            <p className="text-[#1a1a1a]/70 text-sm mt-3 leading-relaxed">
              Décris ton sujet, l'IA te génère un script complet en 10 secondes : hook, scènes, voix off, CTA, hashtags et son tendance. Personnalisé pour <strong>{userInputs.restaurantName || 'ton resto'}</strong>.
            </p>
          </div>
          <ViralScriptGenerator restaurantType={userInputs.restaurantType || 'restaurant'} restaurantName={userInputs.restaurantName || 'mon resto'} />
        </div>
      )
    },

    // SCREEN — CALENDRIER ÉDITORIAL 30 JOURS
    {
      id: 'social-calendar',
      title: 'CALENDRIER ÉDITORIAL',
      content: (
        <div className="space-y-5">
          <div>
            <p className="text-purple-600 text-xs font-bold uppercase tracking-wider mb-2">Réseaux sociaux · Plan d'attaque</p>
            <h2 className="font-['Playfair_Display',serif] text-3xl md:text-4xl font-black text-[#1a1a1a] leading-[1.05]">
              30 jours.<br/><span className="text-purple-600">30 contenus.</span><br/>Zéro panne d'inspi.
            </h2>
            <p className="text-[#1a1a1a]/70 text-sm mt-3 leading-relaxed">
              Le calendrier exact à dérouler en 4 semaines pour transformer ton compte en machine à clients. Chaque jour = un type de post précis.
            </p>
          </div>

          <div className="space-y-3">
            {[
              { week: 'SEMAINE 1', title: 'Présenter le resto', color: 'bg-purple-500', posts: [
                { d: 'L', t: 'Reel "Story du chef"', a: 'POV : pourquoi tu as ouvert ton resto (45s)' },
                { d: 'Ma', t: 'Carrousel signature', a: '3 plats stars + prix + ingrédients clés' },
                { d: 'Me', t: 'Reel "Une journée"', a: 'Behind the scenes du matin au service (60s)' },
                { d: 'J', t: 'Story team', a: 'Tour des équipes en cuisine + salle' },
                { d: 'V', t: 'Reel "Avant/Après"', a: 'Plat brut → assiette finale (15s, son trending)' },
                { d: 'S', t: 'Post photo brunch', a: 'Photo flatlay + heure d\'ouverture + CTA réservation' },
                { d: 'D', t: 'Story récap', a: 'Best of de la semaine en story compilée' },
              ]},
              { week: 'SEMAINE 2', title: 'Éduquer + valeur', color: 'bg-orange-500', posts: [
                { d: 'L', t: 'Reel "3 erreurs"', a: '3 erreurs que les restos font sur ton plat star' },
                { d: 'Ma', t: 'Carrousel "Pourquoi"', a: 'Pourquoi ton ingrédient phare est meilleur ici' },
                { d: 'Me', t: 'Reel recette express', a: 'Mini-tuto 30s d\'un plat simple de ta carte' },
                { d: 'J', t: 'Story sondage', a: 'Quel plat veux-tu voir au menu cette semaine ?' },
                { d: 'V', t: 'Reel "POV client"', a: 'Tu arrives, tu commandes, tu kiffes (45s)' },
                { d: 'S', t: 'UGC repost', a: 'Repost le meilleur contenu client de la semaine' },
                { d: 'D', t: 'Story "menu lundi"', a: 'Teasing du plat du jour de demain' },
              ]},
              { week: 'SEMAINE 3', title: 'Engager + viraliser', color: 'bg-pink-500', posts: [
                { d: 'L', t: 'Reel "trend hijack"', a: 'Reprendre un trending sound + ton plat star (15s)' },
                { d: 'Ma', t: 'Carrousel "Notre histoire"', a: 'Avant l\'ouverture vs aujourd\'hui' },
                { d: 'Me', t: 'Reel "Réaction"', a: 'Ton équipe goûte un nouveau plat (45s)' },
                { d: 'J', t: 'Story Q&A', a: '"Posez-moi vos questions" sur le resto' },
                { d: 'V', t: 'Reel collab', a: 'Avec un commerçant voisin (boulanger, caviste…)' },
                { d: 'S', t: 'Post "weekend vibes"', a: 'Photo carrée ambiance + horaires service' },
                { d: 'D', t: 'Story bilan', a: '"Voici ce qu\'on a vécu cette semaine"' },
              ]},
              { week: 'SEMAINE 4', title: 'Convertir + fidéliser', color: 'bg-green-500', posts: [
                { d: 'L', t: 'Reel "transformation"', a: 'Ton mois 1 vs aujourd\'hui (résultats chiffrés)' },
                { d: 'Ma', t: 'Carrousel offre spéciale', a: 'Menu midi nouveauté + photo + CTA réserver' },
                { d: 'Me', t: 'Reel "Pourquoi nous"', a: 'Les 3 raisons de nous choisir vs concurrents' },
                { d: 'J', t: 'Story "merci"', a: 'Témoignage client filmé sur place (30s)' },
                { d: 'V', t: 'Reel "Top 3"', a: 'Top 3 plats du mois + comment les commander' },
                { d: 'S', t: 'Post photo "samedi"', a: 'Ambiance soirée + tag des amis' },
                { d: 'D', t: 'Story "Suivez-nous"', a: 'CTA "Active les notifs" + teasing semaine pro' },
              ]},
            ].map((wk) => (
              <div key={wk.week} className="bg-white rounded-2xl border border-[#e0dbd4] overflow-hidden">
                <div className={`${wk.color} text-white px-4 py-2.5`}>
                  <p className="text-[10px] font-black uppercase tracking-widest opacity-90">{wk.week}</p>
                  <p className="text-base font-black">{wk.title}</p>
                </div>
                <div className="p-3 space-y-1.5">
                  {wk.posts.map((p, i) => (
                    <div key={i} className="flex items-start gap-2.5 py-1.5 border-b border-[#f0ede8] last:border-0">
                      <div className="w-6 h-6 rounded-md bg-[#1a1a1a] text-white text-[10px] font-black flex items-center justify-center flex-shrink-0">{p.d}</div>
                      <div className="flex-1 min-w-0">
                        <p className="text-[#1a1a1a] text-xs font-bold leading-tight">{p.t}</p>
                        <p className="text-[#1a1a1a]/65 text-[11px] leading-snug">{p.a}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className="bg-amber-50 rounded-xl p-4 border border-amber-200">
            <p className="text-amber-800 text-sm font-bold">💡 Pro tip de Sabril</p>
            <p className="text-[#1a1a1a]/80 text-xs leading-relaxed mt-1">
              Filme 4 vidéos d'un coup le lundi matin (batch). Tu publies tout le reste de la semaine sans stress. Le secret des comptes qui cartonnent : le batch + la consistance.
            </p>
          </div>
        </div>
      )
    },

    // SCREEN — BIBLIOTHÈQUE DE HOOKS VIRAUX
    {
      id: 'social-hooks',
      title: 'BIBLIOTHÈQUE DE HOOKS',
      content: (
        <div className="space-y-5">
          <div>
            <p className="text-purple-600 text-xs font-bold uppercase tracking-wider mb-2">Réseaux sociaux · Copier-coller</p>
            <h2 className="font-['Playfair_Display',serif] text-3xl md:text-4xl font-black text-[#1a1a1a] leading-[1.05]">
              50 hooks <span className="text-purple-600">viraux</span><br/>copier-coller.
            </h2>
            <p className="text-[#1a1a1a]/70 text-sm mt-3 leading-relaxed">
              Les 3 premières secondes décident si on regarde ou si on scrolle. Voici les patterns qui marchent en restauration en 2026 — testés, prouvés, viraux.
            </p>
          </div>

          {[
            { cat: '🪝 Curiosity gap', color: 'border-purple-400 bg-purple-50', hooks: [
              'Personne ne te dit ça sur [ton plat]…',
              'Voici ce qu\'on cache dans la cuisine d\'un resto.',
              'Le seul truc à savoir avant de manger un [ton plat].',
              'J\'ai testé [X] et voici ce qui s\'est passé.',
              'Si tu fais [ça], ton plat va être dégueu.',
              'Pourquoi ton [plat] est dur (et comment le fixer).',
              'La vérité sur le [ton plat] que tu commandes.',
            ]},
            { cat: '🔥 Contrast / Avant-Après', color: 'border-orange-400 bg-orange-50', hooks: [
              'Avant : 0 client. Après 30 jours : queue dehors.',
              'On est passé de [X]€ à [Y]€ de CA en 1 mois.',
              'Mon resto il y a 6 mois vs aujourd\'hui.',
              'Ce que je pensais sur le marketing vs ce qui marche vraiment.',
              'Avant j\'achetais chez [X], maintenant je fais ça.',
              'Mon premier service en cuisine vs le 1000ème.',
            ]},
            { cat: '⚡ Liste / Top X', color: 'border-pink-400 bg-pink-50', hooks: [
              '3 erreurs que TOUS les restaurateurs font.',
              'Top 3 plats que tu dois commander chez [ton resto].',
              '5 trucs que je ferais différemment si je rouvrais.',
              '3 raisons pourquoi ton CA stagne (et personne ne te le dit).',
              'Les 7 jours qui vont transformer ton resto.',
              '4 ingrédients qui changent TOUT dans ta cuisine.',
            ]},
            { cat: '🎯 Question / Hook direct', color: 'border-blue-400 bg-blue-50', hooks: [
              'Tu sais pourquoi 80% des restos ferment ?',
              'Tu commandes encore [X] ? Arrête.',
              'Qui d\'autre kiffe le [plat star] ici ?',
              'Restaurateur, tu fais cette erreur ?',
              'Tu connais ce plat qu\'on sert qu\'ici ?',
              'Tu paies combien ton food cost ?',
            ]},
            { cat: '💔 Mistake / Confession', color: 'border-red-400 bg-red-50', hooks: [
              'J\'ai perdu 30 000€ à cause de ça.',
              'L\'erreur que j\'ai mis 2 ans à comprendre.',
              'Ce que j\'aurais aimé savoir avant d\'ouvrir.',
              'Je faisais ça, jusqu\'au jour où j\'ai compris…',
              'On m\'a menti pendant 1 an.',
              'J\'ai failli fermer à cause de cette décision.',
            ]},
            { cat: '🎬 POV / Behind the scenes', color: 'border-green-400 bg-green-50', hooks: [
              'POV : tu es chef et il est 19h30 un samedi.',
              'POV : tu viens manger chez [ton resto] pour la 1ère fois.',
              'Une journée dans la peau d\'un restaurateur.',
              'Ce qui se passe en cuisine quand tu commandes [X].',
              'Comment on prépare ton plat préféré.',
              '7h du matin chez [ton resto]. Voici ce qu\'on fait.',
            ]},
            { cat: '🤯 Stat / Chiffre choc', color: 'border-amber-400 bg-amber-50', hooks: [
              '8 restos sur 10 ferment dans les 3 ans. Pourquoi ?',
              'On a servi 1247 [plats] ce mois-ci. Voici le n°1.',
              '47€ qui peuvent te faire gagner 4000€.',
              '+20% de CA en 30 jours. Méthode.',
            ]},
          ].map((cat) => (
            <div key={cat.cat} className={`rounded-2xl p-4 border-l-4 ${cat.color}`}>
              <p className="text-[#1a1a1a] font-black text-sm mb-2.5">{cat.cat}</p>
              <div className="space-y-1.5">
                {cat.hooks.map((h, i) => (
                  <button
                    key={i}
                    onClick={() => navigator.clipboard.writeText(h)}
                    className="w-full text-left bg-white hover:bg-[#f0ede8] active:bg-[#e0dbd4] border border-white rounded-lg px-3 py-2 text-sm text-[#1a1a1a] transition flex items-start gap-2 group"
                  >
                    <span className="flex-1 leading-snug">"{h}"</span>
                    <span className="text-[#1a1a1a]/40 text-[10px] group-hover:text-purple-600 flex-shrink-0">📋</span>
                  </button>
                ))}
              </div>
            </div>
          ))}

          <div className="bg-[#1a1a1a] rounded-2xl p-4 text-white">
            <p className="text-orange-400 text-xs font-bold uppercase tracking-wider mb-1.5">💡 Mode d'emploi</p>
            <p className="text-white/85 text-sm leading-relaxed">
              Clique sur un hook pour le copier. Remplace les [crochets] par ton resto / plat / chiffre. Teste 3 hooks différents par semaine — celui qui fait +40% de vues est ton "format gagnant".
            </p>
          </div>
        </div>
      )
    },

    // SCREEN — STRATÉGIE COLLAB INFLUENCEURS
    {
      id: 'social-collab',
      title: 'COLLAB INFLUENCEURS',
      content: (
        <div className="space-y-5">
          <div>
            <p className="text-purple-600 text-xs font-bold uppercase tracking-wider mb-2">Réseaux sociaux · Acquisition rapide</p>
            <h2 className="font-['Playfair_Display',serif] text-3xl md:text-4xl font-black text-[#1a1a1a] leading-[1.05]">
              Comment booker<br/>les <span className="text-purple-600">bons</span><br/>influenceurs locaux.
            </h2>
            <p className="text-[#1a1a1a]/70 text-sm mt-3 leading-relaxed">
              1 bon influenceur local = 50-200 clients dans les 2 semaines. La méthode exacte pour les contacter sans passer pour un débutant.
            </p>
          </div>

          {/* Qui contacter ? */}
          <div className="bg-white rounded-2xl border border-[#e0dbd4] p-5">
            <p className="text-purple-700 text-[10px] font-black uppercase tracking-widest mb-3">🎯 ÉTAPE 1 — Qui cibler ?</p>
            <div className="space-y-2.5">
              {[
                { t: 'Foodies locaux 5k-50k abonnés', d: 'Ratio engagement > taille. Mieux 10k engagés que 100k passifs.', icon: '✅' },
                { t: 'Localisés dans ta ville ou région', d: 'Audience géolocalisée = clients potentiels. Pas un foodie parisien si t\'es à Lille.', icon: '✅' },
                { t: 'Vérifie l\'engagement', d: 'Likes/abonnés > 3% = bon. Compte hyper-actif en commentaires = top.', icon: '✅' },
                { t: 'Éviter les comptes "Top 10 restos"', d: 'Ils prennent ton argent et te mettent à côté de 9 concurrents.', icon: '❌' },
                { t: 'Éviter les abonnés achetés', d: 'Outil : socialblade.com → courbe en escalier = abonnés achetés.', icon: '❌' },
              ].map((it, i) => (
                <div key={i} className="flex items-start gap-2.5">
                  <span className="text-base flex-shrink-0">{it.icon}</span>
                  <div className="flex-1">
                    <p className="text-[#1a1a1a] text-sm font-bold leading-tight">{it.t}</p>
                    <p className="text-[#1a1a1a]/65 text-xs leading-snug mt-0.5">{it.d}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Template DM */}
          <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl border border-purple-200 p-5">
            <p className="text-purple-700 text-[10px] font-black uppercase tracking-widest mb-3">📩 ÉTAPE 2 — DM template (qui marche)</p>
            <div className="bg-white rounded-xl p-4 border border-purple-100 mb-3">
              <p className="text-[#1a1a1a] text-sm leading-relaxed font-mono">
                Hello [Prénom],<br/><br/>
                Sabril, le fondateur de [ton resto] à [ta ville].<br/><br/>
                Je suis ton contenu depuis [X temps] — ton reel sur [post précis] m'a fait kiffer 🔥.<br/><br/>
                Je veux te faire découvrir le resto. Si ça t'intéresse, je t'invite avec un(e) ami(e) un soir cette semaine, on te prépare un menu spécial chef.<br/><br/>
                Si l'expérience te plaît, on serait ravis que tu partages ton ressenti — mais zéro obligation, c'est avant tout une rencontre.<br/><br/>
                Tu me dis ?<br/><br/>
                Sabril 🥑
              </p>
            </div>
            <button
              onClick={() => navigator.clipboard.writeText(`Hello [Prénom],\n\nSabril, le fondateur de [ton resto] à [ta ville].\n\nJe suis ton contenu depuis [X temps] — ton reel sur [post précis] m'a fait kiffer 🔥.\n\nJe veux te faire découvrir le resto. Si ça t'intéresse, je t'invite avec un(e) ami(e) un soir cette semaine, on te prépare un menu spécial chef.\n\nSi l'expérience te plaît, on serait ravis que tu partages ton ressenti — mais zéro obligation, c'est avant tout une rencontre.\n\nTu me dis ?\n\nSabril 🥑`)}
              className="w-full py-2.5 bg-purple-600 hover:bg-purple-700 text-white text-sm font-bold rounded-lg"
            >
              📋 Copier le template DM
            </button>
          </div>

          {/* Le deal */}
          <div className="bg-white rounded-2xl border border-[#e0dbd4] p-5">
            <p className="text-purple-700 text-[10px] font-black uppercase tracking-widest mb-3">🤝 ÉTAPE 3 — Le deal type</p>
            <div className="space-y-3">
              <div className="bg-green-50 rounded-xl p-3 border border-green-200">
                <p className="text-green-700 font-bold text-sm mb-1">💚 Ce que tu offres</p>
                <p className="text-[#1a1a1a]/85 text-sm leading-snug">Repas chef offert pour 2 (valeur 80-150€) + accueil VIP par le chef + photos pro de l'assiette (en bonus).</p>
              </div>
              <div className="bg-blue-50 rounded-xl p-3 border border-blue-200">
                <p className="text-blue-700 font-bold text-sm mb-1">💙 Ce que tu demandes</p>
                <p className="text-[#1a1a1a]/85 text-sm leading-snug">1 reel + 3 stories (en story ils tag ton compte + ta loc Google). PAS de fee si tu paies le repas. Si grosse audience (50k+ engagés), 50-200€ en plus.</p>
              </div>
              <div className="bg-orange-50 rounded-xl p-3 border border-orange-200">
                <p className="text-orange-700 font-bold text-sm mb-1">🧡 Ce que tu NE FAIS PAS</p>
                <p className="text-[#1a1a1a]/85 text-sm leading-snug">Pas de validation du contenu avant publication (tue l'authenticité). Pas de script imposé. Pas de "tu dois dire que c'est le meilleur resto" — ridicule et ça se voit.</p>
              </div>
            </div>
          </div>

          {/* Erreurs à éviter */}
          <div className="bg-red-50 rounded-2xl border border-red-200 p-5">
            <p className="text-red-700 text-[10px] font-black uppercase tracking-widest mb-3">🚫 Erreurs qui tuent la collab</p>
            <ul className="space-y-2 text-sm text-[#1a1a1a]/85">
              <li className="flex items-start gap-2"><span>❌</span><span><strong>Contacter en pleine cohue</strong> (vendredi soir, samedi soir).</span></li>
              <li className="flex items-start gap-2"><span>❌</span><span><strong>Demander 1 reel + 5 posts + 10 stories</strong>. Trop = ils décrochent.</span></li>
              <li className="flex items-start gap-2"><span>❌</span><span><strong>Vouloir contrôler le contenu.</strong> Tu paies pour leur ton, leur audience, leur authenticité.</span></li>
              <li className="flex items-start gap-2"><span>❌</span><span><strong>Faire 10 collabs en 1 semaine.</strong> 2-3 par mois max. Sinon ton compte devient sponso.</span></li>
              <li className="flex items-start gap-2"><span>❌</span><span><strong>Oublier de re-poster leur contenu</strong> en story (sauvegarde aussi dans les highlights "Ils en parlent").</span></li>
            </ul>
          </div>

          {/* CTA action */}
          <div className="bg-gradient-to-br from-[#1a1a1a] to-[#2d1810] rounded-2xl p-5 text-white">
            <p className="text-orange-400 text-xs font-bold uppercase tracking-widest mb-2">🎯 Action cette semaine</p>
            <p className="text-white text-base font-bold leading-snug mb-3">
              Identifie 10 influenceurs locaux. Envoie ce DM à 5 d'entre eux dès lundi. Vise 2 acceptations.
            </p>
            <p className="text-white/70 text-xs leading-relaxed">
              Résultat type : 1 collab = 40-100 nouveaux clients dans les 14 jours. Sur 2 collabs/mois = 80-200 clients en plus. Multiplie par 12 mois.
            </p>
          </div>
        </div>
      )
    },

    // SCREEN 15c - AUTOMATISATIONS IA
    {
      id: 'ia-automatisations',
      title: 'AUTOMATISATIONS IA',
      content: (
        <div className="space-y-6">
          <div>
            <p className="text-pink-600 text-xs font-bold uppercase tracking-wider mb-2">Réseaux sociaux · 3/3</p>
            <h2 className="text-3xl font-black text-[#1a1a1a] leading-tight">
              5 automatisations IA = <span className="text-orange-500">+10h/semaine.</span>
            </h2>
            <p className="text-[#1a1a1a]/60 text-sm mt-2">Tu n'as pas à devenir un geek. Tu dois juste copier-coller.</p>
          </div>

          <div className="space-y-3">
            <div className="bg-white border border-[#e0dbd4] rounded-2xl p-5 border border-[#e0dbd4]">
              <div className="flex items-center gap-2 mb-2">
                <span className="w-8 h-8 rounded-lg bg-purple-500/30 flex items-center justify-center text-purple-700 text-sm font-black">1</span>
                <p className="text-[#1a1a1a] font-bold text-sm">ChatGPT — Écrire tes 7 posts en 10 min</p>
              </div>
              <p className="text-[#1a1a1a]/70 text-xs mb-3">Plus besoin de "quoi poster aujourd'hui ?". Tu génères ta semaine en une fois.</p>
              <div className="bg-[#f0ede8] rounded-lg p-3 text-xs text-[#1a1a1a]/80 font-mono">
                <p className="text-purple-700 mb-1">Prompt à coller :</p>
                "Tu es un copywriter resto. Écris 7 scripts de reels Insta pour mon resto [TYPE] à [VILLE]. Format : HOOK (3s) + REHOOK + STORY (problème/solution ou coulisses) + CTA. Durée 10-15s. Mets le texte qui doit apparaître à l'écran. Varie les angles."
              </div>
            </div>

            <div className="bg-white border border-[#e0dbd4] rounded-2xl p-5 border border-[#e0dbd4]">
              <div className="flex items-center gap-2 mb-2">
                <span className="w-8 h-8 rounded-lg bg-purple-500/30 flex items-center justify-center text-purple-700 text-sm font-black">2</span>
                <p className="text-[#1a1a1a] font-bold text-sm">CapCut Auto-Captions — Sous-titrer en 30s</p>
              </div>
              <p className="text-[#1a1a1a]/70 text-xs mb-3">80% des gens regardent sans le son. Pas de sous-titres = vidéo morte.</p>
              <div className="bg-[#f0ede8] rounded-lg p-3 text-xs text-[#1a1a1a]/70">
                App gratuite. Tu importes ta vidéo → "Auto-captions" → langue française → en 30s tu as des sous-titres stylés. Gain : 15 min → 30s par vidéo.
              </div>
            </div>

            <div className="bg-white border border-[#e0dbd4] rounded-2xl p-5 border border-[#e0dbd4]">
              <div className="flex items-center gap-2 mb-2">
                <span className="w-8 h-8 rounded-lg bg-purple-500/30 flex items-center justify-center text-purple-700 text-sm font-black">3</span>
                <p className="text-[#1a1a1a] font-bold text-sm">Google Sheets + ChatGPT — Analyser tes ventes</p>
              </div>
              <p className="text-[#1a1a1a]/70 text-xs mb-3">Tu copies ton tableau KPIs hebdo dans ChatGPT et tu lui demandes.</p>
              <div className="bg-[#f0ede8] rounded-lg p-3 text-xs text-[#1a1a1a]/80 font-mono">
                <p className="text-purple-700 mb-1">Prompt :</p>
                "Voici mes ventes des 4 dernières semaines. Analyse les tendances, les plats en baisse, et donne-moi 3 actions concrètes pour augmenter le CA de 10% le mois prochain."
              </div>
            </div>

            <div className="bg-white border border-[#e0dbd4] rounded-2xl p-5 border border-[#e0dbd4]">
              <div className="flex items-center gap-2 mb-2">
                <span className="w-8 h-8 rounded-lg bg-purple-500/30 flex items-center justify-center text-purple-700 text-sm font-black">4</span>
                <p className="text-[#1a1a1a] font-bold text-sm">WhatsApp Business — Réponses automatiques</p>
              </div>
              <p className="text-[#1a1a1a]/70 text-xs mb-3">Les FAQ (horaires, menu, réservation, adresse) répondues sans toi.</p>
              <div className="bg-[#f0ede8] rounded-lg p-3 text-xs text-[#1a1a1a]/70">
                Configure : message d'accueil + réponses rapides pour les 5 questions les + posées. Gain : 30 min/jour de messages redondants.
              </div>
            </div>

            <div className="bg-white border border-[#e0dbd4] rounded-2xl p-5 border border-[#e0dbd4]">
              <div className="flex items-center gap-2 mb-2">
                <span className="w-8 h-8 rounded-lg bg-purple-500/30 flex items-center justify-center text-purple-700 text-sm font-black">5</span>
                <p className="text-[#1a1a1a] font-bold text-sm">ChatGPT — Réponses aux avis Google</p>
              </div>
              <p className="text-[#1a1a1a]/70 text-xs mb-3">Tu colles l'avis, il te rédige une réponse pro, personnalisée, en 10s.</p>
              <div className="bg-[#f0ede8] rounded-lg p-3 text-xs text-[#1a1a1a]/80 font-mono">
                <p className="text-purple-700 mb-1">Prompt :</p>
                "Rédige une réponse pro, chaleureuse et sincère à cet avis Google (4 lignes max) : [COLLE L'AVIS]. Je suis le gérant de [RESTO], mon prénom est [X]."
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-purple-500/10 to-pink-500/5 rounded-xl p-4 border border-purple-500/30">
            <p className="text-purple-700 text-sm font-bold mb-1">⏱️ Calcul du gain :</p>
            <div className="text-sm text-[#1a1a1a]/80 space-y-0.5">
              <p>Écriture posts : -3h/sem</p>
              <p>Sous-titrage : -2h/sem</p>
              <p>Analyse ventes : -2h/sem</p>
              <p>WhatsApp FAQ : -2,5h/sem</p>
              <p>Réponses avis : -1h/sem</p>
              <p className="text-purple-700 font-black mt-2 pt-2 border-t border-purple-500/30">
                = +10h libérées chaque semaine
              </p>
            </div>
          </div>

          <button
            onClick={() => toggleAction('ia', 'setup')}
            className={`w-full p-4 rounded-xl flex items-center justify-center gap-2 font-bold transition-all ${
              completedActions['ia-setup']
                ? 'bg-green-500 text-white'
                : 'bg-[#e0dbd4] text-[#1a1a1a] hover:bg-[#d4cfc7]'
            }`}
          >
            <Check className="w-5 h-5" />
            {completedActions['ia-setup'] ? 'Les 5 automatisations en place ✓' : 'Je mets 1 automatisation en place cette semaine'}
          </button>
        </div>
      )
    },
    
    // ============ NEW CHAPTERS — OPERATIONAL DEEP DIVE ============

    // SCREEN — OPTIMISEUR FINANCIER IA
    {
      id: 'financial-optimizer',
      title: 'OPTIMISEUR FINANCIER IA',
      content: (
        <div className="space-y-5">
          <div>
            <p className="text-emerald-600 text-xs font-bold uppercase tracking-wider mb-2">Finance & pilotage · Outil IA</p>
            <h2 className="font-['Playfair_Display',serif] text-3xl md:text-4xl font-black text-[#1a1a1a] leading-[1.05]">
              Ton <span className="text-emerald-600">plan financier</span><br/>chiffré en 15s.
            </h2>
            <p className="text-[#1a1a1a]/70 text-sm mt-3 leading-relaxed">
              Renseigne tes chiffres. L'IA te génère un <strong>diagnostic complet</strong>, identifie tes fuites de marge et te livre <strong>6-8 actions chiffrées</strong> avec gain mensuel estimé en € + roadmap 30 jours. ⚡
            </p>
          </div>
          <FinancialOptimizerGenerator userInputs={userInputs} />
          <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-200">
            <p className="text-emerald-800 text-sm font-bold">💡 Pro tip de Sabril</p>
            <p className="text-[#1a1a1a]/85 text-xs leading-relaxed mt-1">
              Lance le diagnostic UNE FOIS par mois après ton bilan compta. Compare tes chiffres mois après mois. Tu verras les leviers qui paient vraiment. C'est ton tableau de bord patron.
            </p>
          </div>
        </div>
      )
    },

    // SCREEN 16 - FOOD COST PAR PLAT
    {
      id: 'foodcost-plat',
      title: 'FOOD COST PAR PLAT',
      content: (
        <div className="space-y-6">
          {/* Bloc pédagogique */}
          <div className="bg-gradient-to-br from-amber-50 to-yellow-50 rounded-2xl p-5 border-2 border-amber-200">
            <p className="text-amber-700 text-xs font-black uppercase tracking-widest mb-2">📖 C'est quoi le Food Cost ?</p>
            <p className="text-[#1a1a1a] text-base font-bold mb-3 leading-snug">
              Le <span className="text-amber-700">Food Cost</span> c'est <strong>combien tu dépenses en matières premières</strong> pour préparer un plat, en pourcentage de son prix de vente.
            </p>
            <div className="bg-white rounded-lg p-3 border border-amber-200 mb-3">
              <p className="text-[#1a1a1a]/85 text-sm leading-relaxed">
                <strong>Exemple ultra-simple :</strong> tu vends un burger <strong>14€</strong>, et il te coûte <strong>4,20€</strong> en ingrédients (pain + viande + cheddar + sauce…).<br/>
                Food cost = 4,20 ÷ 14 × 100 = <strong className="text-amber-700">30%</strong>.<br/>
                <span className="text-[#1a1a1a]/60 text-xs">Sur 14€ encaissés, 4,20€ partent en achats. Reste 9,80€ pour payer le reste.</span>
              </p>
            </div>
            <div className="pt-3 border-t border-amber-200">
              <p className="text-amber-700 text-xs font-bold uppercase tracking-wider mb-1">⚡ Pourquoi le calculer plat par plat ?</p>
              <p className="text-[#1a1a1a]/80 text-sm leading-relaxed">
                Parce que la moyenne globale ment. Tu peux avoir 30% en moyenne mais 2 plats à 50% qui te plombent. Quand tu sais lesquels, tu agis chirurgicalement (prix +1€, fournisseur, portion) au lieu de paniquer sur tout.
              </p>
            </div>
          </div>

          <div>
            <p className="text-orange-600 text-xs font-bold uppercase tracking-wider mb-2">Finance & pilotage · 1/3</p>
            <h2 className="text-3xl font-black text-[#1a1a1a] leading-tight">
              Calcule le food cost de <span className="text-orange-500">chaque plat.</span>
            </h2>
            <p className="text-[#1a1a1a]/60 text-sm mt-2">Pas la moyenne. Pas le global. Chaque plat.</p>
          </div>

          <div className="bg-white border border-[#e0dbd4] rounded-2xl p-5 border border-[#e0dbd4]">
            <p className="text-[#1a1a1a]/70 text-xs uppercase tracking-wider mb-3">Formule</p>
            <div className="bg-[#f0ede8] rounded-xl p-4 font-mono text-sm">
              <p className="text-[#1a1a1a]/80">Food Cost % =</p>
              <p className="text-orange-600 font-bold mt-1">(coût matières ÷ prix de vente HT) × 100</p>
            </div>
          </div>

          <div className="bg-gradient-to-br from-green-500/10 to-emerald-500/5 rounded-2xl p-5 border border-green-500/25">
            <p className="text-green-700 text-xs uppercase tracking-wider font-bold mb-3">Exemple : Burger signature</p>
            <div className="space-y-1.5 text-sm text-[#1a1a1a]/85">
              <p>Prix vente HT : <span className="text-[#1a1a1a] font-bold">14€</span></p>
              <p>Pain 0,60€ + bœuf 2,80€ + cheddar 0,40€ + salade/tomate 0,30€ + sauce 0,10€ = <span className="text-[#1a1a1a] font-bold">4,20€</span></p>
            </div>
            <div className="mt-3 pt-3 border-t border-green-500/20">
              <p className="text-green-600 font-bold">→ Food cost = 30% ✅ Sain.</p>
            </div>
          </div>

          <div className="bg-red-500/10 rounded-2xl p-5 border border-red-500/30">
            <p className="text-red-700 text-sm font-bold mb-2">🚨 Si ton food cost d'un plat dépasse 35% :</p>
            <div className="space-y-1 text-sm text-[#1a1a1a]/80">
              <p>1. Augmente le prix de 1 à 2€ (teste avant de paniquer)</p>
              <p>2. Négocie ton fournisseur OU change de fournisseur</p>
              <p>3. Réduis la portion de 10% (invisible pour le client)</p>
            </div>
          </div>

          <div className="bg-orange-500/10 rounded-xl p-4 border border-orange-500/30">
            <p className="text-orange-700 text-sm font-bold mb-1">🎯 Action cette semaine :</p>
            <p className="text-[#1a1a1a]/80 text-sm">Calcule le food cost de tes 10 plats les plus vendus. Identifie les 3 qui dépassent 35%.</p>
          </div>

          <button
            onClick={() => toggleAction('foodcost', 'calculated')}
            className={`w-full p-4 rounded-xl flex items-center justify-center gap-2 font-bold transition-all ${
              completedActions['foodcost-calculated'] ? 'bg-green-500 text-white' : 'bg-[#e0dbd4] text-[#1a1a1a] hover:bg-[#d4cfc7]'
            }`}
          >
            <Check className="w-5 h-5" />
            {completedActions['foodcost-calculated'] ? 'Food cost calculés ✓' : 'Je calcule mes 10 plats'}
          </button>
        </div>
      )
    },

    // SCREEN 17 - KPIs HEBDO
    {
      id: 'kpis-hebdo',
      title: 'KPIs HEBDO',
      content: (
        <div className="space-y-6">
          <div>
            <p className="text-orange-600 text-xs font-bold uppercase tracking-wider mb-2">Finance & pilotage · 2/3</p>
            <h2 className="text-3xl font-black text-[#1a1a1a] leading-tight">
              7 chiffres à regarder <span className="text-orange-500">chaque lundi.</span>
            </h2>
            <p className="text-[#1a1a1a]/60 text-sm mt-2">10 minutes, une fois par semaine. Pas plus.</p>
          </div>

          <div className="space-y-3">
            {[
              { i: '01', label: 'CA semaine', detail: 'vs semaine N-1 (+%)' },
              { i: '02', label: 'Couverts + panier moyen', detail: 'CA / nombre de tickets' },
              { i: '03', label: 'Food cost %', detail: 'achats semaine ÷ CA semaine' },
              { i: '04', label: 'Masse salariale %', detail: 'salaires ÷ CA — alerte si >35%' },
              { i: '05', label: 'Avis Google', detail: 'nombre + note moyenne' },
              { i: '06', label: 'Heures extras staff', detail: 'signal d\'organisation' },
              { i: '07', label: 'Cash dispo en banque', detail: 'mini 3 mois de charges' },
            ].map((k) => (
              <div key={k.i} className="flex items-center gap-3 bg-white border border-[#e0dbd4] rounded-xl p-3 border border-[#e0dbd4]">
                <div className="w-9 h-9 rounded-lg bg-orange-500/20 flex items-center justify-center flex-shrink-0">
                  <span className="text-orange-600 font-black text-xs">{k.i}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[#1a1a1a] font-bold text-sm">{k.label}</p>
                  <p className="text-[#1a1a1a]/50 text-xs">{k.detail}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="bg-blue-500/10 rounded-xl p-4 border border-blue-500/30">
            <p className="text-blue-700 text-sm font-bold mb-1">💡 Template prêt :</p>
            <p className="text-[#1a1a1a]/80 text-xs mb-2">Un Google Sheet simple. Une ligne par semaine, une colonne par KPI.</p>
            <p className="text-[#1a1a1a]/60 text-xs">👉 Pro tip : décide d'un seuil d'alerte par KPI. Quand tu passes sous, tu agis cette semaine — pas le mois prochain.</p>
          </div>

          <button
            onClick={() => toggleAction('kpis', 'setup')}
            className={`w-full p-4 rounded-xl flex items-center justify-center gap-2 font-bold transition-all ${
              completedActions['kpis-setup'] ? 'bg-green-500 text-white' : 'bg-[#e0dbd4] text-[#1a1a1a] hover:bg-[#d4cfc7]'
            }`}
          >
            <Check className="w-5 h-5" />
            {completedActions['kpis-setup'] ? 'Tableau créé ✓' : 'Je crée mon tableau lundi'}
          </button>
        </div>
      )
    },

    // SCREEN 18 - TRESORERIE
    {
      id: 'tresorerie',
      title: 'TRÉSORERIE',
      content: (
        <div className="space-y-6">
          {/* Bloc pédagogique */}
          <div className="bg-gradient-to-br from-cyan-50 to-blue-50 rounded-2xl p-5 border-2 border-cyan-200">
            <p className="text-cyan-700 text-xs font-black uppercase tracking-widest mb-2">📖 Trésorerie ≠ Bénéfice</p>
            <p className="text-[#1a1a1a] text-base font-bold mb-3 leading-snug">
              La <span className="text-cyan-700">trésorerie</span> c'est <strong>le cash dispo MAINTENANT sur ton compte bancaire.</strong> Le <span className="text-cyan-700">bénéfice</span> c'est ce que tu gagnes en théorie sur le papier.
            </p>
            <div className="bg-white rounded-lg p-3 border border-cyan-200 mb-3">
              <p className="text-[#1a1a1a]/85 text-sm leading-relaxed">
                <strong>Tu peux être bénéficiaire ET en faillite.</strong><br/>
                Exemple : tu fais 5 000€ de bénéfice ce mois, mais ton TVA + URSSAF + loyer tombent la même semaine et tu n'as que 2 000€ en banque → tu es <strong className="text-red-600">en cessation de paiement</strong>.
              </p>
            </div>
            <div className="pt-3 border-t border-cyan-200">
              <p className="text-cyan-700 text-xs font-bold uppercase tracking-wider mb-1">⚡ Pourquoi 80% des restos ferment ?</p>
              <p className="text-[#1a1a1a]/80 text-sm leading-relaxed">
                Pas par manque de clients. Par <strong>manque de cash le jour où une grosse échéance tombe</strong>. La tréso, c'est l'oxygène. Tu peux survivre 3 mois sans bénéf, jamais 3 jours sans cash.
              </p>
            </div>
          </div>

          <div>
            <p className="text-orange-600 text-xs font-bold uppercase tracking-wider mb-2">Finance & pilotage · 3/3</p>
            <h2 className="text-3xl font-black text-[#1a1a1a] leading-tight">
              Arrête d'être surpris par <span className="text-orange-500">les fins de mois.</span>
            </h2>
            <p className="text-[#1a1a1a]/60 text-sm mt-2">Anticipe. La tréso, c'est la vie d'un resto.</p>
          </div>

          <div className="bg-gradient-to-br from-orange-500/10 to-amber-500/5 rounded-2xl p-5 border border-orange-500/25">
            <p className="text-orange-700 text-xs font-bold uppercase tracking-wider mb-2">Règle d'or</p>
            <p className="text-2xl font-black text-[#1a1a1a]">3 mois de charges fixes en cash, toujours.</p>
            <p className="text-[#1a1a1a]/60 text-xs mt-2">Loyer + salaires + TVA + charges sociales × 3</p>
          </div>

          <div className="bg-white border border-[#e0dbd4] rounded-2xl p-5 border border-[#e0dbd4]">
            <p className="text-[#1a1a1a]/80 text-sm font-bold mb-3">📅 Le calendrier des échéances :</p>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between py-2 border-b border-[#e0dbd4]">
                <span className="text-[#1a1a1a]/80">5 du mois</span>
                <span className="text-[#1a1a1a] font-bold">Loyer</span>
              </div>
              <div className="flex justify-between py-2 border-b border-[#e0dbd4]">
                <span className="text-[#1a1a1a]/80">15 du mois</span>
                <span className="text-[#1a1a1a] font-bold">TVA (si mensuelle)</span>
              </div>
              <div className="flex justify-between py-2 border-b border-[#e0dbd4]">
                <span className="text-[#1a1a1a]/80">Fin de mois</span>
                <span className="text-[#1a1a1a] font-bold">Salaires + cotisations</span>
              </div>
              <div className="flex justify-between py-2">
                <span className="text-[#1a1a1a]/80">J+30 après facture</span>
                <span className="text-[#1a1a1a] font-bold">Fournisseurs</span>
              </div>
            </div>
          </div>

          <div className="bg-green-500/10 rounded-xl p-4 border border-green-500/30">
            <p className="text-green-700 text-sm font-bold mb-2">✅ 3 leviers pour lisser la tréso :</p>
            <div className="space-y-1 text-sm text-[#1a1a1a]/80">
              <p>1. Négocie tes fournisseurs à 30-45j (au lieu de 15j)</p>
              <p>2. Étale tes gros achats (équipement) sur plusieurs mois</p>
              <p>3. Garde une ligne de crédit dispo (même si tu ne l'utilises pas)</p>
            </div>
          </div>

          <div className="bg-red-500/10 rounded-xl p-4 border border-red-500/30">
            <p className="text-red-700 text-sm font-bold mb-1">🚨 Alerte rouge :</p>
            <p className="text-[#1a1a1a]/80 text-sm">Si ton solde bancaire passe sous ton seuil d'1 mois de charges → tu réagis dans les 15 jours. Pas le jour J.</p>
          </div>
        </div>
      )
    },

    // SCREEN 19 - MENU ENGINEERING
    {
      id: 'menu-engineering',
      title: 'MENU ENGINEERING',
      content: (
        <div className="space-y-6">
          <div>
            <p className="text-pink-600 text-xs font-bold uppercase tracking-wider mb-2">Carte & revenus · 1/2</p>
            <h2 className="text-3xl font-black text-[#1a1a1a] leading-tight">
              Classe tes plats en <span className="text-orange-500">4 cases.</span>
            </h2>
            <p className="text-[#1a1a1a]/60 text-sm mt-2">Tu sauras instantanément quoi pousser, quoi retirer.</p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="bg-green-500/15 rounded-xl p-4 border border-green-500/30">
              <p className="text-green-700 text-xs font-bold uppercase mb-1">⭐ Stars</p>
              <p className="text-[#1a1a1a] text-sm font-bold">Vendus + rentables</p>
              <p className="text-[#1a1a1a]/60 text-xs mt-2">→ MET EN AVANT (carte, photos, serveurs)</p>
            </div>
            <div className="bg-blue-500/15 rounded-xl p-4 border border-blue-500/30">
              <p className="text-blue-700 text-xs font-bold uppercase mb-1">🧩 Puzzles</p>
              <p className="text-[#1a1a1a] text-sm font-bold">Rentables mais peu vendus</p>
              <p className="text-[#1a1a1a]/60 text-xs mt-2">→ PROMOTE (renomme, repositionne)</p>
            </div>
            <div className="bg-yellow-500/15 rounded-xl p-4 border border-yellow-500/30">
              <p className="text-yellow-700 text-xs font-bold uppercase mb-1">🐴 Plowhorses</p>
              <p className="text-[#1a1a1a] text-sm font-bold">Vendus mais peu rentables</p>
              <p className="text-[#1a1a1a]/60 text-xs mt-2">→ RETRAVAILLE (+prix ou -coût)</p>
            </div>
            <div className="bg-red-500/15 rounded-xl p-4 border border-red-500/30">
              <p className="text-red-700 text-xs font-bold uppercase mb-1">💀 Dogs</p>
              <p className="text-[#1a1a1a] text-sm font-bold">Ni vendus ni rentables</p>
              <p className="text-[#1a1a1a]/60 text-xs mt-2">→ RETIRE sans hésiter</p>
            </div>
          </div>

          <div className="bg-gradient-to-br from-white/5 to-transparent rounded-2xl p-5 border border-[#e0dbd4]">
            <p className="text-orange-700 text-xs font-bold uppercase tracking-wider mb-3">Exemple concret</p>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between items-center bg-green-500/10 rounded-lg p-3">
                <span className="text-[#1a1a1a]">Burger signature</span>
                <span className="text-green-700 font-bold text-xs">STAR</span>
              </div>
              <div className="flex justify-between items-center bg-red-500/10 rounded-lg p-3">
                <span className="text-[#1a1a1a]">Tartare végétarien</span>
                <span className="text-red-700 font-bold text-xs">DOG → RETIRE</span>
              </div>
              <div className="flex justify-between items-center bg-yellow-500/10 rounded-lg p-3">
                <span className="text-[#1a1a1a]">Pizza 4 fromages (grosse part viande, marge faible)</span>
                <span className="text-yellow-700 font-bold text-xs">PLOWHORSE</span>
              </div>
            </div>
          </div>

          <div className="bg-orange-500/10 rounded-xl p-4 border border-orange-500/30">
            <p className="text-orange-700 text-sm font-bold mb-1">🎯 Action :</p>
            <p className="text-[#1a1a1a]/80 text-sm">Classe TES 15 plats principaux dans les 4 cases. Tu vas découvrir 2-3 "dogs" qui te coûtent du cash.</p>
          </div>

          <button
            onClick={() => toggleAction('menu', 'engineered')}
            className={`w-full p-4 rounded-xl flex items-center justify-center gap-2 font-bold transition-all ${
              completedActions['menu-engineered'] ? 'bg-green-500 text-white' : 'bg-[#e0dbd4] text-[#1a1a1a] hover:bg-[#d4cfc7]'
            }`}
          >
            <Check className="w-5 h-5" />
            {completedActions['menu-engineered'] ? 'Menu classé ✓' : 'Je classe mes plats'}
          </button>
        </div>
      )
    },

    // SCREEN — MENU TRENDS GENERATOR (IA)
    {
      id: 'menu-trends',
      title: 'TENDANCES 2026',
      content: (
        <div className="space-y-5">
          <div>
            <p className="text-orange-600 text-xs font-bold uppercase tracking-wider mb-2">Carte & revenus · Outil IA</p>
            <h2 className="font-['Playfair_Display',serif] text-3xl md:text-4xl font-black text-[#1a1a1a] leading-[1.05]">
              Les <span className="text-orange-500">vraies tendances</span><br/>2026 de ton type<br/>de resto.
            </h2>
            <p className="text-[#1a1a1a]/70 text-sm mt-3 leading-relaxed">
              Boissons, plats, desserts, concepts et formats réseaux qui cartonnent. <strong>Food cost et prix conseillés inclus.</strong> Tu choisis, tu ajoutes à ta carte. 🚀
            </p>
          </div>
          <MenuTrendsGenerator />
          <div className="bg-green-50 rounded-xl p-4 border border-green-200">
            <p className="text-green-800 text-sm font-bold">💡 Pro tip de Sabril</p>
            <p className="text-[#1a1a1a]/85 text-xs leading-relaxed mt-1">
              Ne change pas toute ta carte d'un coup. Ajoute 1 boisson tendance + 1 plat signature + 1 dessert chaque mois. Mesure ce qui marche après 3 semaines, tu gardes le top 50%.
            </p>
          </div>
        </div>
      )
    },

    // SCREEN 20 - TICKET MOYEN
    {
      id: 'ticket-moyen',
      title: 'TICKET MOYEN',
      content: (
        <div className="space-y-6">
          <div>
            <p className="text-pink-600 text-xs font-bold uppercase tracking-wider mb-2">Carte & revenus · 2/2</p>
            <h2 className="text-3xl font-black text-[#1a1a1a] leading-tight">
              +3€ de panier moyen = <span className="text-orange-500">+9 000€/mois.</span>
            </h2>
            <p className="text-[#1a1a1a]/60 text-sm mt-2">Sans plus de clients. Juste avec 3 techniques.</p>
          </div>

          <div className="bg-green-500/10 rounded-2xl p-5 border border-green-500/30">
            <p className="text-green-700 text-xs font-bold uppercase tracking-wider mb-2">Calcul de base</p>
            <div className="text-sm text-[#1a1a1a]/85 space-y-1">
              <p>120 couverts/jour × 3€ = 360€/jour</p>
              <p>360€ × 25 jours = <span className="text-green-600 font-black text-lg">9 000€/mois</span></p>
            </div>
          </div>

          <div className="space-y-3">
            <div className="bg-white border border-[#e0dbd4] rounded-xl p-4 border border-[#e0dbd4]">
              <div className="flex items-center gap-2 mb-2">
                <span className="w-8 h-8 rounded-lg bg-orange-500/20 flex items-center justify-center text-orange-600 font-black text-sm">1</span>
                <p className="text-[#1a1a1a] font-bold">Upsell vertical</p>
              </div>
              <p className="text-[#1a1a1a]/70 text-sm mb-2">Monter en gamme sur le plat commandé.</p>
              <div className="bg-[#f0ede8] rounded-lg p-3 text-xs text-orange-700 font-mono">
                "Avec supplément bacon ou double cheddar ? (+2€)"
              </div>
            </div>

            <div className="bg-white border border-[#e0dbd4] rounded-xl p-4 border border-[#e0dbd4]">
              <div className="flex items-center gap-2 mb-2">
                <span className="w-8 h-8 rounded-lg bg-orange-500/20 flex items-center justify-center text-orange-600 font-black text-sm">2</span>
                <p className="text-[#1a1a1a] font-bold">Upsell horizontal</p>
              </div>
              <p className="text-[#1a1a1a]/70 text-sm mb-2">Ajouter une ligne au ticket.</p>
              <div className="bg-[#f0ede8] rounded-lg p-3 text-xs text-orange-700 font-mono">
                "Un dessert maison pour finir ? (+6€)"
              </div>
            </div>

            <div className="bg-white border border-[#e0dbd4] rounded-xl p-4 border border-[#e0dbd4]">
              <div className="flex items-center gap-2 mb-2">
                <span className="w-8 h-8 rounded-lg bg-orange-500/20 flex items-center justify-center text-orange-600 font-black text-sm">3</span>
                <p className="text-[#1a1a1a] font-bold">Formule incitative</p>
              </div>
              <p className="text-[#1a1a1a]/70 text-sm mb-2">Entrée + plat + dessert à 2-3€ de moins que l'addition séparée.</p>
              <div className="bg-[#f0ede8] rounded-lg p-3 text-xs text-orange-700 font-mono">
                "Formule 24€ au lieu de 27€ à la carte"
              </div>
            </div>
          </div>

          <div className="bg-blue-500/10 rounded-xl p-4 border border-blue-500/30">
            <p className="text-blue-700 text-sm font-bold mb-2">⚠️ Règle :</p>
            <p className="text-[#1a1a1a]/80 text-sm">Tes serveurs doivent proposer. Tu perds 80% des ventes additionnelles si personne ne propose. Briefe-les chaque jour.</p>
          </div>
        </div>
      )
    },

    // SCREEN 21 - FOURNISSEURS
    {
      id: 'fournisseurs',
      title: 'FOURNISSEURS',
      content: (
        <div className="space-y-6">
          <div>
            <p className="text-yellow-600 text-xs font-bold uppercase tracking-wider mb-2">Achats & stock · 1/2</p>
            <h2 className="text-3xl font-black text-[#1a1a1a] leading-tight">
              Tes fournisseurs <span className="text-orange-500">te volent peut-être.</span>
            </h2>
            <p className="text-[#1a1a1a]/60 text-sm mt-2">Pas par méchanceté. Par habitude. Reprends la main.</p>
          </div>

          <div className="bg-white border border-[#e0dbd4] rounded-2xl p-5 border border-[#e0dbd4]">
            <p className="text-orange-700 text-sm font-bold mb-3">📋 Règle des 3 devis</p>
            <p className="text-[#1a1a1a]/80 text-sm mb-3">Pour chaque catégorie : viande, poisson, légumes, pain, vin.</p>
            <div className="space-y-2 text-sm">
              <div className="flex items-center gap-2 text-[#1a1a1a]/80">
                <Check className="w-4 h-4 text-green-600 flex-shrink-0" />
                3 devis minimum, même gamme de produit
              </div>
              <div className="flex items-center gap-2 text-[#1a1a1a]/80">
                <Check className="w-4 h-4 text-green-600 flex-shrink-0" />
                Compare prix + délai paiement + franco de port
              </div>
              <div className="flex items-center gap-2 text-[#1a1a1a]/80">
                <Check className="w-4 h-4 text-green-600 flex-shrink-0" />
                Renégocie chaque 6 mois (volumes évoluent)
              </div>
            </div>
          </div>

          <div className="bg-green-500/10 rounded-2xl p-5 border border-green-500/30">
            <p className="text-green-700 text-sm font-bold mb-3">💬 Ce que tu peux négocier :</p>
            <div className="space-y-2 text-sm text-[#1a1a1a]/85">
              <div className="flex justify-between">
                <span>Prix unitaire</span>
                <span className="text-green-600 font-bold">-3 à -8%</span>
              </div>
              <div className="flex justify-between">
                <span>Délai paiement (15j → 45j)</span>
                <span className="text-green-600 font-bold">+1 mois de tréso</span>
              </div>
              <div className="flex justify-between">
                <span>Franco de port (livraison incluse)</span>
                <span className="text-green-600 font-bold">-15€/livraison</span>
              </div>
              <div className="flex justify-between">
                <span>Remise de fin d'année (RFA)</span>
                <span className="text-green-600 font-bold">-2 à -5% total</span>
              </div>
            </div>
          </div>

          <div className="bg-orange-500/10 rounded-2xl p-5 border border-orange-500/30">
            <p className="text-orange-700 text-sm font-bold mb-3">📦 Template bon de commande</p>
            <div className="bg-[#f0ede8] rounded-lg p-3 text-xs text-[#1a1a1a]/80 font-mono space-y-1">
              <p>Produit | Quantité | Prix unitaire | Total</p>
              <p className="text-[#1a1a1a]/50">— — — — — — — — — — — — —</p>
              <p>Bœuf haché | 10kg | 8,50€ | 85€</p>
              <p>Salade | 5 bacs | 3,20€ | 16€</p>
              <p className="text-[#1a1a1a]/50">...</p>
              <p className="text-orange-700">Total commande : 287,50€ HT</p>
            </div>
          </div>

          <div className="bg-red-500/10 rounded-xl p-4 border border-red-500/30">
            <p className="text-red-700 text-sm font-bold mb-1">🚨 Stock mini :</p>
            <p className="text-[#1a1a1a]/80 text-sm">Ne descends jamais sous 3 jours de conso sur les produits critiques (viande, pain, légumes frais). Rupture = clients perdus.</p>
          </div>
        </div>
      )
    },

    // SCREEN 22 - STOCKS & GASPILLAGE
    {
      id: 'stocks',
      title: 'STOCKS & GASPI',
      content: (
        <div className="space-y-6">
          <div>
            <p className="text-yellow-600 text-xs font-bold uppercase tracking-wider mb-2">Achats & stock · 2/2</p>
            <h2 className="text-3xl font-black text-[#1a1a1a] leading-tight">
              Le gaspillage, c'est <span className="text-orange-500">-3% de ton CA.</span>
            </h2>
            <p className="text-[#1a1a1a]/60 text-sm mt-2">L'industrie tourne à 8-10%. Tu peux diviser ça par 3.</p>
          </div>

          <div className="bg-white border border-[#e0dbd4] rounded-2xl p-5 border border-[#e0dbd4]">
            <p className="text-orange-700 text-sm font-bold mb-3">🗓️ Inventaire hebdo (10 min)</p>
            <p className="text-[#1a1a1a]/80 text-sm mb-3">Chaque dimanche soir ou lundi matin :</p>
            <div className="space-y-1 text-sm text-[#1a1a1a]/80">
              <p>• Compte physiquement les stocks critiques</p>
              <p>• Note ce qui a été jeté (raison + quantité)</p>
              <p>• Calcule l'écart entre achats et consommé</p>
            </div>
          </div>

          <div className="bg-blue-500/10 rounded-2xl p-5 border border-blue-500/30">
            <p className="text-blue-700 text-sm font-bold mb-3">🔄 Règle FIFO</p>
            <p className="text-[#1a1a1a]/80 text-sm mb-2">First In, First Out. Le plus ancien sort en premier.</p>
            <div className="bg-[#f0ede8] rounded-lg p-3 text-xs text-[#1a1a1a]/70">
              → Chaque livraison : note la date sur l'emballage au marqueur. Place devant ce qui est le plus ancien.
            </div>
          </div>

          <div className="bg-red-500/10 rounded-2xl p-5 border border-red-500/30">
            <p className="text-red-700 text-sm font-bold mb-3">🗑️ Top 3 sources de gaspi</p>
            <div className="space-y-2">
              <div className="bg-[#f0ede8] rounded-lg p-3">
                <p className="text-[#1a1a1a] font-bold text-sm">1. Sur-portionnage</p>
                <p className="text-[#1a1a1a]/60 text-xs">Pèse tes portions. 10g en trop × 100 couverts = 1kg/jour de gaspi.</p>
              </div>
              <div className="bg-[#f0ede8] rounded-lg p-3">
                <p className="text-[#1a1a1a] font-bold text-sm">2. Mauvaise rotation</p>
                <p className="text-[#1a1a1a]/60 text-xs">Produits périmés au fond du frigo. FIFO corrige ça.</p>
              </div>
              <div className="bg-[#f0ede8] rounded-lg p-3">
                <p className="text-[#1a1a1a] font-bold text-sm">3. Mauvais stockage</p>
                <p className="text-[#1a1a1a]/60 text-xs">Température frigo mal réglée, déshydratation, cross-contamination.</p>
              </div>
            </div>
          </div>

          <div className="bg-green-500/10 rounded-xl p-4 border border-green-500/30">
            <p className="text-green-700 text-sm font-bold mb-1">💡 Recycle intelligemment :</p>
            <p className="text-[#1a1a1a]/80 text-sm">Pain rassis → pain perdu au dessert. Légumes défraîchis → soupe du jour. Rien ne se jette sans réfléchir.</p>
          </div>
        </div>
      )
    },

    // SCREEN 23 - ONBOARDING (Maison Avocado detailed)
    {
      id: 'onboarding',
      title: 'ONBOARDING AVOCADO',
      content: (
        <div className="space-y-6">
          <div>
            <p className="text-purple-600 text-xs font-bold uppercase tracking-wider mb-2">Ma méthode Maison Avocado · Équipe 1/2</p>
            <h2 className="text-3xl font-black text-[#1a1a1a] leading-tight">
              À son démarrage, <span className="text-orange-500">il sait exactement quoi faire.</span>
            </h2>
            <p className="text-[#1a1a1a]/60 text-sm mt-2">Zéro ambiguïté. Zéro "tu feras ce qu'on te dira". Un protocole précis.</p>
          </div>

          {/* OUVERTURE ENSEIGNE */}
          <div className="bg-white rounded-2xl p-5 border border-[#e0dbd4]">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 rounded-lg bg-orange-500/15 flex items-center justify-center">
                <Settings className="w-4 h-4 text-orange-600" />
              </div>
              <h3 className="text-[#1a1a1a] font-bold">Protocole ouverture enseigne</h3>
            </div>
            <div className="space-y-2">
              {[
                'Nettoyage des tables (une par une, produit + microfibre)',
                'Nettoyage du sol (toujours avant le service, toujours)',
                'Remplir le frigo → il doit être plein avant chaque service',
                'Bar : check des bouteilles 3 à 4 fois dans la journée',
                'Bar : nettoyage complet 3 à 4 fois dans la journée',
                'Toutes les surfaces propres. Toujours. Toujours. Toujours.',
              ].map((t, i) => (
                <div key={i} className="flex items-start gap-2 text-sm">
                  <Check className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                  <span className="text-[#1a1a1a]/80">{t}</span>
                </div>
              ))}
            </div>
          </div>

          {/* CUISINE */}
          <div className="bg-white rounded-2xl p-5 border border-[#e0dbd4]">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 rounded-lg bg-orange-500/15 flex items-center justify-center">
                <span className="text-lg">🍳</span>
              </div>
              <h3 className="text-[#1a1a1a] font-bold">Protocole cuisine — dosages</h3>
            </div>
            <div className="bg-[#f0ede8] rounded-xl p-4 text-sm text-[#1a1a1a]/85 space-y-2">
              <p><span className="font-bold">Pré-dosages en amont :</span> chaque sauce, chaque portion est dosée la veille dans des ramequins.</p>
              <p><span className="font-bold">Conséquence :</span> toujours le même goût, toujours la même quantité, quel que soit le cuisinier.</p>
              <p className="text-orange-700 font-bold pt-2 border-t border-[#e0dbd4]">👉 Les fiches techniques sont précises, rédigées en amont par le patron. Non négociables.</p>
            </div>
          </div>

          {/* PROGRESSION J1-J7 */}
          <div className="bg-[#f0ede8] rounded-2xl p-5 border border-[#e0dbd4]">
            <p className="text-[#1a1a1a] text-xs font-bold uppercase tracking-wider mb-3">Progression J1 → J7</p>
            <div className="space-y-2">
              {[
                { d: 'J1 matin', t: 'Immersion : tour, équipe, règles d\'or' },
                { d: 'J1 service', t: 'Observation en binôme avec un ancien' },
                { d: 'J2-J3', t: 'Pratique en double — un ancien valide chaque étape' },
                { d: 'J4-J5', t: 'Autonomie sur une zone définie' },
                { d: 'J7', t: '1-à-1 30 min avec toi — feedback + objectifs S2' },
              ].map((d, i) => (
                <div key={i} className="flex gap-3 items-start bg-white rounded-lg p-2 border border-[#e0dbd4]">
                  <span className="text-orange-600 text-xs font-bold font-mono bg-orange-50 rounded px-2 py-0.5 border border-orange-200 flex-shrink-0">{d.d}</span>
                  <span className="text-[#1a1a1a]/80 text-xs">{d.t}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-orange-50 rounded-xl p-4 border border-orange-200">
            <p className="text-orange-700 text-sm font-bold mb-1">🎯 Pourquoi ça marche chez Avocado :</p>
            <p className="text-[#1a1a1a]/80 text-sm">Le nouveau n'a pas à "comprendre". Il exécute un protocole. Résultat : qualité constante, peu d'abandon, équipe qui tient dans le temps.</p>
          </div>
        </div>
      )
    },

    // SCREEN 24 - BRIEFING QUOTIDIEN
    {
      id: 'briefing',
      title: 'BRIEFING',
      content: (
        <div className="space-y-6">
          <div>
            <p className="text-purple-600 text-xs font-bold uppercase tracking-wider mb-2">Équipe · 2/2</p>
            <h2 className="text-3xl font-black text-[#1a1a1a] leading-tight">
              10 minutes qui <span className="text-orange-500">changent ton service.</span>
            </h2>
            <p className="text-[#1a1a1a]/60 text-sm mt-2">Briefing quotidien avant chaque service. Non négociable.</p>
          </div>

          <div className="bg-gradient-to-br from-white/5 to-transparent rounded-2xl p-5 border border-[#e0dbd4]">
            <p className="text-orange-700 text-sm font-bold uppercase tracking-wider mb-4">Le déroulé (10 min)</p>
            <div className="space-y-3">
              {[
                { t: '2 min', x: 'Plats du jour', d: 'Le chef présente. Si possible, l\'équipe goûte.' },
                { t: '1 min', x: 'Ruptures & 86', d: 'Ce qui est en rupture ou retiré du service.' },
                { t: '3 min', x: 'Objectif du service', d: 'Ex : pousser la formule du soir, 10 entrées vendues, +2€ panier moyen.' },
                { t: '2 min', x: 'Un positif de la veille', d: 'Ce qui a bien marché. Nom d\'un serveur cité.' },
                { t: '2 min', x: 'Qui fait quoi', d: 'Runner, bar, terrasse, rangs. Zéro ambiguïté.' },
              ].map((s, i) => (
                <div key={i} className="flex gap-3 items-start bg-[#f0ede8] rounded-lg p-3">
                  <span className="text-orange-600 font-mono text-xs font-bold bg-orange-500/20 rounded px-2 py-1 flex-shrink-0">{s.t}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-[#1a1a1a] font-bold text-sm">{s.x}</p>
                    <p className="text-[#1a1a1a]/60 text-xs mt-0.5">{s.d}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-green-500/10 rounded-xl p-4 border border-green-500/30">
            <p className="text-green-700 text-sm font-bold mb-2">✅ Résultat concret :</p>
            <div className="space-y-1 text-sm text-[#1a1a1a]/80">
              <p>• Moins d'erreurs en salle</p>
              <p>• +10-15% de ventes additionnelles (si objectif briefé)</p>
              <p>• Équipe alignée, moins de stress</p>
              <p>• Tu détectes les tensions AVANT qu'elles explosent</p>
            </div>
          </div>

          <button
            onClick={() => toggleAction('briefing', 'done')}
            className={`w-full p-4 rounded-xl flex items-center justify-center gap-2 font-bold transition-all ${
              completedActions['briefing-done'] ? 'bg-green-500 text-white' : 'bg-[#e0dbd4] text-[#1a1a1a] hover:bg-[#d4cfc7]'
            }`}
          >
            <Check className="w-5 h-5" />
            {completedActions['briefing-done'] ? 'Briefing instauré ✓' : 'J\'instaure le briefing demain'}
          </button>
        </div>
      )
    },

    // SCREEN 25 - GOOGLE & AVIS
    {
      id: 'avis-google',
      title: 'AVIS GOOGLE',
      content: (
        <div className="space-y-6">
          <div>
            <p className="text-cyan-700 text-xs font-bold uppercase tracking-wider mb-2">Clients & marketing · 1/2</p>
            <h2 className="text-3xl font-black text-[#1a1a1a] leading-tight">
              Google, c'est ton <span className="text-orange-500">2ème chef de salle.</span>
            </h2>
            <p className="text-[#1a1a1a]/60 text-sm mt-2">Tu le gères ou il te gère. Objectif : 4.5+ étoiles.</p>
          </div>

          <div className="bg-white border border-[#e0dbd4] rounded-2xl p-5 border border-[#e0dbd4]">
            <p className="text-orange-700 text-sm font-bold mb-3">📣 Comment demander un avis (sans être relou)</p>
            <div className="space-y-2">
              <div className="bg-[#f0ede8] rounded-lg p-3 text-sm">
                <p className="text-[#1a1a1a]/60 text-xs uppercase tracking-wider mb-1">En fin de service :</p>
                <p className="text-[#1a1a1a] italic">"Si vous avez passé un bon moment, un avis Google nous aide énormément. Ça prend 30 secondes."</p>
              </div>
              <div className="bg-[#f0ede8] rounded-lg p-3 text-sm">
                <p className="text-[#1a1a1a]/60 text-xs uppercase tracking-wider mb-1">Sur le ticket / QR code :</p>
                <p className="text-[#1a1a1a] italic">"Ton avis fait vivre notre resto → [QR code]"</p>
              </div>
            </div>
          </div>

          <div className="bg-green-500/10 rounded-2xl p-5 border border-green-500/30">
            <p className="text-green-700 text-sm font-bold mb-3">✅ Répondre à 100% des avis en 48h</p>
            <div className="space-y-2 text-sm text-[#1a1a1a]/85">
              <p><span className="text-green-600 font-bold">Positif</span> : "Merci [prénom] ! À très vite 🙏"</p>
              <p><span className="text-red-600 font-bold">Négatif</span> : voir template ci-dessous</p>
            </div>
          </div>

          <div className="bg-red-500/10 rounded-2xl p-5 border border-red-500/30">
            <p className="text-red-700 text-sm font-bold mb-3">📝 Template réponse avis négatif</p>
            <div className="bg-[#f0ede8] rounded-lg p-3 text-sm text-[#1a1a1a]/80 italic space-y-1">
              <p>"Bonjour [prénom],</p>
              <p>Merci d'avoir pris le temps de nous écrire. Je suis [ton nom], le gérant.</p>
              <p>[Reconnaît l'erreur précise — ne nie jamais]</p>
              <p>J'aimerais comprendre ce qui s'est passé et rectifier. Peux-tu m'écrire en privé au [tel / email] ?</p>
              <p>À bientôt."</p>
            </div>
            <p className="text-[#1a1a1a]/60 text-xs mt-3">👉 Règle d'or : jamais d'excuse bidon. Jamais de défensive. Sincérité + action.</p>
          </div>

          <div className="bg-orange-500/10 rounded-xl p-4 border border-orange-500/30">
            <p className="text-orange-700 text-sm font-bold mb-1">📈 Impact :</p>
            <p className="text-[#1a1a1a]/80 text-sm">Passer de 4.0 à 4.5 étoiles = +10 à +15% de nouveaux clients. Google priorise les restos bien notés.</p>
          </div>
        </div>
      )
    },

    // SCREEN 26 - FIDELISATION SIMPLE
    {
      id: 'fidelisation-simple',
      title: 'FIDÉLISATION',
      content: (
        <div className="space-y-6">
          <div>
            <p className="text-cyan-700 text-xs font-bold uppercase tracking-wider mb-2">Clients & marketing · 2/2</p>
            <h2 className="text-3xl font-black text-[#1a1a1a] leading-tight">
              Pas besoin d'app. <span className="text-orange-500">Juste 3 gestes.</span>
            </h2>
            <p className="text-[#1a1a1a]/60 text-sm mt-2">Fidéliser, c'est faire revenir. Pas collectionner des data.</p>
          </div>

          <div className="space-y-3">
            <div className="bg-white border border-[#e0dbd4] rounded-2xl p-5 border border-[#e0dbd4]">
              <div className="flex items-center gap-2 mb-2">
                <span className="w-8 h-8 rounded-lg bg-orange-500/20 flex items-center justify-center text-orange-600 font-black text-sm">1</span>
                <p className="text-[#1a1a1a] font-bold">Carte de fidélité papier</p>
              </div>
              <p className="text-[#1a1a1a]/70 text-sm mb-3">La plus vieille technique du monde. Elle marche toujours.</p>
              <div className="bg-[#f0ede8] rounded-lg p-3 text-sm">
                <p className="text-orange-700 font-mono text-xs">Exemples :</p>
                <p className="text-[#1a1a1a]/80 mt-1">• 10 cafés tamponnés = 1 offert</p>
                <p className="text-[#1a1a1a]/80">• 10 menus = 1 dessert offert</p>
                <p className="text-[#1a1a1a]/80">• 5 pizzas = -50% sur la 6ème</p>
              </div>
            </div>

            <div className="bg-white border border-[#e0dbd4] rounded-2xl p-5 border border-[#e0dbd4]">
              <div className="flex items-center gap-2 mb-2">
                <span className="w-8 h-8 rounded-lg bg-orange-500/20 flex items-center justify-center text-orange-600 font-black text-sm">2</span>
                <p className="text-[#1a1a1a] font-bold">SMS anniversaire</p>
              </div>
              <p className="text-[#1a1a1a]/70 text-sm mb-3">Base ultra simple : prénom + date anniv + tel.</p>
              <div className="bg-[#f0ede8] rounded-lg p-3 text-sm">
                <p className="text-orange-700 font-mono text-xs">Template SMS :</p>
                <p className="text-[#1a1a1a]/80 italic mt-1">"Bon anniversaire [prénom] 🎂 ! Ton dessert est offert ce mois-ci chez [resto]. À très vite 🙏"</p>
              </div>
            </div>

            <div className="bg-white border border-[#e0dbd4] rounded-2xl p-5 border border-[#e0dbd4]">
              <div className="flex items-center gap-2 mb-2">
                <span className="w-8 h-8 rounded-lg bg-orange-500/20 flex items-center justify-center text-orange-600 font-black text-sm">3</span>
                <p className="text-[#1a1a1a] font-bold">Reconnaître les VIP</p>
              </div>
              <p className="text-[#1a1a1a]/70 text-sm mb-3">Client qui vient 3+ fois par mois = VIP. Surprend-le.</p>
              <div className="bg-[#f0ede8] rounded-lg p-3 text-sm text-[#1a1a1a]/80">
                <p>→ Apéro offert à la 4ème visite du mois</p>
                <p>→ Table préférée réservée d'office</p>
                <p>→ Appellation par son prénom (= roi)</p>
              </div>
            </div>
          </div>

          <div className="bg-green-500/10 rounded-xl p-4 border border-green-500/30">
            <p className="text-green-700 text-sm font-bold mb-1">💰 Le calcul :</p>
            <p className="text-[#1a1a1a]/80 text-sm">Un client fidèle dépense 3 à 5x plus qu'un client unique sur un an. Et il parle de toi à 2-3 personnes.</p>
          </div>
        </div>
      )
    },

    // ============ SCALING — AVOCADO MULTI-CA SYSTEM ============

    // SCREEN 27 - ORGANISATION DU TEMPS PATRON (80/10/10)
    {
      id: 'temps-patron',
      title: 'TON TEMPS DE PATRON',
      content: (
        <div className="space-y-6">
          <div>
            <p className="text-orange-700 text-xs font-bold uppercase tracking-wider mb-2">Dirigeant · 1/6</p>
            <h2 className="text-3xl font-black text-[#1a1a1a] leading-tight">
              80% / 10% / 10%. <span className="text-orange-500">Pas 100% de cuisine.</span>
            </h2>
            <p className="text-[#1a1a1a]/60 text-sm mt-2">Si tu es dans le feu, personne n'allume les projecteurs. Change de posture.</p>
          </div>

          {/* 80% */}
          <div className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-2xl p-5 border border-orange-200">
            <div className="flex items-baseline gap-3 mb-3">
              <span className="text-5xl font-black text-orange-500">80%</span>
              <p className="text-[#1a1a1a] font-bold">Développement</p>
            </div>
            <div className="space-y-1 text-sm text-[#1a1a1a]/80">
              <p>• Réseaux sociaux (scripts + tournage + post)</p>
              <p>• Ventes additionnelles, offres, partenariats</p>
              <p>• Stratégie de croissance (nouveaux canaux, nouvelle carte)</p>
              <p>• Faire grandir ton affaire (franchise, 2e établissement, ouverture)</p>
            </div>
          </div>

          {/* 10% contrôle */}
          <div className="bg-white rounded-2xl p-5 border border-[#e0dbd4]">
            <div className="flex items-baseline gap-3 mb-3">
              <span className="text-4xl font-black text-[#1a1a1a]">10%</span>
              <p className="text-[#1a1a1a] font-bold">Contrôle quotidien</p>
            </div>
            <div className="space-y-1 text-sm text-[#1a1a1a]/75">
              <p>• Check des KPIs de la veille (5 min le matin)</p>
              <p>• Tour du resto avant l'ouverture (propreté, stocks)</p>
              <p>• Écoute signaux faibles équipe</p>
            </div>
          </div>

          {/* 10% formation/équipe */}
          <div className="bg-white rounded-2xl p-5 border border-[#e0dbd4]">
            <div className="flex items-baseline gap-3 mb-3">
              <span className="text-4xl font-black text-[#1a1a1a]">10%</span>
              <p className="text-[#1a1a1a] font-bold">Formation & équipe</p>
            </div>
            <div className="space-y-1 text-sm text-[#1a1a1a]/75">
              <p>• Briefing quotidien (10 min)</p>
              <p>• 1-à-1 mensuels avec chaque membre</p>
              <p>• Montée en compétences (nouveaux protocoles)</p>
            </div>
          </div>

          <div className="bg-red-50 rounded-xl p-4 border border-red-200">
            <p className="text-red-700 text-sm font-bold mb-1">🚨 Si tu es à 80% en cuisine :</p>
            <p className="text-[#1a1a1a]/80 text-sm">Tu n'es pas patron. Tu es un employé cher. Et ton resto ne grandira jamais.</p>
          </div>

          <div className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-xl p-4 border border-purple-200">
            <p className="text-purple-700 text-sm font-bold mb-1">💎 Chez Avocado :</p>
            <p className="text-[#1a1a1a]/80 text-sm">J'ai un système tout-en-un propre à moi qui permet de tenir ce rythme sans craquer. Je te le montre si tu prends l'accompagnement.</p>
          </div>
        </div>
      )
    },

    // SCREEN 28 - QR CODES SUR TABLES
    {
      id: 'qr-tables',
      title: 'QR CODES TABLES',
      content: (
        <div className="space-y-6">
          <div>
            <p className="text-orange-700 text-xs font-bold uppercase tracking-wider mb-2">Dirigeant · 2/6 — Avocado</p>
            <h2 className="text-3xl font-black text-[#1a1a1a] leading-tight">
              QR code sur les tables = <span className="text-orange-500">équipe qui vend plus.</span>
            </h2>
            <p className="text-[#1a1a1a]/60 text-sm mt-2">Chez Avocado, les serveurs se concentrent sur ce qui rapporte.</p>
          </div>

          <div className="bg-white rounded-2xl p-5 border border-[#e0dbd4]">
            <p className="text-[#1a1a1a] font-bold text-sm mb-3">Comment ça marche :</p>
            <div className="space-y-2 text-sm text-[#1a1a1a]/80">
              <div className="flex items-start gap-3">
                <span className="w-6 h-6 rounded bg-orange-500/15 text-orange-700 text-xs font-black flex items-center justify-center flex-shrink-0">1</span>
                <span>Le client s'assoit, scanne le QR code</span>
              </div>
              <div className="flex items-start gap-3">
                <span className="w-6 h-6 rounded bg-orange-500/15 text-orange-700 text-xs font-black flex items-center justify-center flex-shrink-0">2</span>
                <span>Il voit la carte, ajoute au panier</span>
              </div>
              <div className="flex items-start gap-3">
                <span className="w-6 h-6 rounded bg-orange-500/15 text-orange-700 text-xs font-black flex items-center justify-center flex-shrink-0">3</span>
                <span>Il paye en Apple Pay directement sur son tel</span>
              </div>
              <div className="flex items-start gap-3">
                <span className="w-6 h-6 rounded bg-green-500/15 text-green-700 text-xs font-black flex items-center justify-center flex-shrink-0">✓</span>
                <span className="text-green-700 font-bold">Rapide, simple, efficace.</span>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-5 border border-green-200">
            <p className="text-green-700 text-sm font-bold mb-3">🎯 Ce que tu gagnes :</p>
            <div className="space-y-2 text-sm text-[#1a1a1a]/80">
              <p>• <span className="font-bold">Moins d'actions redondantes</span> pour tes serveurs (prise de commande, addition, encaissement)</p>
              <p>• <span className="font-bold">L'équipe en salle se concentre sur la fidélisation</span> et les ventes additionnelles</p>
              <p>• <span className="font-bold">Plus de temps passé avec les clients</span> = plus d'upsell, plus d'avis Google, plus de retours</p>
              <p>• <span className="font-bold">Tickets payés direct</span> = moins d'erreurs, moins de vols, cash instantané</p>
            </div>
          </div>

          <div className="bg-orange-50 rounded-xl p-4 border border-orange-200">
            <p className="text-orange-700 text-sm font-bold mb-1">💡 À implémenter :</p>
            <p className="text-[#1a1a1a]/80 text-sm">Stickers QR collés sur chaque table, pointant vers ta carte digitale (Sunday, Innovorder, ou ton propre site). Coût : 20€ pour 30 stickers. ROI : immédiat.</p>
          </div>
        </div>
      )
    },

    // SCREEN 29 - CLICK & COLLECT
    {
      id: 'click-collect',
      title: 'CLICK & COLLECT',
      content: (
        <div className="space-y-6">
          <div>
            <p className="text-orange-700 text-xs font-bold uppercase tracking-wider mb-2">Dirigeant · 3/6 — Avocado</p>
            <h2 className="text-3xl font-black text-[#1a1a1a] leading-tight">
              Click & Collect — <span className="text-orange-500">ton CA avant le rush.</span>
            </h2>
            <p className="text-[#1a1a1a]/60 text-sm mt-2">Chez Avocado, 10-15 commandes déjà prêtes AVANT l'ouverture.</p>
          </div>

          <div className="bg-white rounded-2xl p-5 border border-[#e0dbd4]">
            <p className="text-[#1a1a1a] font-bold text-sm mb-3">Ma méthode exacte :</p>
            <div className="space-y-3">
              <div className="bg-[#f0ede8] rounded-xl p-4">
                <p className="text-[#1a1a1a] font-bold text-sm mb-1">Outil : Clickit (ou équivalent)</p>
                <p className="text-[#1a1a1a]/70 text-xs">Plateforme de click & collect branchée sur ta carte digitale.</p>
              </div>
              <div className="bg-[#f0ede8] rounded-xl p-4">
                <p className="text-[#1a1a1a] font-bold text-sm mb-1">Code promo : -10%</p>
                <p className="text-[#1a1a1a]/70 text-xs">Incite fortement à commander en click & collect. 10% de moins = marge toujours positive.</p>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-5 border border-green-200">
            <p className="text-green-700 text-sm font-bold mb-3">📊 Pourquoi c'est puissant :</p>
            <div className="space-y-2 text-sm text-[#1a1a1a]/80">
              <p>✅ <span className="font-bold">Pas besoin de plus de personnes en salle</span> — les commandes sont faites en cuisine en amont</p>
              <p>✅ <span className="font-bold">À midi avant le rush</span>, tu as déjà 10-15 commandes prêtes à partir</p>
              <p>✅ <span className="font-bold">CA payé d'avance</span> = tes équipes sont déjà rentabilisées pour la journée</p>
              <p>✅ <span className="font-bold">Marge préservée</span> — pas de commission plateforme comme Uber</p>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-4 border border-[#e0dbd4]">
            <p className="text-[#1a1a1a] font-bold text-sm mb-2">💰 Le calcul Avocado type :</p>
            <div className="space-y-1 text-sm text-[#1a1a1a]/80">
              <p>12 commandes × 22€ moyen = <span className="font-bold">264€ de CA</span></p>
              <p>-10% code promo → <span className="font-bold">237€ encaissés</span></p>
              <p className="text-green-700 font-bold pt-2 border-t border-[#e0dbd4]">= Tes 2 serveurs du midi sont payés, avant même le premier client en salle.</p>
            </div>
          </div>
        </div>
      )
    },

    // SCREEN 30 - TABLETTES À EMPORTER (4 FLÈCHES)
    {
      id: 'tablettes',
      title: 'TABLETTES',
      content: (
        <div className="space-y-6">
          <div>
            <p className="text-orange-700 text-xs font-bold uppercase tracking-wider mb-2">Dirigeant · 4/6 — Avocado</p>
            <h2 className="text-3xl font-black text-[#1a1a1a] leading-tight">
              4 flèches à ton arc. <span className="text-orange-500">Pas une seule.</span>
            </h2>
            <p className="text-[#1a1a1a]/60 text-sm mt-2">Si tu dépends d'un seul CA, tu es dépendant de ce CA. Diversifie.</p>
          </div>

          <div className="bg-white rounded-2xl p-5 border border-[#e0dbd4]">
            <p className="text-[#1a1a1a] font-bold text-sm mb-3">Chez Avocado, je combine 4 sources de CA :</p>
            <div className="space-y-2">
              {[
                { n: '1', t: 'Salle (sur place)', d: 'Client qui vient physiquement + QR code' },
                { n: '2', t: 'Click & Collect', d: 'Clickit avec code -10% — CA avant le rush' },
                { n: '3', t: 'Tablettes à emporter', d: 'Tab Resto — bornes internes pour commandes à emporter' },
                { n: '4', t: 'Livraison (Uber Eats)', d: 'Pièce d\'identité + acquisition de nouveaux clients' },
              ].map((s) => (
                <div key={s.n} className="flex items-start gap-3 bg-[#f0ede8] rounded-xl p-3">
                  <span className="w-8 h-8 rounded-lg bg-orange-500 text-white text-sm font-black flex items-center justify-center flex-shrink-0">{s.n}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-[#1a1a1a] font-bold text-sm">{s.t}</p>
                    <p className="text-[#1a1a1a]/60 text-xs">{s.d}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-2xl p-5 border border-orange-200">
            <p className="text-orange-700 text-sm font-bold mb-2">🎯 Pourquoi 4 flèches :</p>
            <div className="space-y-1 text-sm text-[#1a1a1a]/80">
              <p>• Si la salle est calme un mardi → ton click & collect compense</p>
              <p>• Si ton secteur subit un choc (grève, météo) → Uber Eats tient</p>
              <p>• Si Uber Eats change ses commissions → tes autres canaux absorbent</p>
              <p className="text-orange-700 font-bold pt-2 mt-2 border-t border-orange-200">👉 Tu deviens anti-fragile. Plus de dépendance à un seul flux.</p>
            </div>
          </div>

          <div className="bg-red-50 rounded-xl p-4 border border-red-200">
            <p className="text-red-700 text-sm font-bold mb-1">🚨 Si tu as 1 seule source de CA :</p>
            <p className="text-[#1a1a1a]/80 text-sm">Tu n'as pas un restaurant. Tu as un point de vente fragile qui peut s'écrouler en 30 jours.</p>
          </div>
        </div>
      )
    },

    // SCREEN 31 - STRATÉGIE UBER EATS
    {
      id: 'uber-eats',
      title: 'UBER EATS — CARTE',
      content: (
        <div className="space-y-6">
          <div>
            <p className="text-orange-700 text-xs font-bold uppercase tracking-wider mb-2">Dirigeant · 5/6 — Avocado</p>
            <h2 className="text-3xl font-black text-[#1a1a1a] leading-tight">
              Uber Eats, c'est <span className="text-orange-500">ta carte d'identité.</span>
            </h2>
            <p className="text-[#1a1a1a]/60 text-sm mt-2">Pas ton gros CA. Ton canal d'acquisition. Nuance capitale.</p>
          </div>

          <div className="bg-white rounded-2xl p-5 border border-[#e0dbd4]">
            <p className="text-[#1a1a1a] font-bold text-sm mb-3">✅ Les 4 règles de la carte Uber Eats :</p>
            <div className="space-y-3">
              <div className="bg-[#f0ede8] rounded-xl p-3">
                <p className="text-orange-700 font-bold text-xs uppercase tracking-wider mb-1">Règle 1 — Carte différente</p>
                <p className="text-[#1a1a1a]/80 text-sm">Ta carte Uber Eats ≠ ta carte en salle. Courte. Simple. Efficace.</p>
              </div>
              <div className="bg-[#f0ede8] rounded-xl p-3">
                <p className="text-orange-700 font-bold text-xs uppercase tracking-wider mb-1">Règle 2 — Plats rentables</p>
                <p className="text-[#1a1a1a]/80 text-sm">Uniquement les plats où tu fais le plus de marge. Les autres, hors carte.</p>
              </div>
              <div className="bg-[#f0ede8] rounded-xl p-3">
                <p className="text-orange-700 font-bold text-xs uppercase tracking-wider mb-1">Règle 3 — Best-sellers signatures</p>
                <p className="text-[#1a1a1a]/80 text-sm">1 ou 2 plats signatures qui sont TA DA (direction artistique). Les gens viennent pour toi grâce à eux.</p>
              </div>
              <div className="bg-[#f0ede8] rounded-xl p-3">
                <p className="text-orange-700 font-bold text-xs uppercase tracking-wider mb-1">Règle 4 — Prix +15 à +20%</p>
                <p className="text-[#1a1a1a]/80 text-sm">Tu augmentes tes prix Uber de 15-20%. La commission Uber (30%) rentre DANS ce mark-up. Tu gardes ta marge.</p>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-5 border border-green-200">
            <p className="text-green-700 text-sm font-bold mb-3">🎁 Les offres Uber Eats (important) :</p>
            <div className="space-y-2 text-sm text-[#1a1a1a]/80">
              <p>• <span className="font-bold">Active des offres</span> (1 acheté = 1 offert, -20% sur X, etc.)</p>
              <p>• Uber Eats boost ta visibilité quand tu as une offre active</p>
              <p>• Le mark-up de +15-20% absorbe la réduction + la commission</p>
            </div>
          </div>

          <div className="bg-orange-50 rounded-xl p-4 border border-orange-200">
            <p className="text-orange-700 text-sm font-bold mb-1">💡 Note sur les best-sellers :</p>
            <p className="text-[#1a1a1a]/80 text-sm">Tes best-sellers ne sont PAS les plats les plus commandés. Ce sont tes <span className="font-bold">produits à plus forte marge</span>. Tu orientes l'offre sur ce que TU veux vendre, pas sur ce que le marché te réclame sans valeur ajoutée.</p>
          </div>
        </div>
      )
    },

    // SCREEN 32 - UBER EATS POSITIONNEMENT
    {
      id: 'uber-eats-strategy',
      title: 'UBER EATS — POSITION',
      content: (
        <div className="space-y-6">
          <div>
            <p className="text-orange-700 text-xs font-bold uppercase tracking-wider mb-2">Dirigeant · 6/6 — Avocado</p>
            <h2 className="text-3xl font-black text-[#1a1a1a] leading-tight">
              Uber Eats ne sert pas à <span className="text-orange-500">gagner beaucoup.</span>
            </h2>
            <p className="text-[#1a1a1a]/60 text-sm mt-2">Il sert à te faire connaître. Change ton regard, change tes résultats.</p>
          </div>

          <div className="bg-white rounded-2xl p-5 border border-[#e0dbd4]">
            <p className="text-[#1a1a1a] font-bold text-sm mb-3">Ce que Uber Eats doit faire pour toi :</p>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg bg-green-500/15 flex items-center justify-center flex-shrink-0">
                  <Check className="w-4 h-4 text-green-700" />
                </div>
                <div>
                  <p className="text-[#1a1a1a] font-bold text-sm">Payer tes charges fixes</p>
                  <p className="text-[#1a1a1a]/60 text-xs">Loyer, abonnements, petit matériel. Pas ton bénéfice.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg bg-green-500/15 flex items-center justify-center flex-shrink-0">
                  <Check className="w-4 h-4 text-green-700" />
                </div>
                <div>
                  <p className="text-[#1a1a1a] font-bold text-sm">Développer ta clientèle</p>
                  <p className="text-[#1a1a1a]/60 text-xs">Les gens commandent, goûtent, reviennent en salle pour l'expérience.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg bg-green-500/15 flex items-center justify-center flex-shrink-0">
                  <Check className="w-4 h-4 text-green-700" />
                </div>
                <div>
                  <p className="text-[#1a1a1a] font-bold text-sm">Toucher ta cible idéale</p>
                  <p className="text-[#1a1a1a]/60 text-xs">Tu captes des clients dans un périmètre de 3-5 km que tu n'aurais jamais eus autrement.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg bg-green-500/15 flex items-center justify-center flex-shrink-0">
                  <Check className="w-4 h-4 text-green-700" />
                </div>
                <div>
                  <p className="text-[#1a1a1a] font-bold text-sm">Te faire reconnaître dans ton secteur</p>
                  <p className="text-[#1a1a1a]/60 text-xs">Être visible sur Uber Eats = être dans la tête des gens du quartier.</p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-orange-50 to-red-50 rounded-2xl p-5 border border-orange-200">
            <p className="text-orange-700 text-xs font-bold uppercase tracking-wider mb-2">🧠 Le bon état d'esprit</p>
            <p className="text-[#1a1a1a] text-lg font-bold leading-tight mb-2">
              "Uber Eats = mes charges payées.<br/>Mon CA salle = mon bénéfice."
            </p>
            <p className="text-[#1a1a1a]/70 text-sm">Quand tu raisonnes comme ça, tu arrêtes de te sentir volé par la commission. Et tu optimises la salle sans stress.</p>
          </div>

          <div className="bg-white rounded-xl p-4 border border-[#e0dbd4]">
            <p className="text-[#1a1a1a] font-bold text-sm mb-2">🎯 Ton action cette semaine :</p>
            <div className="space-y-1 text-sm text-[#1a1a1a]/80">
              <p>1. Refais ta carte Uber Eats → 10-15 plats max, les plus rentables</p>
              <p>2. Monte tes prix de 15-20% sur cette carte dédiée</p>
              <p>3. Active une offre (-20% ou 1+1)</p>
              <p>4. Check dans 30 jours : CA Uber couvre-t-il tes charges fixes ?</p>
            </div>
          </div>
        </div>
      )
    },

    // SCREEN 33 - FINAL — PLAN PERSONNALISÉ 30 JOURS
    {
      id: 'final',
      title: 'TON PLAN 30 JOURS',
      content: (() => {
        const firstName = userInputs.restaurantName || 'Ton resto';
        const fc = parseFloat(userInputs.foodCost) || 0;
        const sc = parseFloat(userInputs.staffCost) || 0;
        const ca = parseFloat(userInputs.monthlyCA) || 0;
        const team = parseInt(userInputs.teamSize) || 0;
        const obj = userInputs.mainObjective;
        const type = userInputs.restaurantType || 'restaurant';
        const city = userInputs.city || '';

        // Build prioritized actions per week based on objective + diagnostic data
        const week1 = [];
        const week2 = [];
        const week3 = [];
        const week4 = [];

        // Week 1 — ALWAYS: structure & KPIs (fondations)
        week1.push({ t: 'Crée ton tableau KPIs hebdo', d: '7 chiffres (CA, couverts, food cost, masse salariale, avis Google, extras, cash). 10 min lundi matin.' });
        if (fc >= 30 || !userInputs.foodCost) {
          week1.push({ t: 'Calcule le food cost de tes 10 plats les plus vendus', d: fc >= 35 ? `Urgence : ton food cost global est à ${fc}%. Identifie les 3 plats qui dépassent 35%.` : 'Pour savoir exactement où sont tes marges.' });
        }
        if (obj === 'equipe' || obj === 'temps') {
          week1.push({ t: 'Instaure le briefing quotidien 10 min', d: 'Avant chaque service. Non négociable. Plats du jour + objectif + qui fait quoi.' });
        } else {
          week1.push({ t: 'Note ton break-even', d: 'Charges fixes ÷ marge brute. Tu dois connaître ce chiffre par cœur.' });
        }

        // Week 2 — Fonction de l'objectif principal
        if (obj === 'ca') {
          week2.push({ t: 'Menu engineering — classe tes 15 plats', d: `Stars / Puzzles / Plowhorses / Dogs. Retire les dogs cette semaine.` });
          week2.push({ t: 'Brief upsell à ton équipe', d: `Scripts : "Avec supplément ?" / "Un dessert pour finir ?" / formule +3€. Objectif : +2€ panier moyen.` });
          week2.push({ t: 'Active une offre sur Uber Eats', d: `Carte dédiée (+15-20% prix). 1 offre active (-20% ou 1+1).` });
        } else if (obj === 'cout') {
          week2.push({ t: 'Demande 3 devis à tes fournisseurs concurrents', d: 'Viande, légumes, pain, boissons. Compare prix + délai paiement.' });
          if (fc >= 35) {
            week2.push({ t: `Retravaille les plats dont le food cost > 35%`, d: '+1-2€ de prix OU change fournisseur OU réduis la portion de 10%.' });
          } else {
            week2.push({ t: 'Mets en place FIFO sur tous les stocks', d: 'Date sur chaque livraison. Rotation stricte. Cible gaspillage <3%.' });
          }
          week2.push({ t: 'Inventaire hebdo + note tout ce qui est jeté', d: 'Lundi matin, 15 min. Le gaspillage invisible devient visible.' });
        } else if (obj === 'equipe') {
          week2.push({ t: 'Rédige le protocole d\'ouverture enseigne', d: 'Checklist précise : tables, sol, frigo, bar, bouteilles. Avocado-style.' });
          week2.push({ t: 'Mets en place les primes par paliers CA', d: ca ? `Base : ${Math.round(ca * 1.1)}€ = 150€/pers, ${Math.round(ca * 1.3)}€ = 200€/pers, ${Math.round(ca * 1.4)}€ = 250€/pers` : 'Paliers collectifs : 150€ / 200€ / 250€ par personne selon CA.' });
          week2.push({ t: 'Rédige tes fiches techniques précises', d: 'Pré-dosages en ramequins. Chaque plat, chaque sauce. Tu élimines les écarts.' });
        } else { // temps
          week2.push({ t: 'Délègue 3 tâches quotidiennes à ton équipe', d: 'Stocks, ouverture, fermeture, encaissement. Tu récupères 1h/jour minimum.' });
          week2.push({ t: 'Installe les QR codes sur les tables', d: 'Commande + paiement Apple Pay direct. Serveurs libérés pour la vente.' });
          week2.push({ t: 'Ratio 80/10/10 : audite ta semaine', d: 'Note chaque tâche. Identifie ce que tu fais en cuisine qui devrait être délégué.' });
        }

        // Week 3 — Scaling / CA
        week3.push({ t: 'Lance ton Click & Collect avec code -10%', d: 'Objectif : 10-15 commandes avant le rush midi. Ton CA du jour payé avant ouverture.' });
        week3.push({ t: 'Poste 1 reel/jour avec le format Hook/Rehook/Story/CTA', d: `Cible "${type}${city ? ' à ' + city : ''}". Tag ta ville. 3-5 hashtags locaux.` });
        week3.push({ t: 'Demande activement des avis Google', d: 'Script fin de service + QR code sur le ticket. Objectif : +10 avis 5★ cette semaine.' });

        // Week 4 — Optimisation / lock
        week4.push({ t: 'Audit complet : compare tes KPIs S1 vs S4', d: `Mesure l'impact réel. Si CA +10% ou food cost -2%, tu as déjà rentabilisé.` });
        if (obj === 'equipe' || team >= 3) {
          week4.push({ t: '1-à-1 avec chaque membre de l\'équipe', d: '30 min par personne. Ce qui va / ce qui coince / objectifs mois 2.' });
        }
        week4.push({ t: 'Active 2 automatisations IA', d: 'ChatGPT pour tes 7 posts + CapCut pour sous-titrer. Économie : 5h/semaine.' });
        week4.push({ t: 'Planifie le mois 2', d: 'Les 3 actions qui ont le plus impacté ton CA → systématise-les. Le reste → optimise.' });

        const weeks = [
          { n: 1, title: 'FONDATIONS', color: 'blue', actions: week1 },
          { n: 2, title: obj === 'ca' ? 'REVENUS' : obj === 'cout' ? 'MARGES' : obj === 'equipe' ? 'ÉQUIPE' : 'DÉLÉGATION', color: 'orange', actions: week2 },
          { n: 3, title: 'CROISSANCE', color: 'green', actions: week3 },
          { n: 4, title: 'OPTIMISATION', color: 'purple', actions: week4 },
        ];

        const objLabel = obj === 'ca' ? 'Augmenter ton CA'
          : obj === 'cout' ? 'Réduire tes coûts'
          : obj === 'equipe' ? 'Structurer ton équipe'
          : obj === 'temps' ? 'Libérer ton temps'
          : 'Action rapide';

        return (
          <div className="space-y-6">
            <div>
              <p className="text-orange-600 text-xs font-bold uppercase tracking-widest mb-3">Ton plan d'exécution · 30 jours</p>
              <h2 className="font-['Playfair_Display',serif] text-3xl md:text-4xl font-black text-[#1a1a1a] leading-[1.05] tracking-tight">
                {firstName}.
                <br/>
                <span className="text-orange-500">Voici ton plan.</span>
              </h2>
              <p className="text-[#1a1a1a]/70 text-sm mt-3">
                Généré selon tes réponses. Priorité : <span className="font-bold text-[#1a1a1a]">{objLabel}</span>.
              </p>
            </div>

            {/* Summary card */}
            {(userInputs.restaurantType || userInputs.city || userInputs.monthlyCA) && (
              <div className="bg-white rounded-2xl p-5 border border-[#e0dbd4]">
                <p className="text-xs font-bold uppercase tracking-wider text-[#1a1a1a]/60 mb-3">Ton profil</p>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  {userInputs.restaurantType && (
                    <div>
                      <p className="text-[#1a1a1a]/50 text-xs">Type</p>
                      <p className="text-[#1a1a1a] font-bold">{userInputs.restaurantType}</p>
                    </div>
                  )}
                  {userInputs.city && (
                    <div>
                      <p className="text-[#1a1a1a]/50 text-xs">Ville</p>
                      <p className="text-[#1a1a1a] font-bold">{userInputs.city}</p>
                    </div>
                  )}
                  {userInputs.monthlyCA && (
                    <div>
                      <p className="text-[#1a1a1a]/50 text-xs">CA mensuel</p>
                      <p className="text-[#1a1a1a] font-bold">{parseInt(userInputs.monthlyCA).toLocaleString('fr-FR')} €</p>
                    </div>
                  )}
                  {userInputs.teamSize && (
                    <div>
                      <p className="text-[#1a1a1a]/50 text-xs">Équipe</p>
                      <p className="text-[#1a1a1a] font-bold">{userInputs.teamSize} personnes</p>
                    </div>
                  )}
                  {userInputs.foodCost && (
                    <div>
                      <p className="text-[#1a1a1a]/50 text-xs">Food cost</p>
                      <p className={`font-bold ${fc >= 35 ? 'text-red-700' : fc >= 30 ? 'text-orange-600' : 'text-green-700'}`}>{fc}%</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Weekly plan */}
            <div className="space-y-4">
              {weeks.map((w) => {
                const colorMap = {
                  blue: { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-700', dot: 'bg-blue-500' },
                  orange: { bg: 'bg-orange-50', border: 'border-orange-200', text: 'text-orange-700', dot: 'bg-orange-500' },
                  green: { bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-700', dot: 'bg-green-500' },
                  purple: { bg: 'bg-purple-50', border: 'border-purple-200', text: 'text-purple-700', dot: 'bg-purple-500' },
                };
                const c = colorMap[w.color];
                return (
                  <div key={w.n} className={`${c.bg} rounded-2xl p-5 border ${c.border}`}>
                    <div className="flex items-center gap-2 mb-4">
                      <div className={`w-10 h-10 rounded-xl ${c.dot} text-white flex items-center justify-center font-black`}>S{w.n}</div>
                      <div>
                        <p className="text-[#1a1a1a]/50 text-xs font-bold uppercase tracking-wider">Semaine {w.n}</p>
                        <p className={`font-black text-lg ${c.text}`}>{w.title}</p>
                      </div>
                    </div>
                    <div className="space-y-2">
                      {w.actions.map((a, i) => (
                        <div key={i} className="bg-white rounded-xl p-3 border border-[#e0dbd4]">
                          <div className="flex items-start gap-2 mb-1">
                            <div className={`w-5 h-5 rounded-full ${c.dot} text-white text-xs font-black flex items-center justify-center flex-shrink-0 mt-0.5`}>{i + 1}</div>
                            <p className="text-[#1a1a1a] font-bold text-sm leading-snug">{a.t}</p>
                          </div>
                          <p className="text-[#1a1a1a]/60 text-xs ml-7">{a.d}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Vitesse d'exécution */}
            <div className="bg-gradient-to-br from-orange-50 to-red-50 rounded-2xl p-6 border border-orange-200">
              <p className="text-orange-700 text-xs font-bold uppercase tracking-widest mb-2">Règle d'or</p>
              <p className="text-2xl font-black text-[#1a1a1a] leading-tight">
                La différence entre ceux qui réussissent<br/>
                <span className="text-orange-500">et les autres ? La vitesse d'exécution.</span>
              </p>
              <p className="text-[#1a1a1a]/70 text-sm mt-2">Tu as 30 jours. Pas 3 mois. Commence S1 lundi matin.</p>
            </div>

            {/* Print + Export full plan CTAs */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <button
                onClick={handleExportFullPlan}
                data-testid="ebook-export-full-pdf"
                className="w-full py-4 bg-[#1a1a1a] hover:bg-black text-white font-bold rounded-xl flex items-center justify-center gap-2 transition-all shadow-lg"
              >
                📥 Télécharger TOUT le plan (PDF)
              </button>
              <button
                onClick={() => window.print()}
                className="w-full py-4 bg-white text-[#1a1a1a] border border-[#e0dbd4] font-bold rounded-xl flex items-center justify-center gap-2 hover:bg-[#f0ede8] transition-all"
              >
                🖨️ Imprimer cette page
              </button>
            </div>
            <div className="bg-amber-50 rounded-xl p-3 border border-amber-200 -mt-1">
              <p className="text-amber-800 text-xs font-bold leading-snug">
                💡 <strong>« Télécharger TOUT le plan »</strong> exporte les 45 chapitres en PDF — ta base de travail complète. Imprime-la, annote-la, garde-la sur ton bureau.
              </p>
            </div>

            {/* Premium CTA — Pitch coaching haut-ticket */}
            <div className="relative overflow-hidden rounded-3xl border-2 border-orange-500 bg-gradient-to-br from-[#1a1a1a] via-[#241510] to-[#1a1a1a] p-7 md:p-8 my-2">
              {/* Glow effects */}
              <div className="absolute top-0 right-0 w-40 h-40 bg-orange-500/20 rounded-full blur-3xl" />
              <div className="absolute bottom-0 left-0 w-32 h-32 bg-orange-500/15 rounded-full blur-3xl" />

              <div className="relative">
                <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-orange-500/20 rounded-full mb-4 border border-orange-500/30">
                  <Sparkles className="w-3.5 h-3.5 text-orange-400" />
                  <span className="text-orange-400 text-xs font-black uppercase tracking-widest">Niveau supérieur</span>
                </div>

                <h3 className="font-['Playfair_Display',serif] text-3xl md:text-4xl font-black text-white leading-[1.1] mb-4">
                  Même les <span className="text-orange-500">plus grands</span><br/>
                  se font coacher.
                </h3>

                {/* HIGHLIGHT — 2 jours sur place */}
                <div
                  className="mb-5 rounded-2xl px-4 py-3 flex items-center gap-3 border"
                  style={{
                    background: 'linear-gradient(135deg, rgba(240,90,26,0.22) 0%, rgba(251,191,36,0.14) 100%)',
                    borderColor: 'rgba(240,90,26,0.5)',
                  }}
                  data-testid="ebook-onsite-badge"
                >
                  <div
                    className="flex-shrink-0 w-11 h-11 rounded-xl flex items-center justify-center font-black text-white text-base"
                    style={{ background: 'linear-gradient(135deg, #F05A1A 0%, #FBBF24 100%)' }}
                  >
                    2J
                  </div>
                  <div className="text-left">
                    <p className="text-[10px] font-black uppercase tracking-widest text-orange-300">Inclus dans l'accompagnement</p>
                    <p className="text-white text-sm md:text-base font-bold leading-tight">
                      Je passe <span className="text-orange-400">2 jours sur place</span> dans ton resto
                    </p>
                    <p className="text-white/70 text-[11px] leading-tight mt-0.5">Audit terrain · équipe · service · cuisine — en direct avec toi</p>
                  </div>
                </div>

                <p className="text-white/80 text-base leading-relaxed mb-5">
                  Mbappé a un préparateur. Federer avait son entraîneur. Les chefs étoilés ont leur mentor. Pourquoi tu serais l'exception ?
                </p>

                <p className="text-white/70 text-sm leading-relaxed mb-6">
                  Tu viens de recevoir <strong className="text-white">le système exact</strong> qui fait tourner Maison Avocado. Maintenant, tu as 2 choix :
                </p>

                {/* Option A vs B */}
                <div className="space-y-3 mb-7">
                  <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-4 border border-white/10">
                    <p className="text-white/40 text-xs font-bold uppercase tracking-widest mb-2">Option A · Seul</p>
                    <p className="text-white/85 text-sm leading-snug">
                      Tu testes. Tu te plantes. Tu rattrapes. Tu recommences.<br/>
                      <span className="text-white/50 text-xs">⏱ 6-12 mois pour voir des résultats — si tu tiens.</span>
                    </p>
                  </div>
                  <div className="bg-orange-500/10 backdrop-blur-sm rounded-2xl p-4 border-2 border-orange-500/40 relative">
                    <div className="absolute -top-2 right-4 px-2.5 py-0.5 bg-orange-500 rounded-full">
                      <span className="text-white text-[10px] font-black uppercase tracking-widest">Recommandé</span>
                    </div>
                    <p className="text-orange-400 text-xs font-bold uppercase tracking-widest mb-2">Option B · Avec moi</p>
                    <p className="text-white text-sm leading-snug font-bold">
                      Tu exécutes <span className="text-orange-400">avec les meilleurs de ton domaine.</span>
                    </p>
                    <p className="text-white/75 text-sm leading-snug mt-1.5">
                      Tu accélères. Tu évites les erreurs qui coûtent 50k€. Tu fais x2 sur ton CA en 90 jours.<br/>
                      <span className="text-orange-300 text-xs">⚡ Résultats visibles dès le mois 1.</span>
                    </p>
                  </div>
                </div>

                {/* Social proof bullets */}
                <div className="bg-white/5 rounded-2xl p-4 border border-white/10 mb-6">
                  <p className="text-white/50 text-xs font-bold uppercase tracking-wider mb-3">Ce que tu as avec PRSNL :</p>
                  <ul className="space-y-2 text-sm">
                    <li className="flex items-start gap-2 text-white/95 font-bold">
                      <Check className="w-4 h-4 text-orange-400 flex-shrink-0 mt-0.5" />
                      <span>🔥 <span className="text-orange-400">2 jours sur place</span> dans ton resto avec moi (terrain · équipe · service · cuisine)</span>
                    </li>
                    <li className="flex items-start gap-2 text-white/85">
                      <Check className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />
                      <span>Audit complet de TON resto (pas un PDF générique)</span>
                    </li>
                    <li className="flex items-start gap-2 text-white/85">
                      <Check className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />
                      <span>Mes scripts, fiches, automatisations, contacts fournisseurs</span>
                    </li>
                    <li className="flex items-start gap-2 text-white/85">
                      <Check className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />
                      <span>Appels stratégiques 1-à-1 avec moi (Sabril)</span>
                    </li>
                    <li className="flex items-start gap-2 text-white/85">
                      <Check className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />
                      <span>Support WhatsApp prioritaire — réponse en moins de 24h</span>
                    </li>
                    <li className="flex items-start gap-2 text-white/85">
                      <Check className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />
                      <span>Garantie résultat ou tu es remboursé</span>
                    </li>
                  </ul>
                </div>

                {/* Punchline */}
                <div className="bg-gradient-to-r from-orange-500/15 to-red-500/10 rounded-xl p-4 border border-orange-500/30 mb-6">
                  <p className="text-white text-sm leading-relaxed italic">
                    « Les restaurateurs qui se font accompagner cette année <strong className="text-orange-400 not-italic">multiplient leur CA par 2 à 3.</strong> Ceux qui restent seuls ferment ou stagnent. C'est aussi simple que ça. »
                  </p>
                  <p className="text-white/50 text-xs font-bold mt-2">— Sabril, Fondateur Maison Avocado · Bring Café</p>
                </div>

                {/* Main CTA */}
                <a
                  href={`https://wa.me/33745522450?text=${encodeURIComponent(`Salut Sabril ! J'ai généré mon plan 30 jours${userInputs.restaurantName ? ' pour ' + userInputs.restaurantName : ''}. Je veux passer au niveau supérieur — j'aimerais être accompagné par PRSNL pour exécuter et accélérer.`)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="ebook-final-coaching-cta"
                  className="group relative block w-full py-5 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-black rounded-2xl text-center transition-all shadow-[0_8px_24px_rgba(240,90,26,0.45)] hover:shadow-[0_12px_36px_rgba(240,90,26,0.65)] hover:scale-[1.02]"
                >
                  <span className="relative flex items-center justify-center gap-2 text-lg">
                    Je veux être accompagné par les meilleurs
                    <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </span>
                  <span className="block text-white/85 text-xs font-bold mt-1 uppercase tracking-widest">Appel stratégique offert · 30 min</span>
                </a>

                <p className="text-white/40 text-xs text-center mt-4">
                  ⚡ Réservé aux restaurateurs sérieux · Places limitées chaque semaine
                </p>
              </div>
            </div>

            {/* Share the offer */}
            <div className="bg-white rounded-2xl p-5 border border-[#e0dbd4]">
              <div className="flex items-start gap-3 mb-3">
                <BookOpen className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-[#1a1a1a] font-bold text-sm">Tu connais un restaurateur qui galère ?</p>
                  <p className="text-[#1a1a1a]/60 text-xs mt-1">
                    Partage-lui le lien direct. Lui aussi aura son plan personnalisé.
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2 bg-[#f0ede8] rounded-lg px-3 py-2 mb-3 border border-[#e0dbd4]">
                <code className="text-orange-700 text-xs flex-1 truncate" data-testid="offre-shareable-link">
                  {typeof window !== 'undefined' ? `${window.location.origin}/ebook` : '/ebook'}
                </code>
                <button
                  onClick={() => navigator.clipboard.writeText(`${window.location.origin}/ebook`)}
                  className="text-xs text-orange-700 hover:text-orange-800 font-bold uppercase tracking-wider flex-shrink-0"
                  data-testid="offre-copy-link-btn"
                >
                  Copier
                </button>
              </div>

              <a
                href={`https://wa.me/?text=${encodeURIComponent(`Salut, je pense que ça peut t'aider pour ton resto : ${typeof window !== 'undefined' ? window.location.origin : ''}/ebook`)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="block w-full py-3 bg-[#f0ede8] text-[#1a1a1a] text-sm font-bold rounded-xl text-center hover:bg-[#e0dbd4] transition-all"
                data-testid="offre-share-whatsapp-btn"
              >
                💬 Partager sur WhatsApp
              </a>
            </div>

            {/* Progress */}
            <div className="bg-white rounded-xl p-4 border border-[#e0dbd4]">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[#1a1a1a]/70 text-sm">Ta progression</span>
                <span className="text-orange-500 font-bold">{progress}%</span>
              </div>
              <div className="h-2 bg-[#e0dbd4] rounded-full overflow-hidden">
                <div className="h-full bg-gradient-to-r from-orange-500 to-orange-600 rounded-full transition-all" style={{ width: `${progress}%` }} />
              </div>
              <p className="text-[#1a1a1a]/50 text-xs mt-2">{completedCount}/{totalActions} actions complétées</p>
            </div>
          </div>
        );
      })()
    }
  ];
  
  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#f5f3f0] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-orange-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Authorized + downloadUrl set → show a dedicated "Ton ebook est prêt" screen
  // with a big download button. Skips the legacy React reader.
  if (isAuthorized && downloadUrl) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6"
           style={{ background: 'radial-gradient(120% 80% at 50% 0%, #0F2A47 0%, #061224 60%, #030812 100%)' }}>
        <div className="w-full max-w-md" data-testid="ebook-ready-screen">
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full mb-5"
                 style={{ background: 'rgba(52,211,153,0.14)', border: '0.5px solid rgba(52,211,153,0.35)' }}>
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
              <span className="text-emerald-300 text-[10px] font-black uppercase tracking-[0.18em]">Accès confirmé</span>
            </div>
            <h1 className="font-['Playfair_Display',serif] text-white text-[34px] font-black leading-[1.02] tracking-[-0.025em] mb-3">
              Ton ebook <span className="italic" style={{ color: '#FBBF24' }}>est prêt</span>.
            </h1>
            <p className="text-white/60 text-[14px] leading-snug">
              70 pages · 12 schémas visuels · 6 méthodes bonus · La méthode complète du restaurant 2026.
            </p>
          </div>

          {/* PDF Preview card */}
          <div className="relative rounded-3xl overflow-hidden mb-5"
               style={{
                 background: 'linear-gradient(160deg, rgba(255,255,255,0.06) 0%, rgba(255,255,255,0.02) 100%)',
                 border: '0.5px solid rgba(255,255,255,0.14)',
                 padding: '20px 22px',
                 backdropFilter: 'blur(20px)',
               }}>
            <div className="flex items-center gap-4">
              <div className="flex-shrink-0 flex items-center justify-center"
                   style={{
                     width: 68, height: 68, borderRadius: 18,
                     background: 'linear-gradient(135deg, #C9A87A 0%, #8B6520 100%)',
                     boxShadow: '0 12px 28px -8px rgba(201,168,122,0.55), inset 0 1px 0 rgba(255,255,255,0.20)',
                   }}>
                <span style={{ fontSize: 30 }}>📕</span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[10px] font-black uppercase tracking-[0.18em] text-[#C9A87A]">
                  SABRIL · Restaurant 2026
                </p>
                <p className="text-white font-black text-[17px] leading-tight tracking-[-0.01em] mt-1">
                  Sabril Ebook 30j.pdf
                </p>
                <p className="text-white/50 text-[11px] mt-1">
                  8.8 MB · 70 pages · PDF
                </p>
              </div>
            </div>
          </div>

          {/* Big download button */}
          <a
            href={downloadUrl}
            download="sabril_ebook_30jours.pdf"
            className="block w-full text-center py-5 rounded-2xl font-black transition active:scale-[0.98]"
            style={{
              background: 'linear-gradient(135deg, #C9A87A 0%, #8B6520 100%)',
              color: '#0F2A47',
              fontFamily: '"Playfair Display", serif',
              fontSize: 18,
              letterSpacing: '-0.01em',
              boxShadow: '0 14px 32px -8px rgba(201,168,122,0.60), inset 0 1px 0 rgba(255,255,255,0.30)',
              textDecoration: 'none',
            }}
            data-testid="ebook-download-pdf-btn"
          >
            ⬇ Télécharger le PDF
          </a>

          {/* Open in new tab (secondary) */}
          <a
            href={downloadUrl}
            target="_blank"
            rel="noreferrer"
            className="block w-full text-center py-3.5 rounded-2xl mt-3 transition active:scale-[0.98]"
            style={{
              background: 'rgba(255,255,255,0.06)',
              color: '#FFFFFF',
              border: '0.5px solid rgba(255,255,255,0.16)',
              fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif',
              fontSize: 14, fontWeight: 700,
              letterSpacing: '-0.005em',
              textDecoration: 'none',
            }}
            data-testid="ebook-open-pdf-btn"
          >
            📖 Ouvrir dans un nouvel onglet
          </a>

          <p className="text-white/40 text-[11px] text-center mt-5 leading-snug">
            🔒 Ce lien t'appartient · accès illimité · re-téléchargeable à volonté
          </p>
        </div>
      </div>
    );
  }

  if (!isAuthorized) {
    if (showCodeForm) {
      return (
        <div className="min-h-screen bg-[#f5f3f0] flex items-center justify-center p-6">
          <div className="w-full max-w-md" data-testid="ebook-code-form">
            <div className="text-center mb-8">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-orange-500/10 rounded-full mb-4">
                <Lock className="w-4 h-4 text-orange-500" />
                <span className="text-orange-600 text-xs font-bold uppercase tracking-widest">Accès protégé</span>
              </div>
              <h1 className="font-['Playfair_Display',serif] text-3xl md:text-4xl font-black text-[#1a1a1a] leading-[1.05] mb-3">
                Entre <span className="text-orange-500">ton code</span><br/>d'accès.
              </h1>
              <p className="text-[#1a1a1a]/60 text-sm">
                Tu l'as reçu par email après ton paiement.
              </p>
            </div>

            <form onSubmit={handleSubmitCode} className="bg-white rounded-2xl p-6 border border-[#e0dbd4] shadow-sm">
              <label className="block text-[#1a1a1a] text-sm font-bold mb-2">Code d'accès</label>
              <input
                type="text"
                value={codeInput}
                onChange={(e) => { setCodeInput(e.target.value); setCodeError(''); }}
                placeholder="Ex : XXXXXXXXXXXX"
                autoComplete="off"
                autoCapitalize="characters"
                data-testid="ebook-code-input"
                className="w-full px-4 py-3 bg-[#f5f3f0] border-2 border-[#e0dbd4] rounded-xl text-[#1a1a1a] font-mono text-base font-bold tracking-wider uppercase outline-none focus:border-orange-500 transition"
              />
              {codeError && (
                <p className="text-red-600 text-xs font-bold mt-2" data-testid="ebook-code-error">{codeError}</p>
              )}
              <button
                type="submit"
                disabled={verifying}
                data-testid="ebook-code-submit"
                className="w-full mt-4 py-3.5 bg-orange-500 hover:bg-orange-600 disabled:bg-orange-300 text-white font-bold rounded-xl transition flex items-center justify-center gap-2"
              >
                {verifying ? (
                  <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <>Accéder à mon plan <ArrowRight className="w-4 h-4" /></>
                )}
              </button>
            </form>

            {/* OR — direct Stripe purchase */}
            <div className="mt-6 flex items-center gap-3">
              <span className="flex-1 h-px bg-[#e0dbd4]" />
              <span className="text-[#1a1a1a]/40 text-xs font-bold tracking-widest uppercase">OU</span>
              <span className="flex-1 h-px bg-[#e0dbd4]" />
            </div>

            <button
              type="button"
              onClick={async () => {
                if (checkoutLoading) return;
                setCheckoutLoading(true);
                try {
                  const res = await fetch(`${API_URL}/api/ebook/checkout`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ origin_url: window.location.origin, source: 'ebook-portal' }),
                  });
                  const data = await res.json();
                  if (data && data.url) {
                    window.location.href = data.url;
                  } else {
                    setCheckoutLoading(false);
                  }
                } catch (e) {
                  setCheckoutLoading(false);
                }
              }}
              disabled={checkoutLoading}
              data-testid="ebook-buy-btn"
              className="mt-4 w-full py-3.5 rounded-xl font-bold text-white flex items-center justify-center gap-2 transition disabled:opacity-70"
              style={{
                background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
                boxShadow: '0 10px 24px -8px rgba(16,185,129,0.55)',
              }}
            >
              {checkoutLoading ? (
                <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <>📖 Acheter l'ebook · 147€ TTC <ArrowRight className="w-4 h-4" /></>
              )}
            </button>

            <div className="mt-4 text-center">
              <p className="text-[#1a1a1a]/50 text-xs">
                Paiement sécurisé Stripe · PDF envoyé par email · Accès illimité
              </p>
            </div>
          </div>
        </div>
      );
    }
    return (
      <div className="min-h-screen bg-[#f5f3f0] flex items-center justify-center p-6">
        <div className="text-center">
          <Lock className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-[#1a1a1a] mb-2">Accès refusé</h1>
          <p className="text-[#1a1a1a]/60 mb-4">Code d'accès invalide</p>
          <Link to="/" className="text-orange-500 hover:underline">Retour à l'accueil</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f5f3f0]">
      {/* Print-only styles for full export */}
      <style>{`
        @media print {
          @page { size: A4; margin: 12mm; }
          body { background: white !important; }
          .no-print { display: none !important; }
          .print-page { page-break-after: always; padding: 0 !important; min-height: auto !important; }
          .print-page:last-child { page-break-after: auto; }
          .print-cover {
            page-break-after: always;
            text-align: left;
            padding: 0 !important;
          }
          .print-screen-num {
            display: block !important;
            font-size: 10px;
            color: #999;
            margin-bottom: 8px;
            font-weight: bold;
            letter-spacing: 0.1em;
            text-transform: uppercase;
          }
          input, textarea {
            border: 1px solid #ccc !important;
            background: white !important;
          }
          .fixed { position: static !important; }
        }
        .print-screen-num { display: none; }
      `}</style>

      {/* EXPORT MODE — render all screens stacked for printing */}
      {exportMode ? (
        <div className="max-w-2xl mx-auto px-6 py-8 print:py-0">
          {/* Cover page */}
          <div className="print-cover bg-gradient-to-br from-orange-500 to-amber-500 rounded-3xl p-8 text-white print:rounded-none print:bg-white print:text-[#1a1a1a] print:border-b-4 print:border-orange-500 mb-8">
            <p className="text-white/90 text-xs font-bold uppercase tracking-widest mb-3 print:text-orange-600">Plan d'exécution personnalisé · 30 jours</p>
            <h1 className="font-['Playfair_Display',serif] text-4xl md:text-5xl font-black leading-[1.05] mb-4">
              {userInputs.restaurantName || 'Ton resto'}.<br/>
              <span className="print:text-orange-500">Ton plan complet.</span>
            </h1>
            <p className="text-white/85 text-base print:text-[#1a1a1a]/70 mt-3 leading-relaxed">
              Tout le contenu du plan, prêt à imprimer.<br/>
              Annote, surligne, garde-le sur ton bureau.
            </p>
            <div className="mt-6 pt-6 border-t border-white/30 print:border-[#e0dbd4]">
              <p className="text-white/80 text-xs print:text-[#1a1a1a]/60">
                {new Date().toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' })}<br/>
                PRSNL · Maison Avocado · Bring Café
              </p>
            </div>
          </div>

          {/* All screens */}
          {screens.map((screen, idx) => (
            <div key={screen.id} className="print-page mb-12 print:mb-0">
              <div className="print-screen-num">Page {idx + 1} / {screens.length} · {screen.title}</div>
              <div>{screen.content}</div>
            </div>
          ))}

          {/* Exit export mode CTA (screen-only) */}
          <div className="no-print text-center mt-8 sticky bottom-4 z-50">
            <div className="inline-flex flex-col gap-2 bg-white border border-[#e0dbd4] shadow-lg rounded-2xl p-4">
              <p className="text-[#1a1a1a] text-sm font-bold">📄 Mode export — tous les écrans affichés</p>
              <p className="text-[#1a1a1a]/60 text-xs">La fenêtre d'impression devrait apparaître. Choisis « Enregistrer en PDF ».</p>
              <div className="flex gap-2 mt-1">
                <button
                  onClick={() => window.print()}
                  className="flex-1 py-2.5 bg-orange-500 hover:bg-orange-600 text-white text-sm font-bold rounded-xl transition"
                >
                  🖨️ Relancer l'impression
                </button>
                <button
                  onClick={() => setExportMode(false)}
                  className="flex-1 py-2.5 bg-[#e0dbd4] hover:bg-[#d4cfc7] text-[#1a1a1a] text-sm font-bold rounded-xl transition"
                >
                  Retour au plan
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <>
          {/* SOMMAIRE / SIDEBAR — accessible via le bouton menu */}
          {(() => {
            const sectionsConfig = [
              { name: '👋 Démarrage', icon: '👋', ids: ['accroche', 'profil-name', 'profil-type', 'profil-numbers', 'profil-objectif', 'profil-construction'] },
              { name: '🚨 Survie & Diagnostic', icon: '🚨', ids: ['risk-diag', 'stop-hemorragie', 'templates', 'glossaire'] },
              { name: '🏛 Système & Méthode', icon: '🏛', ids: ['systeme', 'structurer', 'attirer', 'convertir', 'fideliser'] },
              { name: '💰 Pilotage financier', icon: '💰', ids: ['kpis-hebdo', 'breakeven', 'primecost', 'tresorerie', 'calculateur', 'financial-optimizer'] },
              { name: '🛒 Marges & achats', icon: '🛒', ids: ['foodcost-plat', 'fournisseurs', 'stocks'] },
              { name: '🍽 Carte & revenus', icon: '🍽', ids: ['menu-engineering', 'menu-trends', 'ticket-moyen'] },
              { name: '👨‍🍳 Équipe', icon: '👨‍🍳', ids: ['recrutement', 'fiches', 'briefing', 'onboarding', 'primes', 'temps-patron'] },
              { name: '📱 Acquisition & réseaux', icon: '📱', ids: ['reseaux', 'format-video', 'social-generator', 'social-calendar', 'social-hooks', 'social-collab', 'ia-automatisations', 'avis-google', 'fidelisation-simple'] },
              { name: '🚀 Canaux digitaux', icon: '🚀', ids: ['qr-tables', 'click-collect', 'tablettes', 'uber-eats', 'uber-eats-strategy'] },
              { name: '🎯 Conclusion', icon: '🎯', ids: ['final'] },
            ];
            // Build sections with their actual screen indexes
            const indexById = {};
            screens.forEach((s, i) => { indexById[s.id] = i; });
            const sections = sectionsConfig.map(section => ({
              ...section,
              items: section.ids
                .map(id => indexById[id] !== undefined ? { id, index: indexById[id], title: screens[indexById[id]].title || id } : null)
                .filter(Boolean)
            })).filter(s => s.items.length > 0);

            return (
              <>
                {/* Menu button — fixed top-right */}
                <button
                  onClick={() => setSidebarOpen(true)}
                  data-testid="ebook-menu-btn"
                  className="fixed top-3 left-3 z-[60] flex items-center gap-1.5 px-3 h-10 rounded-xl bg-[#1a1a1a] hover:bg-black text-white shadow-lg transition no-print"
                  aria-label="Sommaire"
                >
                  <Menu className="w-4 h-4" />
                  <span className="text-xs font-bold">Sommaire</span>
                </button>

                {/* Overlay */}
                <AnimatePresence>
                  {sidebarOpen && (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      onClick={() => setSidebarOpen(false)}
                      className="fixed inset-0 z-[70] bg-black/40 no-print"
                    />
                  )}
                </AnimatePresence>

                {/* Sidebar drawer */}
                <AnimatePresence>
                  {sidebarOpen && (
                    <motion.aside
                      initial={{ x: '-100%' }}
                      animate={{ x: 0 }}
                      exit={{ x: '-100%' }}
                      transition={{ type: 'tween', duration: 0.25 }}
                      className="fixed top-0 left-0 bottom-0 z-[80] w-[88%] max-w-sm bg-white shadow-2xl overflow-y-auto no-print"
                      data-testid="ebook-sidebar"
                    >
                      <div className="sticky top-0 bg-gradient-to-r from-orange-500 to-amber-500 text-white px-5 py-4 flex items-center justify-between">
                        <div>
                          <p className="text-[10px] font-bold uppercase tracking-widest opacity-90">Sommaire</p>
                          <p className="text-base font-black leading-tight">Ton plan d'exécution</p>
                        </div>
                        <button
                          onClick={() => setSidebarOpen(false)}
                          className="w-9 h-9 rounded-lg bg-white/20 hover:bg-white/30 flex items-center justify-center"
                          aria-label="Fermer"
                        >
                          <X className="w-5 h-5" />
                        </button>
                      </div>

                      {/* Progress summary */}
                      <div className="px-5 py-3 border-b border-[#f0ede8]">
                        <div className="flex items-center justify-between mb-1.5">
                          <span className="text-[#1a1a1a]/60 text-xs font-bold">PROGRESSION</span>
                          <span className="text-orange-500 text-xs font-black">{Math.round((Object.keys(visitedScreens).length / screens.length) * 100)}%</span>
                        </div>
                        <div className="h-1.5 bg-[#f0ede8] rounded-full overflow-hidden">
                          <div className="h-full bg-orange-500 rounded-full transition-all" style={{ width: `${(Object.keys(visitedScreens).length / screens.length) * 100}%` }} />
                        </div>
                        <p className="text-[#1a1a1a]/60 text-[11px] mt-1.5">
                          {Object.keys(visitedScreens).length} / {screens.length} chapitres consultés
                        </p>
                      </div>

                      {/* Sections list */}
                      <nav className="px-3 py-3 space-y-1">
                        {sections.map(section => {
                          const sectionDone = section.items.every(it => visitedScreens[it.id]);
                          const sectionStarted = section.items.some(it => visitedScreens[it.id]);
                          return (
                            <div key={section.name} className="mb-3">
                              <div className="px-2 py-1.5 flex items-center gap-2">
                                <p className={`text-[11px] font-black uppercase tracking-widest flex-1 ${
                                  sectionDone ? 'text-green-600' : sectionStarted ? 'text-orange-600' : 'text-[#1a1a1a]/40'
                                }`}>
                                  {section.name}
                                </p>
                                {sectionDone && <Check className="w-3.5 h-3.5 text-green-600" strokeWidth={3} />}
                              </div>
                              <div className="space-y-0.5">
                                {section.items.map(it => {
                                  const isCurrent = it.index === currentScreen;
                                  const isVisited = visitedScreens[it.id];
                                  return (
                                    <button
                                      key={it.id}
                                      onClick={() => goToScreen(it.index)}
                                      data-testid={`sidebar-link-${it.id}`}
                                      className={`w-full text-left px-3 py-2.5 rounded-lg flex items-center gap-2.5 transition ${
                                        isCurrent
                                          ? 'bg-orange-500 text-white shadow-sm'
                                          : isVisited
                                            ? 'bg-green-50 text-[#1a1a1a] hover:bg-green-100'
                                            : 'text-[#1a1a1a]/75 hover:bg-[#f5f3f0]'
                                      }`}
                                    >
                                      <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-black flex-shrink-0 ${
                                        isCurrent ? 'bg-white/25 text-white' : isVisited ? 'bg-green-500 text-white' : 'bg-[#e0dbd4] text-[#1a1a1a]/60'
                                      }`}>
                                        {isVisited && !isCurrent ? <Check className="w-3 h-3" strokeWidth={3} /> : it.index + 1}
                                      </span>
                                      <span className="text-sm font-bold flex-1 min-w-0 leading-tight">{it.title}</span>
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                          );
                        })}
                      </nav>
                    </motion.aside>
                  )}
                </AnimatePresence>
              </>
            );
          })()}

          {/* Progress bar */}
          <div className="fixed top-0 left-0 right-0 z-50 bg-[#f5f3f0]/90 backdrop-blur-sm border-b border-[#e0dbd4] no-print">
            <div className="h-1 bg-[#e0dbd4]">
              <motion.div
                className="h-full bg-gradient-to-r from-orange-500 to-orange-600"
                initial={{ width: 0 }}
                animate={{ width: `${((currentScreen + 1) / screens.length) * 100}%` }}
              />
            </div>
            <div className="flex items-center justify-between px-4 py-3 pl-32">
              <span className="text-[#1a1a1a]/50 text-sm">
                {currentScreen + 1} / {screens.length}
              </span>
              <span className="text-orange-500 font-bold text-sm hidden sm:inline">
                🎯 Plan d'exécution personnalisé
              </span>
              <span className="text-[#1a1a1a]/50 text-sm">
                {progress}% complété
              </span>
            </div>
          </div>

          {/* Content */}
          <div className="pt-20 pb-40 px-6 max-w-2xl mx-auto">
            <AnimatePresence mode="wait">
              <motion.div
                key={currentScreen}
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -50 }}
                transition={{ duration: 0.3 }}
              >
                {screens[currentScreen].content}
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Navigation — mobile-safe with iOS bottom inset */}
          <div
            className="fixed bottom-0 left-0 right-0 bg-[#f5f3f0]/95 backdrop-blur-sm border-t border-[#e0dbd4] no-print"
            style={{ paddingBottom: 'env(safe-area-inset-bottom, 0)' }}
          >
            <div className="max-w-2xl mx-auto px-3 sm:px-4 py-3 flex items-center gap-2 sm:gap-4">
              <button
                onClick={() => setCurrentScreen(Math.max(0, currentScreen - 1))}
                disabled={currentScreen === 0}
                aria-label="Précédent"
                className="flex items-center gap-1.5 px-3 sm:px-4 py-3 rounded-xl bg-[#e0dbd4] text-[#1a1a1a] font-bold text-sm disabled:opacity-30 disabled:cursor-not-allowed hover:bg-[#d4cfc7] transition-colors flex-shrink-0"
              >
                <ChevronLeft className="w-4 h-4 sm:w-5 sm:h-5" />
                <span className="hidden sm:inline">Précédent</span>
              </button>

              {/* Compact progress on mobile, dots on desktop */}
              <div className="flex-1 flex items-center justify-center min-w-0">
                <div className="hidden md:flex gap-1 max-w-full overflow-x-auto">
                  {screens.map((_, i) => (
                    <button
                      key={i}
                      onClick={() => setCurrentScreen(i)}
                      className={`h-2 rounded-full transition-all flex-shrink-0 ${
                        i === currentScreen ? 'bg-orange-500 w-4' : 'bg-[#c9c4bc] hover:bg-[#1a1a1a]/50 w-2'
                      }`}
                      aria-label={`Aller à l'écran ${i + 1}`}
                    />
                  ))}
                </div>
                <div className="md:hidden h-1.5 w-full max-w-[100px] bg-[#e0dbd4] rounded-full overflow-hidden">
                  <div
                    className="h-full bg-orange-500 rounded-full transition-all"
                    style={{ width: `${((currentScreen + 1) / screens.length) * 100}%` }}
                  />
                </div>
              </div>

              <button
                onClick={() => setCurrentScreen(Math.min(screens.length - 1, currentScreen + 1))}
                disabled={currentScreen === screens.length - 1}
                aria-label="Suivant"
                className="flex items-center gap-1.5 px-3 sm:px-4 py-3 rounded-xl bg-orange-500 text-white font-bold text-sm disabled:opacity-30 disabled:cursor-not-allowed hover:bg-orange-600 transition-colors flex-shrink-0"
              >
                <span className="hidden sm:inline">Suivant</span>
                <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5" />
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default EbookPage;
